import "./app.css";
import Lenis from "lenis";
import { TASTE_AXES, normalizeTasteVector, rankTasteMatches, scoreTasteMatch } from "./taste-profile.js";

// ---- Data loaded from Python-generated bootstrap JSON ----
let BOOT = {};
let top5_deals = [];
let products = [];
let productNameSet = new Set();
let currentProduct = "";
let dataVersion = "";
let dataBaseUrl = "";
let heroTrend = null;
let landingIntelligenceCards = [];
let alltimePriceDropsByProduct = new Map();
let recentPriceDropsByProduct = new Map();
const productPayloadCache = new Map();

// NEW: catalog (fast search/browse index)
let CATALOG = null;
let CATALOG_ITEMS = [];
let CATALOG_BY_NAME = new Map();
let tasteReferenceName = "";
let tasteReferenceError = "";
let tasteReferencePreviousSort = null;
const tasteMatchCache = new Map();
const TASTE_REFERENCE_PARAM = "taste";
let discoveryMode = "alltime";
let discoveryDropMetric = "pct";
const discoveryTasteAxes = new Set();
const discoveryTasteLevels = new Map();
let discoveryTasteGuideAxis = "fruity";
let activeFlavourSlider = null;
// The query becomes a catalogue filter only when it is submitted. This lets the
// input remain a lightweight type-ahead while preserving a deliberate search.
let catalogueSearchFilter = "";

const DISCOVERY_MODE_COPY = Object.freeze({
  alltime: {
    title: "Which all-time price drops matter most?",
    resultsTitle: "Largest all-time price drops",
    action: "View Bottles",
  },
  recent: {
    title: "What has dropped in the past 7 days?",
    resultsTitle: "Largest price drops in the past 7 days",
    action: "View Bottles",
  },
  similar: {
    title: "What whisky do you already enjoy?",
    resultsTitle: "Choose a bottle to compare",
    action: "View Bottles",
  },
  flavour: {
    title: "What kind of dram are you in the mood for?",
    resultsTitle: "Build a flavour profile",
    action: "View Bottles",
  },
});

const DISCOVERY_DROP_RESULT_COPY = Object.freeze({
  alltime: {
    pct: {
      resultsTitle: "Largest all-time percentage drops",
    },
    value: {
      resultsTitle: "Largest all-time Rand drops",
    },
  },
  recent: {
    pct: {
      resultsTitle: "Largest percentage drops in the past 7 days",
    },
    value: {
      resultsTitle: "Largest Rand drops in the past 7 days",
    },
  },
});

function normaliseDiscoveryMode(value) {
  if (value === "percent" || value === "value" || value === "drops") return "alltime";
  return Object.hasOwn(DISCOVERY_MODE_COPY, value) ? value : "alltime";
}

function getDiscoveryDropSort(mode = discoveryMode, metric = discoveryDropMetric) {
  if (mode === "recent") return metric === "value" ? "recent_value" : "recent_pct";
  return metric === "value" ? "value" : "pct";
}

function getDiscoveryDropStateForSort(sortBy) {
  if (sortBy === "value") return { mode: "alltime", metric: "value" };
  if (sortBy === "pct") return { mode: "alltime", metric: "pct" };
  if (sortBy === "recent_value" || sortBy === "recent") return { mode: "recent", metric: "value" };
  if (sortBy === "recent_pct") return { mode: "recent", metric: "pct" };
  return null;
}

function syncDiscoveryDropStateForSort(sortBy) {
  const state = getDiscoveryDropStateForSort(sortBy);
  if (!state) return false;
  discoveryMode = state.mode;
  discoveryDropMetric = state.metric;
  return true;
}

function getDiscoveryModeCopy() {
  const base = DISCOVERY_MODE_COPY[discoveryMode] || DISCOVERY_MODE_COPY.alltime;
  const dropCopy = DISCOVERY_DROP_RESULT_COPY[discoveryMode]?.[discoveryDropMetric];
  return dropCopy ? { ...base, ...dropCopy } : base;
}

const FLAVOUR_COMPASS_GUIDE = Object.freeze({
  woody: {
    title: "Woody",
    description: "Oak-led aromas shaped by cask maturation, from sweet vanilla to dry cedar and spice.",
    notes: ["Toasted", "Vanilla", "Old Wood", "New Wood"],
  },
  winey: {
    title: "Winey",
    description: "Richer cask-derived character, often reminiscent of fortified wine, nuts or dark sweetness.",
    notes: ["Sherried", "Nutty", "Chocolate", "Oily"],
  },
  cereal: {
    title: "Cereal",
    description: "Grain-forward character that can suggest malt, mash, bread or a gentle yeasty quality.",
    notes: ["Cooked mash", "Cooked veg", "Malt extract", "Yeasty"],
  },
  fruity: {
    title: "Fruity",
    description: "Sweet and fragrant fruit character, ranging from bright citrus to fresh or dried fruit.",
    notes: ["Citric", "Fresh fruit", "Cooked fruit", "Dried fruit"],
  },
  floral: {
    title: "Floral",
    description: "Delicate fresh, leafy and fragrant aromas that can recall flowers, grass, hay or herbs.",
    notes: ["Fragrant", "Green House", "Leafy", "Hay"],
  },
  peaty: {
    title: "Peaty",
    description: "Smoke-led character introduced when malt is dried with peat, from bonfire to medicinal or coastal notes.",
    notes: ["Medicinal", "Smokey", "Kippery", "Mossy"],
  },
  feinty: {
    title: "Feinty",
    description: "Weighty, rustic notes that can become softer with maturation, including honey, leather and tobacco.",
    notes: ["Honey", "Leathery", "Sweat & Plastic", "Tobacco"],
  },
  sulphury: {
    title: "Sulphury",
    description: "A polarising distillation character that can suggest struck matches, rubber or savoury vegetal notes.",
    notes: ["Coal Gas", "Rubbery", "Sandy", "Vegetative"],
  },
});

function getAppPage() {
  return document.body?.dataset?.page || "";
}

function isBrowsePage() {
  return getAppPage() === "browse";
}

function isProductPage() {
  return getAppPage() === "product";
}

function hasLandingHero() {
  return !!document.querySelector(".landing-hero");
}

function hasBrowseSurface() {
  return !!document.getElementById("whisky-shelf-list");
}

function hasDetailSurface() {
  return !!document.getElementById("focus-stage");
}

function parseBootstrapPayload(raw) {
  try {
    const parsed = (typeof raw === "string") ? JSON.parse(raw) : (raw || {});
    return (parsed && typeof parsed === "object") ? parsed : {};
  } catch (e) {
    console.error("Invalid bootstrap payload:", e, raw);
    return {};
  }
}

function applyBootstrap(boot) {
  BOOT = (boot && typeof boot === "object") ? boot : {};
  top5_deals = BOOT.top5Deals || [];
  products = Array.isArray(BOOT.products) ? BOOT.products : [];
  productNameSet = new Set(products);
  currentProduct = BOOT.initialProduct || (products[0] || "");
  dataVersion = String(BOOT.dataVersion || "").trim();
  heroTrend = (BOOT.heroTrend && typeof BOOT.heroTrend === "object") ? BOOT.heroTrend : null;
  landingIntelligenceCards = Array.isArray(BOOT.landingIntelligenceCards) ? BOOT.landingIntelligenceCards : [];
  alltimePriceDropsByProduct = new Map(
    (Array.isArray(BOOT.alltimePriceDrops) ? BOOT.alltimePriceDrops : [])
      .filter((row) => row && String(row.product || "").trim())
      .map((row) => [String(row.product).trim(), row])
  );
  recentPriceDropsByProduct = new Map(
    (Array.isArray(BOOT.recentPriceDrops) ? BOOT.recentPriceDrops : [])
      .filter((row) => row && String(row.product || "").trim())
      .map((row) => [String(row.product).trim(), row])
  );

  if (!products.length && !currentProduct) {
    console.warn("BOOTSTRAP loaded but contains no products. BOOT keys:", Object.keys(BOOT));
  }
}

function withDataVersion(url) {
  if (!dataVersion) return url;
  const joiner = url.includes("?") ? "&" : "?";
  return `${url}${joiner}v=${encodeURIComponent(dataVersion)}`;
}

function getDataBaseUrl() {
  if (dataBaseUrl) return dataBaseUrl;
  try {
    const raw = window.__BOOTSTRAP_URL__ || "./data/bootstrap.json";
    const bootstrapUrl = new URL(raw, window.location.href);
    bootstrapUrl.search = "";
    bootstrapUrl.hash = "";
    dataBaseUrl = new URL(".", bootstrapUrl).toString();
  } catch {
    dataBaseUrl = new URL("./data/", window.location.href).toString();
  }
  return dataBaseUrl;
}

function buildDataUrl(fileName, { versioned = true } = {}) {
  const clean = String(fileName || "").replace(/^\.?\/?data\//, "");
  const url = new URL(clean, getDataBaseUrl());
  if (versioned && dataVersion) {
    url.searchParams.set("v", dataVersion);
  }
  return url.toString();
}

async function loadBootstrap() {
  if (window.__BOOTSTRAP__) {
    applyBootstrap(parseBootstrapPayload(window.__BOOTSTRAP__));
    return;
  }

  const url = window.__BOOTSTRAP_URL__ || "./data/bootstrap.json";
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(String(res.status));
    applyBootstrap(await res.json());
  } catch (e) {
    console.error("Could not load bootstrap JSON.", e);
    applyBootstrap({});
  }
}

// ---- Lenis smooth scroll (Awwwards-style) ----
// Safe: respects prefers-reduced-motion and falls back to native smooth scrolling.
const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
const prefersNativeMobileScroll = window.matchMedia?.("(max-width: 900px)")?.matches;
let lenis = null;
const ENABLE_LENIS = false;
const APP_LOADER_DELAY_MS = 180;
const APP_LOADER_MIN_VISIBLE_MS = 320;
let appLoaderTimer = 0;
let appLoaderShownAt = 0;

function beginInitialLoad() {
  const loader = document.getElementById("app-loader");
  if (!loader) return;
  loader.hidden = true;
  window.clearTimeout(appLoaderTimer);
  appLoaderTimer = window.setTimeout(() => {
    loader.hidden = false;
    appLoaderShownAt = Date.now();
    requestAnimationFrame(() => loader.classList.add("is-visible"));
  }, APP_LOADER_DELAY_MS);
}

function finishInitialLoad() {
  const loader = document.getElementById("app-loader");
  window.clearTimeout(appLoaderTimer);
  if (!loader || loader.hidden) return;
  const remaining = Math.max(0, APP_LOADER_MIN_VISIBLE_MS - (Date.now() - appLoaderShownAt));
  window.setTimeout(() => {
    loader.classList.remove("is-visible");
    window.setTimeout(() => {
      loader.hidden = true;
      appLoaderShownAt = 0;
    }, 220);
  }, remaining);
}

function initLenis() {
  if (!ENABLE_LENIS) return null;
  if (prefersReducedMotion || prefersNativeMobileScroll) return null;
  if (lenis) return lenis;
  lenis = new Lenis({
    lerp: 0.08,           // "weight" of scroll (lower = snappier)
    smoothWheel: true,
    smoothTouch: false
  });
  const raf = (time) => {
    lenis.raf(time);
    requestAnimationFrame(raf);
  };
  requestAnimationFrame(raf);
  window.__lenis = lenis;
  return lenis;
}

// ---- Unified scroll subscription (Lenis-aware) ----
// Use this instead of manually mixing window.addEventListener + lenis.on(...)
function onScrollUnified(handler) {
  const l = initLenis();
  if (l) {
    l.on("scroll", handler);
    // still update on resize; Lenis won't emit scroll on resize by itself
    window.addEventListener("resize", handler, { passive: true });
    return () => {
      try { l.off("scroll", handler); } catch {}
      window.removeEventListener("resize", handler);
    };
  }
  window.addEventListener("scroll", handler, { passive: true });
  window.addEventListener("resize", handler, { passive: true });
  return () => {
    window.removeEventListener("scroll", handler);
    window.removeEventListener("resize", handler);
  };
}

function initLandingParallax() {
  if (!hasLandingHero() || prefersReducedMotion) return;

  const hero = document.getElementById("hero");
  const transition = document.querySelector(".landing-transition");
  if (!hero) return;

  const mobileQuery = window.matchMedia?.("(max-width: 900px)");
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  let ticking = false;

  const setHeroVar = (name, value) => {
    hero.style.setProperty(name, `${Math.round(value)}px`);
  };

  const reset = () => {
    setHeroVar("--landing-bg-y", 0);
    setHeroVar("--landing-copy-y", 0);
    setHeroVar("--landing-trend-y", 0);
    setHeroVar("--landing-metrics-y", 0);
    if (transition) {
      transition.style.setProperty("--landing-transition-y", "0px");
      transition.style.setProperty("--landing-transition-sweep", "0%");
    }
  };

  const update = () => {
    ticking = false;

    if (mobileQuery?.matches) {
      reset();
      return;
    }

    const rect = hero.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight || 1;
    const heroHeight = Math.max(rect.height, 1);
    const sceneProgress = clamp((vh - rect.top) / (heroHeight + vh), 0, 1);
    const exitProgress = clamp(-rect.top / heroHeight, 0, 1);

    setHeroVar("--landing-bg-y", sceneProgress * 72);
    setHeroVar("--landing-copy-y", exitProgress * -34);
    setHeroVar("--landing-trend-y", exitProgress * -56);
    setHeroVar("--landing-metrics-y", exitProgress * -22);
    if (transition) {
      transition.style.setProperty("--landing-transition-y", `${Math.round(exitProgress * -46)}px`);
      transition.style.setProperty("--landing-transition-sweep", `${Math.round(sceneProgress * 130 - 18)}%`);
    }
  };

  const requestUpdate = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  update();
  onScrollUnified(requestUpdate);
  mobileQuery?.addEventListener?.("change", requestUpdate);
}

function getLandingHeaderOffset(extra = 12) {
  const header = document.querySelector(".site-header");
  if (!header) return 0;
  return Math.ceil(header.getBoundingClientRect().height + extra);
}

function initDiscoverySidebarScrollMode() {
  if (window.__discoverySidebarScrollModeInit) return;
  const sidebar = document.querySelector(".discovery-sidebar");
  if (!sidebar) return;
  window.__discoverySidebarScrollModeInit = true;

  const update = () => {
    const desktop = window.matchMedia("(min-width: 981px)").matches;
    const availableHeight = Math.max(0, window.innerHeight - getLandingHeaderOffset(14) - 16);
    const needsNaturalScroll = desktop && sidebar.scrollHeight > availableHeight;
    sidebar.classList.toggle("is-scroll-natural", needsNaturalScroll);
  };

  const scheduleUpdate = () => requestAnimationFrame(update);
  if ("ResizeObserver" in window) {
    const observer = new ResizeObserver(scheduleUpdate);
    observer.observe(sidebar);
  }
  window.addEventListener("resize", scheduleUpdate, { passive: true });
  scheduleUpdate();
}

function initLandingNav() {
  const nav = document.querySelector(".site-nav");
  if (!nav) return;

  const links = Array.from(nav.querySelectorAll('a.site-nav__link[href]'));
  const sectionLinks = links
    .map((link) => {
      const href = link.getAttribute("href") || "";
      if (!href.startsWith("#")) return null;
      const target = document.querySelector(href);
      return target ? { link, href, target } : null;
    })
    .filter(Boolean);

  nav.addEventListener("click", (e) => {
    const link = e.target.closest('a.site-nav__link[href^="#"]');
    if (!link || !nav.contains(link)) return;
    const href = link.getAttribute("href") || "";
    const target = document.querySelector(href);
    if (!target) return;

    e.preventDefault();
    if (href === "#hero") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    smoothScrollTo(target, { offset: -getLandingHeaderOffset(14), duration: 0.75 });
  });

  if (!sectionLinks.length) return;

  const updateActive = () => {
    const y = window.scrollY + getLandingHeaderOffset(28);
    let active = sectionLinks[0];
    for (const item of sectionLinks) {
      const top = item.target.getBoundingClientRect().top + window.scrollY;
      if (top <= y) active = item;
    }
    links.forEach((link) => link.classList.toggle("is-active", active && link === active.link));
  };

  onScrollUnified(updateActive);
  updateActive();
}

function initLandingIntelligenceMotion() {
  if (prefersReducedMotion) return;

  const section = document.getElementById("landing-intelligence");
  const discovery = document.getElementById("discovery-shell");
  if (!section || section.hidden) return;

  const cardDeck = section.querySelector(".landing-intelligence__cards");
  const mobileQuery = window.matchMedia?.("(max-width: 900px)");
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  let ticking = false;

  const setPxVar = (el, name, value) => {
    el.style.setProperty(name, `${Math.round(value)}px`);
  };

  const update = () => {
    ticking = false;

    const vh = window.innerHeight || document.documentElement.clientHeight || 1;
    const mobile = !!mobileQuery?.matches;
    const rect = section.getBoundingClientRect();
    const sectionHeight = Math.max(rect.height, 1);
    const enter = clamp((vh - rect.top) / (vh * (mobile ? 0.92 : 0.86)), 0, 1);
    const travel = clamp((vh - rect.top) / (vh + sectionHeight), 0, 1);
    const exit = clamp(-rect.top / sectionHeight, 0, 1);
    const easeEnter = 1 - Math.pow(1 - enter, 3);

    setPxVar(section, "--intelligence-y", (1 - easeEnter) * (mobile ? 34 : 78) - exit * (mobile ? 18 : 36));
    setPxVar(section, "--intelligence-intro-y", (1 - easeEnter) * (mobile ? 24 : 58) - exit * (mobile ? 12 : 30));
    section.style.setProperty("--intelligence-fade", String(0.25 + easeEnter * 0.75));
    section.style.setProperty("--intelligence-sweep", `${Math.round(travel * 155 - 24)}%`);

    if (cardDeck) {
      const deckDrift = Math.sin(travel * Math.PI) * (mobile ? 10 : 26);
      const deckY = (1 - easeEnter) * (mobile ? 42 : 104) + deckDrift - exit * (mobile ? 14 : 34);
      setPxVar(cardDeck, "--intelligence-cards-y", deckY);
      cardDeck.style.setProperty("--intelligence-cards-fade", String(0.26 + easeEnter * 0.74));
      cardDeck.style.setProperty("--intelligence-cards-scale", String(0.96 + easeEnter * 0.04));
    }

    if (discovery) {
      const dRect = discovery.getBoundingClientRect();
      const dEnter = clamp((vh - dRect.top) / (vh * (mobile ? 0.94 : 0.82)), 0, 1);
      const dEase = 1 - Math.pow(1 - dEnter, 3);
      setPxVar(discovery, "--discovery-y", (1 - dEase) * (mobile ? 30 : 76));
      discovery.style.setProperty("--discovery-fade", String(0.42 + dEase * 0.58));
    }
  };

  const requestUpdate = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  update();
  onScrollUnified(requestUpdate);
  mobileQuery?.addEventListener?.("change", requestUpdate);
}

function initScrollRevealSections() {
  if (!isBrowsePage()) return;
  const sections = Array.from(document.querySelectorAll(".scroll-reveal"));
  if (!sections.length) return;

  if (prefersReducedMotion || typeof IntersectionObserver !== "function") {
    sections.forEach((section) => section.classList.add("is-visible"));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add("is-visible");
      observer.unobserve(entry.target);
    }
  }, {
    root: null,
    threshold: 0.04,
    rootMargin: "0px 0px -8% 0px",
  });

  sections.forEach((section) => observer.observe(section));
}

function initScrollChapters() {
  const chapters = Array.from(document.querySelectorAll("[data-scroll-chapter]"));
  if (!chapters.length) return;

  const root = document.documentElement;
  const desktopQuery = window.matchMedia?.("(min-width: 901px)");
  let rafId = 0;

  const supportsChapterMotion = () => !prefersReducedMotion && !!desktopQuery?.matches;
  const applyFocus = () => {
    rafId = 0;
    if (!supportsChapterMotion()) {
      root.classList.remove("has-scroll-chapters");
      chapters.forEach((chapter) => {
        chapter.classList.remove("is-scroll-current");
        chapter.style.setProperty("--chapter-focus", "1");
        chapter.style.setProperty("--chapter-shift", "0px");
      });
      return;
    }

    root.classList.add("has-scroll-chapters");
    const headerHeight = document.querySelector(".site-header")?.getBoundingClientRect().height || 0;
    const anchor = headerHeight + Math.max(150, (window.innerHeight - headerHeight) * 0.46);
    let currentChapter = chapters[0];
    let smallestDistance = Number.POSITIVE_INFINITY;

    chapters.forEach((chapter) => {
      const rect = chapter.getBoundingClientRect();
      const distance = rect.top > anchor
        ? rect.top - anchor
        : (rect.bottom < anchor ? anchor - rect.bottom : 0);
      if (distance < smallestDistance) {
        smallestDistance = distance;
        currentChapter = chapter;
      }
    });

    chapters.forEach((chapter) => {
      const isCurrent = chapter === currentChapter;
      const rect = chapter.getBoundingClientRect();
      chapter.classList.toggle("is-scroll-current", isCurrent);
      chapter.style.setProperty("--chapter-focus", isCurrent ? "1" : "0.72");
      chapter.style.setProperty("--chapter-shift", isCurrent ? "0px" : (rect.top > anchor ? "24px" : "-14px"));
    });
  };

  const requestFocusUpdate = () => {
    if (rafId) return;
    rafId = requestAnimationFrame(applyFocus);
  };

  onScrollUnified(requestFocusUpdate);
  desktopQuery?.addEventListener?.("change", requestFocusUpdate);
  requestFocusUpdate();
}


function smoothScrollTo(target, opts = {}) {
  const el = (typeof target === "string") ? document.querySelector(target) : target;
  if (!el) return;
  const offset = Number.isFinite(opts.offset) ? opts.offset : -24;

  if (window.__lenis) {
    window.__lenis.scrollTo(el, {
      offset,
      duration: opts.duration ?? 1.1
    });
    return;
  }
  const top = el.getBoundingClientRect().top + window.scrollY + offset;
  window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
}

function getStickyTopOffset(el, fallback = 0) {
  if (!el) return fallback;
  const topValue = window.getComputedStyle(el).top || "";
  const direct = Number.parseFloat(topValue);
  if (Number.isFinite(direct)) return direct;

  const pxParts = topValue.match(/-?\d*\.?\d+px/g) || [];
  const summed = pxParts.reduce((total, part) => total + (Number.parseFloat(part) || 0), 0);
  return Number.isFinite(summed) && summed > 0 ? summed : fallback;
}

function getJourneyNavCoverOffset(extra = 10) {
  const nav = document.getElementById("journey-nav");
  if (!nav) return 32;
  const mobileFallbackTop = window.matchMedia?.("(max-width: 900px)")?.matches ? 2 : 8;
  const topPx = getStickyTopOffset(nav, mobileFallbackTop);
  return Math.ceil(nav.getBoundingClientRect().height + topPx + extra);
}

function escHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function buildRetailerVariantRow(variantsByRetailer, retailerName) {
    const V = variantsByRetailer?.[retailerName];
    if (!V || !V.length) {
        return `<div class="muted">No variants found.</div>`;
    }

    const items = V.slice()
        .sort((a, b) => (a.unit_price ?? 1e18) - (b.unit_price ?? 1e18));

    const rows = items.map(v => {
        const label = escHtml(v.label || 'Variant');
        const basis = v.unit_basis ? ` / ${escHtml(v.unit_basis)}` : '';
        const unit = (v.unit_price != null) ? `${fmtZAR(v.unit_price)}${basis}` : '-';
        const shelf = (v.shelf_price != null) ? `Shelf ${fmtZAR(v.shelf_price)}` : '';
        const meta = shelf ? `<div class="muted wpt-var-meta">${escHtml(shelf)}</div>` : '';
        const url = v.url ? `href="${escHtml(v.url)}" target="_blank" rel="noopener"` : '';
        return `
    <div class="wpt-var-item">
        <div class="wpt-var-left">
        <a ${url} class="wpt-var-link">
            ${label}
        </a>
        ${meta}
        </div>
        <div class="wpt-var-price">${unit}</div>
    </div>
    `;
    }).join('');

    return `
    <div class="muted" style="margin-bottom:6px;">Variants (sorted by normalized price)</div>
    <div class="wpt-var-box">
    ${rows}
    </div>
`;
}

function buildRetailerOfferDetails(row, variantsByRetailer, retailerName) {
    const variantsBlock = buildRetailerVariantRow(variantsByRetailer, retailerName);
    const shelfDiffers = row.current_shelf != null
      && row.current_price != null
      && Math.abs(Number(row.current_shelf) - Number(row.current_price)) > 0.005;
    const facts = [
      row.variant ? { label: 'Variant', value: row.variant } : null,
      shelfDiffers ? { label: 'Shelf', value: fmtZAR(row.current_shelf) } : null,
      row.compare_at_latest != null ? { label: 'Compare-at', value: fmtZAR(row.compare_at_latest) } : null,
    ].filter(Boolean);
    const link = row.url
      ? `<a class="retailer-offer-detail__link" href="${escHtml(row.url)}" target="_blank" rel="noopener" aria-label="Open ${escHtml(row.retailer || 'retailer')} product page">Visit retailer page</a>`
      : '';

    return `
      <div class="retailer-offer-detail">
        ${facts.length ? `
          <div class="retailer-offer-detail__facts">
            ${facts.map((fact) => `
              <div class="retailer-offer-detail__fact">
                <span>${escHtml(fact.label)}</span>
                <strong>${escHtml(fact.value)}</strong>
              </div>
            `).join('')}
          </div>
        ` : ''}
        ${link}
        ${variantsBlock}
      </div>
    `;
}

const PLOT_PALETTE = [
  "#c58a3a", "#a76e2a", "#e0b15c", "#7a4a22", "#d59b4b",
  "#8d5b2a", "#b07a33", "#f0c97a", "#6a3f1f", "#caa053",
];
let plotInitialized = false;
let lockedRetailerHighlight = "";
let previewRetailerHighlight = "";
const PLOT_DEFAULT_RANGE_DAYS = 90;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function norm(value) {
    return (value || '')
        .toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')   // strip accents
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}

// ---- Immersive spotlight (CSS vars) ----
// Updates --mx/--my on :root for cursor-follow glow. Cheap + looks high-end.
(function initSpotlight() {
  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  if (reduce) return;
  let raf = 0;
  let lastX = 0, lastY = 0;
  const setVars = () => {
    raf = 0;
    document.documentElement.style.setProperty("--mx", lastX + "px");
    document.documentElement.style.setProperty("--my", lastY + "px");
  };
  window.addEventListener("pointermove", (e) => {
    lastX = e.clientX;
    lastY = e.clientY;
    if (!raf) raf = requestAnimationFrame(setVars);
  }, { passive: true });
})();

function bigrams(value) {
    const x = (value || '').replace(/\s+/g, '');
    const out = [];
    for (let i = 0; i < x.length - 1; i++) out.push(x.slice(i, i + 2));
    return out;
}

function dice(a, b) {
    if (!a || !b) return 0;
    const A = bigrams(a), B = bigrams(b);
    if (!A.length || !B.length) return 0;
    const m = new Map();
    for (const g of A) m.set(g, (m.get(g) || 0) + 1);
    let inter = 0;
    for (const g of B) {
        const c = m.get(g) || 0;
        if (c > 0) { inter++; m.set(g, c - 1); }
    }
    return (2 * inter) / (A.length + B.length);
}
function buildSearchCorpus(name) {
  const parts = [];
  const entry = getCatalogEntry(name);
  // 1) Name (always)
  parts.push(name);

  // 2) Retailers / stats
  const summary = entry?.stats || null;
  if (summary?.current_min_retailer) parts.push(String(summary.current_min_retailer));

  const rows = Array.isArray(entry?.retailer_rows) ? entry.retailer_rows : [];
  for (const r of rows) {
    if (r?.retailer) parts.push(String(r.retailer));
  }

  // 3) Optional meta
  const meta = entry?.meta || null;
  if (meta) {
    for (const k of ["brand","country","region","age"]) {
      if (meta?.[k]) parts.push(String(meta[k]));
    }
  }

  return parts.filter(Boolean).join(" | ");
}

async function loadCatalog() {
  // Static hosting: keep cache default; you can add ?v=... later if needed
  try {
    const r = await fetch(buildDataUrl("catalog.json"));
    if (!r.ok) throw new Error(String(r.status));
    CATALOG = await r.json();
    CATALOG_ITEMS = Array.isArray(CATALOG?.items) ? CATALOG.items : [];
    CATALOG_BY_NAME = new Map(
      CATALOG_ITEMS
        .map((item) => [String(item?.name || "").trim(), item])
        .filter(([name]) => !!name)
    );
    tasteMatchCache.clear();
    if (CATALOG_ITEMS.length) {
      products = CATALOG_ITEMS
        .map((item) => String(item?.name || "").trim())
        .filter(Boolean);
      productNameSet = new Set(products);
      if (!currentProduct || !productNameSet.has(currentProduct)) {
        currentProduct = products[0] || "";
      }
    }
  } catch (e) {
    console.warn("Could not load catalog.json; falling back to BOOT.products only.", e);
    CATALOG = null;
    CATALOG_ITEMS = [];
    CATALOG_BY_NAME = new Map();
  }
}

function getCatalogEntry(name) {
  const key = String(name || "").trim();
  if (!key) return null;
  return CATALOG_BY_NAME.get(key) || null;
}

function getCatalogMeta(name) {
  return getCatalogEntry(name)?.meta || {};
}

function getCatalogSummary(name) {
  return getCatalogEntry(name)?.stats || null;
}

function getCatalogRetailerRows(name) {
  const rows = getCatalogEntry(name)?.retailer_rows;
  return Array.isArray(rows) ? rows : [];
}

function getCatalogDeal(name) {
  return getCatalogEntry(name)?.deal || {};
}

function getCatalogImage(name) {
  return getCatalogImageCandidates(name)[0] || "";
}

function getCatalogImageCandidates(name) {
  const entry = getCatalogEntry(name) || {};
  const out = [];
  const seen = new Set();
  const push = (value) => {
    const text = String(value ?? '').trim();
    if (!text) return;
    if (!/^https?:\/\//i.test(text)) return;
    const key = text.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push(text);
  };
  push(entry.image);
  const extras = entry.image_candidates;
  if (Array.isArray(extras)) extras.forEach(push);
  return out;
}

function getCatalogDataFile(name) {
  const value = getCatalogEntry(name)?.data_file;
  return value ? String(value).trim() : "";
}

function getCatalogSlug(name) {
  const dataFile = getCatalogDataFile(name);
  const fromDataFile = String(dataFile || "").replace(/\.json$/i, "").trim();
  return fromDataFile || slugifyProductName(name);
}

function buildProductPageUrl(name) {
  const params = new URLSearchParams();
  const slug = getCatalogSlug(name);
  if (slug) params.set("product", slug);
  const query = params.toString();
  return `product.html${query ? `?${query}` : ""}`;
}

function buildTasteBrowseUrl(name) {
  const params = new URLSearchParams();
  const slug = getCatalogSlug(name);
  if (slug) params.set(TASTE_REFERENCE_PARAM, slug);
  const query = params.toString();
  return `index.html${query ? `?${query}` : ""}#discovery-shell`;
}

function resolveCatalogProductName(requestedValue) {
  const requested = String(requestedValue || "").trim();
  if (!requested) return "";
  if (productNameSet.has(requested)) return requested;

  const requestedLower = requested.toLowerCase();
  for (const name of (products || [])) {
    if (getCatalogSlug(name).toLowerCase() === requestedLower) return name;
  }

  const requestedNorm = norm(requested);
  if (!requestedNorm) return "";
  return (products || []).find((name) => (
    norm(name) === requestedNorm || norm(getCatalogSlug(name)) === requestedNorm
  )) || "";
}

function resolveRequestedProductName() {
  const params = new URLSearchParams(window.location.search);
  const requested = String(params.get("product") || params.get("name") || "").trim();
  return resolveCatalogProductName(requested);
}

function readRequestedTasteReference() {
  if (!isBrowsePage()) return { requested: "", name: "" };
  try {
    const requested = String(new URLSearchParams(window.location.search).get(TASTE_REFERENCE_PARAM) || "").trim();
    return { requested, name: resolveCatalogProductName(requested) };
  } catch {
    return { requested: "", name: "" };
  }
}

function syncTasteReferenceUrl(name) {
  if (!isBrowsePage()) return;
  try {
    const url = new URL(window.location.href);
    const slug = getCatalogSlug(name);
    if (slug) url.searchParams.set(TASTE_REFERENCE_PARAM, slug);
    else url.searchParams.delete(TASTE_REFERENCE_PARAM);
    window.history.replaceState(window.history.state, "", url);
  } catch {}
}

function syncPageChrome(name = "") {
  if (!isProductPage()) {
    if (isBrowsePage()) document.title = "Whisk(e)y Tracker";
    return;
  }

  document.title = name ? `${name} | Whisk(e)y Tracker` : "Whisk(e)y Tracker";
  try {
    const url = new URL(window.location.href);
    const slug = getCatalogSlug(name);
    if (slug) url.searchParams.set("product", slug);
    else url.searchParams.delete("product");
    if (url.toString() !== window.location.href) {
      window.history.replaceState({ product: slug }, "", url);
    }
  } catch {}
}

function getSearchRows() {
  const rows = [];
  const seen = new Set();

  if (CATALOG_ITEMS.length) {
    for (const it of CATALOG_ITEMS) {
      const name = String(it?.name || "").trim();
      if (!name || seen.has(name)) continue;
      seen.add(name);
      const n = norm(name);
      rows.push({
        name,
        n,
        nameTokens: n.split(" ").filter(Boolean),
      });
    }
  }

  // Ensure every BOOT product is searchable even if catalog fails to load.
  for (const name of (products || [])) {
    if (!name || seen.has(name)) continue;
    seen.add(name);
    const n = norm(name);
    rows.push({
      name,
      n,
      nameTokens: n.split(" ").filter(Boolean),
    });
  }

  return rows;
}

let SEARCH_ROWS = [];
let SEARCH_ROW_BY_NAME = new Map();
function rebuildSearchIndex() {
  SEARCH_ROWS = getSearchRows();
  SEARCH_ROW_BY_NAME = new Map(SEARCH_ROWS.map((r) => [r.name, r]));
}

// --- Guided browse meta (from parsed bootstrap) ---
const BLANK_FILTER_VALUE = "__BLANK__";

function cleanFacetValue(v) {
  return String(v ?? "").trim();
}

function isNoAgeStatementValue(value) {
  const text = cleanFacetValue(value).replace(/\s+/g, " ");
  if (!text) return true;
  return /^(?:nas|n\/a|na|no age|no age statement|none|null|undefined|nan|-|unknown|not stated)$/i.test(text);
}

function extractNumericAgeValue(value) {
  const text = cleanFacetValue(value).replace(/\s+/g, " ");
  if (!text) return "";
  const match = text.match(/^(?:aged\s*)?(\d+(?:[.,]\d+)?)\s*(?:yo|y\/o|yr|yrs|year|years|year old|years old)?$/i);
  if (!match) return "";
  return match[1].replace(",", ".").replace(/\.0+$/, "");
}

function normalizeAgeFacetValue(value) {
  if (isNoAgeStatementValue(value)) return "";
  return extractNumericAgeValue(value) || cleanFacetValue(value);
}

function formatAgeDisplay(value, { ageStatement = null } = {}) {
  const stated = cleanFacetValue(ageStatement).replace(/\s+/g, " ");
  if (stated) return isNoAgeStatementValue(stated) ? "NAS" : stated;
  if (isNoAgeStatementValue(value)) return "NAS";
  const numeric = extractNumericAgeValue(value);
  if (numeric) return `${numeric} YO`;
  return cleanFacetValue(value);
}

function formatMetadataValueLabel(key, value, { blankLabel = "" } = {}) {
  if (value === BLANK_FILTER_VALUE) return blankLabel || "Unspecified";
  if (key === "age") return formatAgeDisplay(value);
  return cleanFacetValue(value);
}

function hasValidCurrentPrice(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0;
}

function readMultiSelectValues(id) {
  const el = document.getElementById(id);
  if (!el) return [];
  return Array.from(el.selectedOptions || []).map((o) => o.value);
}

const FILTERS = {
  brand: new Set(),
  family: new Set(),
  style: new Set(),
  retailer: new Set(),
  country: new Set(),
  region: new Set(),
  age: new Set(),
  strength: new Set(),
  peat: new Set(),
  cask: new Set(),
  wood: new Set(),
  priceMin: null,
  priceMax: null,
};
const FACET_LABELS = {
  brand: "Brand",
  family: "Family",
  style: "Style",
  retailer: "Retailer",
  country: "Country",
  region: "Region",
  age: "Age",
  strength: "Strength",
  peat: "Peat",
  cask: "Cask Influence",
  wood: "Wood Type",
};
const FACET_EMPTY_LABELS = {
  brand: "No brand",
  family: "No family",
  style: "No style",
  retailer: "No retailer",
  country: "No country",
  region: "No region",
  age: "NAS",
  strength: "No strength",
  peat: "No peat info",
  cask: "No cask influence",
  wood: "No wood info",
};
const FACET_KEYS = Object.keys(FACET_LABELS);
const FACET_META_KEYS = {
  brand: "brand",
  family: "whisky_family",
  style: "production_style",
  country: "country",
  region: "region",
  age: "age",
  strength: "strength_label",
  peat: "peated_state",
  cask: "cask",
};
const FACET_GROUPS = [
  {
    key: "product",
    title: "Product",
    description: "Bottle identity and production profile",
    keys: ["brand", "family", "style", "age", "strength", "peat", "cask", "wood"],
  },
  {
    key: "origin",
    title: "Origin",
    description: "Where the whisky comes from",
    keys: ["country", "region"],
  },
  {
    key: "shopping",
    title: "Shopping",
    description: "Where to buy and what to pay",
    keys: ["retailer"],
  },
];
const DEALS_PER_PAGE = 24;
const DEAL_SORT_LABELS = {
  match: "Closest overall match",
  taste: "Closest taste profile",
  pct: "% all-time price drop",
  value: "Rand all-time price drop",
  recent_pct: "% drop in last 7 days",
  recent_value: "Rand drop in last 7 days",
  price: "Current price",
  name: "Product name",
};
const DEAL_SORT_SCOPE_LABELS = {
  match: "overall profile match",
  taste: "taste profile match",
  pct: "% all-time price drop",
  value: "R all-time price drop",
  recent_pct: "% drop in last 7 days",
  recent_value: "R drop in last 7 days",
  price: "current price",
  name: "product name",
};
const DEAL_SORT_OPTIONS = new Set(Object.keys(DEAL_SORT_LABELS));
const DEAL_SORT_DEFAULT_DIR = {
  match: "desc",
  taste: "desc",
  pct: "desc",
  value: "desc",
  recent_pct: "desc",
  recent_value: "desc",
  price: "asc",
  name: "asc",
};

function getDealOrderValue(sortBy = DEAL_VIEW?.sortBy, sortDir = DEAL_VIEW?.sortDir) {
  return `${sortBy}:${isValidSortDir(sortDir) ? sortDir : getDefaultSortDir(sortBy)}`;
}

function parseDealOrderValue(value) {
  const [sortBy, sortDir] = String(value || "").split(":");
  if (!isValidDealSort(sortBy) || !isValidSortDir(sortDir)) return null;
  return { sortBy, sortDir };
}

const DEAL_VIEW_PREFS_KEY = "wpt-deal-view";
const BROWSE_STATE_KEY = "wpt-browse-state";

function isValidDealSort(sortBy) {
  return DEAL_SORT_OPTIONS.has(sortBy);
}

function isValidSortDir(sortDir) {
  return sortDir === "asc" || sortDir === "desc";
}

function getDefaultSortDir(sortBy) {
  return DEAL_SORT_DEFAULT_DIR[sortBy] || "desc";
}

function isProfileMatchSort(sortBy) {
  return sortBy === "match" || sortBy === "taste";
}

const DEAL_VIEW = (() => {
  let sortBy = "pct";
  let sortDir = getDefaultSortDir(sortBy);
  try {
    const raw = localStorage.getItem(DEAL_VIEW_PREFS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.sortBy === "recent") sortBy = "recent_value";
      else if (isValidDealSort(parsed?.sortBy)) sortBy = parsed.sortBy;
      if (isValidSortDir(parsed?.sortDir)) sortDir = parsed.sortDir;
      else sortDir = getDefaultSortDir(sortBy);
    }
  } catch {}
  return { sortBy, sortDir, page: 1 };
})();

syncDiscoveryDropStateForSort(DEAL_VIEW.sortBy);

function persistDealViewPrefs() {
  try {
    const sortBy = isProfileMatchSort(DEAL_VIEW.sortBy) ? "pct" : DEAL_VIEW.sortBy;
    localStorage.setItem(DEAL_VIEW_PREFS_KEY, JSON.stringify({
      sortBy,
      sortDir: isProfileMatchSort(DEAL_VIEW.sortBy) ? getDefaultSortDir(sortBy) : DEAL_VIEW.sortDir,
    }));
  } catch {}
}

function getTasteReferenceProfile(name) {
  const profile = normalizeTasteVector(getCatalogMeta(name)?.taste_vector);
  return profile && Number(profile.available_field_count) >= 2 ? profile : null;
}

function hasDiscoveryTasteProfile() {
  return Array.from(discoveryTasteLevels.values()).some((level) => level > 0);
}

function getDiscoveryTasteLevel(axisKey) {
  return Math.max(0, Math.min(1, Number(discoveryTasteLevels.get(axisKey)) || 0));
}

function clearDiscoveryTasteProfile() {
  discoveryTasteAxes.clear();
  discoveryTasteLevels.clear();
}

function hasActiveTasteSearch() {
  return !!tasteReferenceName || hasDiscoveryTasteProfile();
}

function syncDiscoveryOrderControl() {
  const orderSelect = document.getElementById("discovery-order");
  if (!orderSelect) return;

  const tasteSearchActive = hasActiveTasteSearch();
  orderSelect.querySelectorAll("[data-profile-order]").forEach((option) => {
    option.disabled = !tasteSearchActive;
  });

  const selectedValue = getDealOrderValue(DEAL_VIEW.sortBy, DEAL_VIEW.sortDir);
  if (Array.from(orderSelect.options).some((option) => option.value === selectedValue)) {
    orderSelect.value = selectedValue;
  }
}

function getDiscoveryTasteProfile() {
  if (!hasDiscoveryTasteProfile()) return null;
  const selectedCount = Array.from(discoveryTasteLevels.values()).filter((level) => level > 0).length;
  return {
    categories: Object.fromEntries(
      TASTE_AXES.map((axis) => [axis.key, getDiscoveryTasteLevel(axis.key)])
    ),
    // These fields only satisfy the shared vector validator. This is a user intent,
    // not evidence derived from a bottle's tasting notes.
    included_count: Math.max(3, selectedCount),
    available_field_count: 3,
    source: "discovery_flavour_profile",
  };
}

function formatDiscoveryTasteSelection() {
  return TASTE_AXES
    .filter((axis) => getDiscoveryTasteLevel(axis.key) > 0)
    .map((axis) => `${axis.label} · ${formatTasteIntensity(getDiscoveryTasteLevel(axis.key))}`);
}

function formatTasteIntensity(level) {
  const value = Math.max(0, Math.min(1, Number(level) || 0));
  if (value <= 0) return "None";
  if (value <= 0.2) return "Hint";
  if (value <= 0.45) return "Light";
  if (value <= 0.7) return "Noticeable";
  if (value < 1) return "Pronounced";
  return "Dominant";
}

function compassPoint(cx, cy, radius, angle) {
  const radians = (angle - 90) * (Math.PI / 180);
  return {
    x: cx + (radius * Math.cos(radians)),
    y: cy + (radius * Math.sin(radians)),
  };
}

function compassArcPath(cx, cy, innerRadius, outerRadius, startAngle, endAngle) {
  const outerStart = compassPoint(cx, cy, outerRadius, startAngle);
  const outerEnd = compassPoint(cx, cy, outerRadius, endAngle);
  const innerEnd = compassPoint(cx, cy, innerRadius, endAngle);
  const innerStart = compassPoint(cx, cy, innerRadius, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return [
    `M ${outerStart.x.toFixed(2)} ${outerStart.y.toFixed(2)}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${outerEnd.x.toFixed(2)} ${outerEnd.y.toFixed(2)}`,
    `L ${innerEnd.x.toFixed(2)} ${innerEnd.y.toFixed(2)}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerStart.x.toFixed(2)} ${innerStart.y.toFixed(2)}`,
    "Z",
  ].join(" ");
}

function compassLabelTransform(cx, cy, radius, angle) {
  const point = compassPoint(cx, cy, radius, angle);
  let rotation = angle;
  if (angle > 90 && angle < 270) rotation += 180;
  return `translate(${point.x.toFixed(2)} ${point.y.toFixed(2)}) rotate(${rotation.toFixed(2)})`;
}

function compassTextArcPath(cx, cy, radius, startAngle, endAngle, reverse = false) {
  const fromAngle = reverse ? endAngle : startAngle;
  const toAngle = reverse ? startAngle : endAngle;
  const from = compassPoint(cx, cy, radius, fromAngle);
  const to = compassPoint(cx, cy, radius, toAngle);
  return [
    `M ${from.x.toFixed(2)} ${from.y.toFixed(2)}`,
    `A ${radius} ${radius} 0 0 ${reverse ? 0 : 1} ${to.x.toFixed(2)} ${to.y.toFixed(2)}`,
  ].join(" ");
}

function shouldRenderCompactFlavourCompass() {
  return typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && window.matchMedia("(max-width: 420px)").matches;
}

function renderDiscoveryTasteTargetWheel() {
  const size = 260;
  const center = size / 2;
  const innerRadius = 43;
  const outerRadius = 105;
  const slice = 360 / TASTE_AXES.length;
  const tracks = TASTE_AXES.map((axis, index) => {
    const start = (index * slice) + 1.5;
    const end = ((index + 1) * slice) - 1.5;
    const level = getDiscoveryTasteLevel(axis.key);
    const intensityRadius = innerRadius + ((outerRadius - innerRadius) * level);
    return `
      <path class="flavour-target-wheel__track" d="${compassArcPath(center, center, innerRadius, outerRadius, start, end)}"></path>
      ${level > 0 ? `<path class="flavour-target-wheel__intensity" d="${compassArcPath(center, center, innerRadius, intensityRadius, start, end)}" style="--taste-tone:${axis.tone}"><title>${escHtml(`${axis.label}: ${formatTasteIntensity(level)} desired intensity`)}</title></path>` : ""}
    `;
  }).join("");
  const activeCount = TASTE_AXES.filter((axis) => getDiscoveryTasteLevel(axis.key) > 0).length;
  return `
    <svg class="flavour-target-wheel" viewBox="0 0 ${size} ${size}" role="img" aria-label="Your target flavour intensity wheel">
      ${tracks}
      <circle class="flavour-target-wheel__core" cx="${center}" cy="${center}" r="${innerRadius - 2}"></circle>
      <text x="${center}" y="${center - 5}" text-anchor="middle">TARGET</text>
      <text class="flavour-target-wheel__count" x="${center}" y="${center + 15}" text-anchor="middle">${activeCount}</text>
    </svg>
  `;
}

function renderDiscoveryTasteIntensityControls() {
  return TASTE_AXES.map((axis) => {
    const level = getDiscoveryTasteLevel(axis.key);
    return `
      <label class="flavour-intensity-control" style="--taste-tone:${axis.tone}">
        <span>${escHtml(axis.label)}</span>
        <div class="flavour-intensity-slider" role="slider" tabindex="0" data-flavour-level="${axis.key}" data-flavour-value="${level}" aria-label="${escHtml(axis.label)} desired intensity" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(level * 100)}" aria-valuetext="${escHtml(formatTasteIntensity(level))}">
          <span class="flavour-intensity-slider__track"><span class="flavour-intensity-slider__fill" style="width:${Math.round(level * 100)}%"></span><i class="flavour-intensity-slider__thumb" style="left:${Math.round(level * 100)}%"></i></span>
        </div>
        <output>${escHtml(formatTasteIntensity(level))}</output>
      </label>
    `;
  }).join("");
}

function renderFlavourCompass() {
  const host = document.getElementById("flavour-compass");
  if (!host) return;

  const activeAxis = TASTE_AXES.find((axis) => axis.key === discoveryTasteGuideAxis) || TASTE_AXES[0];
  const activeGuide = FLAVOUR_COMPASS_GUIDE[activeAxis.key];
  const compactMobile = shouldRenderCompactFlavourCompass();
  const size = 400;
  const center = size / 2;
  const innerRadius = compactMobile ? 82 : 68;
  const middleRadius = compactMobile ? 184 : 124;
  const outerRadius = 184;
  const familyLabelRadius = compactMobile ? 134 : 96;
  const slice = 360 / TASTE_AXES.length;
  const outerNotes = compactMobile ? "" : TASTE_AXES.map((axis, axisIndex) => {
    const notes = FLAVOUR_COMPASS_GUIDE[axis.key]?.notes?.slice(0, 3) || [];
    const start = (axisIndex * slice) + 0.8;
    const end = ((axisIndex + 1) * slice) - 0.8;
    const midpoint = (start + end) / 2;
    const reverse = midpoint > 90 && midpoint < 270;
    const labelRadii = [141, 155, 169];
    return `
      <g class="flavour-compass__note${axis.key === activeAxis.key ? " is-active" : ""}" data-flavour-guide-axis="${axis.key}">
        <path d="${compassArcPath(center, center, middleRadius + 2, outerRadius, start, end)}" style="--taste-tone:${axis.tone}"></path>
        ${notes.map((note, noteIndex) => {
          const pathId = `flavour-compass-label-${axis.key}-${noteIndex}`;
          return `
            <path id="${pathId}" class="flavour-compass__label-path" d="${compassTextArcPath(center, center, labelRadii[noteIndex], start + 2, end - 2, reverse)}"></path>
            <text class="flavour-compass__curved-label" dominant-baseline="middle"><textPath href="#${pathId}" startOffset="50%" text-anchor="middle">${escHtml(note)}</textPath></text>
          `;
        }).join("")}
      </g>
    `;
  }).join("");
  const outerTrack = compactMobile
    ? ""
    : `<circle class="flavour-compass__outer-track" cx="${center}" cy="${center}" r="${outerRadius}"></circle>`;
  const families = TASTE_AXES.map((axis, axisIndex) => {
    const start = (axisIndex * slice) + 1.2;
    const end = ((axisIndex + 1) * slice) - 1.2;
    const midpoint = (start + end) / 2;
    const selected = getDiscoveryTasteLevel(axis.key) > 0;
    const active = axis.key === activeAxis.key;
    return `
      <g class="flavour-compass__family${selected ? " is-selected" : ""}${active ? " is-active" : ""}" data-flavour-axis="${axis.key}" role="button" tabindex="0" aria-pressed="${selected ? "true" : "false"}" aria-label="${escHtml(axis.label)} flavour family${selected ? ", selected" : ""}">
        <path d="${compassArcPath(center, center, innerRadius, middleRadius, start, end)}" style="--taste-tone:${axis.tone}"></path>
        <text transform="${compassLabelTransform(center, center, familyLabelRadius, midpoint)}" text-anchor="middle" dominant-baseline="middle">${escHtml(axis.label)}</text>
      </g>
    `;
  }).join("");
  const selectedLabels = formatDiscoveryTasteSelection();
  host.innerHTML = `
    <div class="flavour-compass__visual${compactMobile ? " is-families-only" : ""}">
      <svg viewBox="0 0 ${size} ${size}" role="img" aria-labelledby="flavour-compass-title flavour-compass-description">
        <title id="flavour-compass-title">Whisky flavour compass</title>
        <desc id="flavour-compass-description">${compactMobile ? "Eight broad flavour families are shown for clear mobile selection." : "Eight broad flavour families form the inner ring. The outer ring offers examples of the aromas associated with each family."}</desc>
        ${outerTrack}
        ${outerNotes}
        ${families}
        <circle class="flavour-compass__core" cx="${center}" cy="${center}" r="${innerRadius - 4}"></circle>
        <text class="flavour-compass__core-kicker" x="${center}" y="${center - 12}" text-anchor="middle">YOUR TASTE</text>
        <text class="flavour-compass__core-value" x="${center}" y="${center + 12}" text-anchor="middle">${selectedLabels.length ? `${selectedLabels.length} ${selectedLabels.length === 1 ? "FAMILY" : "FAMILIES"}` : "CHOOSE"}</text>
      </svg>
    </div>
    <div class="flavour-compass__detail">
      <h3>${escHtml(activeGuide.title)}</h3>
      <p>${escHtml(activeGuide.description)}</p>
      <div class="flavour-compass__note-list" aria-label="${escHtml(activeGuide.title)} note examples">
        ${activeGuide.notes.map((note) => `<span>${escHtml(note)}</span>`).join("")}
      </div>
      <button class="flavour-compass__toggle" type="button" data-flavour-axis="${activeAxis.key}" aria-pressed="${getDiscoveryTasteLevel(activeAxis.key) > 0 ? "true" : "false"}">${getDiscoveryTasteLevel(activeAxis.key) > 0 ? `Clear ${escHtml(activeAxis.label)}` : `Set ${escHtml(activeAxis.label)} to noticeable`}</button>
      ${selectedLabels.length ? `<p class="flavour-compass__selected">${escHtml(selectedLabels.join(" · "))}</p>` : ""}
    </div>
    <section class="flavour-target" aria-label="Target flavour intensity">
      <div class="flavour-target__wheel" id="flavour-target-wheel-host">${renderDiscoveryTasteTargetWheel()}</div>
      <div class="flavour-target__body">
        <p class="flavour-target__eyebrow">Your target intensity</p>
        <p class="flavour-target__hint">Move a slider to set how prominent each flavour should be. Unset families are ignored rather than avoided.</p>
        <div class="flavour-intensity-controls">${renderDiscoveryTasteIntensityControls()}</div>
      </div>
    </section>
    <div class="flavour-compass__families" aria-label="Flavour family controls">
      ${TASTE_AXES.map((axis) => `<button type="button" class="${getDiscoveryTasteLevel(axis.key) > 0 ? "is-selected" : ""}${axis.key === activeAxis.key ? " is-active" : ""}" data-flavour-axis="${axis.key}" aria-pressed="${getDiscoveryTasteLevel(axis.key) > 0 ? "true" : "false"}" style="--taste-tone:${axis.tone}">${escHtml(axis.label)}</button>`).join("")}
    </div>
  `;
}

function initFlavourCompassResponsiveMode() {
  if (window.__flavourCompassResponsiveModeInit || typeof window.matchMedia !== "function") return;
  window.__flavourCompassResponsiveModeInit = true;

  const compactMedia = window.matchMedia("(max-width: 420px)");
  const rerender = () => {
    if (document.getElementById("flavour-compass")) renderFlavourCompass();
  };
  if (typeof compactMedia.addEventListener === "function") {
    compactMedia.addEventListener("change", rerender);
  } else {
    compactMedia.addListener(rerender);
  }
}

function renderDiscoveryFlavourSummary() {
  const summary = document.getElementById("discovery-flavour-summary");
  const browseTitle = document.querySelector(".browse-dock__title");
  if (!summary) return;
  const selected = formatDiscoveryTasteSelection();
  if (!selected.length) {
    summary.hidden = true;
    summary.innerHTML = "";
    return;
  }
  summary.hidden = false;
  summary.innerHTML = `
    <h2>Showing bottles with ${escHtml(formatTasteLeaderList(selected.map((label) => label.toLowerCase())))}</h2>
    <div class="discovery-flavour-summary__chips">${selected.map((label) => `<span>${escHtml(label)}</span>`).join("")}</div>
  `;
  if (browseTitle) browseTitle.textContent = "Flavour-led recommendations";
}

function renderDiscoveryModeUi() {
  if (!isBrowsePage()) return;
  const mode = getDiscoveryModeCopy();
  document.querySelector(".discovery-workspace")?.classList.toggle("is-flavour-mode", discoveryMode === "flavour");
  document.querySelectorAll("[data-discovery-mode]").forEach((button) => {
    const active = button.getAttribute("data-discovery-mode") === discoveryMode;
    button.classList.toggle("is-active", active);
    button.setAttribute("aria-pressed", active ? "true" : "false");
  });
  document.querySelectorAll("[data-discovery-mode-panel]").forEach((panel) => {
    panel.hidden = panel.getAttribute("data-discovery-mode-panel") !== discoveryMode;
  });
  document.querySelectorAll("[data-discovery-mode-control]").forEach((control) => {
    control.hidden = control.getAttribute("data-discovery-mode-control") !== discoveryMode;
  });
  const title = document.getElementById("discovery-guidance-title");
  const action = document.getElementById("discovery-find-matches");
  if (title) title.textContent = mode.title;
  if (action) action.textContent = mode.action;
  syncDiscoveryOrderControl();

  if (!tasteReferenceName && !hasDiscoveryTasteProfile()) {
    const browseTitle = document.querySelector(".browse-dock__title");
    if (browseTitle) browseTitle.textContent = mode.resultsTitle;
  }
  renderFlavourCompass();
}

function refreshDiscoveryBrowse() {
  renderDiscoveryModeUi();
  renderTasteReferenceUi();
  renderDiscoveryFlavourSummary();
  window.__refreshTasteReferenceBrowse?.();
}

function setDiscoveryTasteAxis(axisKey) {
  if (!TASTE_AXES.some((axis) => axis.key === axisKey)) return;
  discoveryTasteGuideAxis = axisKey;
  setDiscoveryTasteLevel(axisKey, getDiscoveryTasteLevel(axisKey) > 0 ? 0 : 0.6);
}

function setDiscoveryTasteLevel(axisKey, rawLevel, { refresh = true } = {}) {
  if (!TASTE_AXES.some((axis) => axis.key === axisKey)) return;
  const level = Math.max(0, Math.min(1, Number(rawLevel) || 0));
  discoveryTasteGuideAxis = axisKey;
  if (level > 0) {
    discoveryTasteAxes.add(axisKey);
    discoveryTasteLevels.set(axisKey, level);
  } else {
    discoveryTasteAxes.delete(axisKey);
    discoveryTasteLevels.delete(axisKey);
  }
  discoveryMode = "flavour";
  if (hasDiscoveryTasteProfile()) {
    if (tasteReferenceName) {
      setTasteReference("", { syncUrl: true, refresh: false, defaultToMatch: false });
    }
    DEAL_VIEW.sortBy = "taste";
    DEAL_VIEW.sortDir = getDefaultSortDir("taste");
  }
  if (refresh) {
    DEAL_VIEW.page = 1;
    refreshDiscoveryBrowse();
  }
}

function setDiscoveryMode(nextMode, { refresh = true } = {}) {
  if (!Object.hasOwn(DISCOVERY_MODE_COPY, nextMode)) return;
  discoveryMode = nextMode;
  if (nextMode === "alltime" || nextMode === "recent") {
    clearDiscoveryTasteProfile();
    setTasteReference("", { syncUrl: true, refresh: false, defaultToMatch: false });
    DEAL_VIEW.sortBy = getDiscoveryDropSort();
    DEAL_VIEW.sortDir = getDefaultSortDir(DEAL_VIEW.sortBy);
  } else if (nextMode === "similar") {
    clearDiscoveryTasteProfile();
    if (tasteReferenceName) {
      DEAL_VIEW.sortBy = "match";
      DEAL_VIEW.sortDir = getDefaultSortDir("match");
    } else if (isProfileMatchSort(DEAL_VIEW.sortBy)) {
      DEAL_VIEW.sortBy = "pct";
      DEAL_VIEW.sortDir = getDefaultSortDir("pct");
    }
  } else if (nextMode === "flavour") {
    setTasteReference("", { syncUrl: true, refresh: false, defaultToMatch: false });
    if (hasDiscoveryTasteProfile()) {
      DEAL_VIEW.sortBy = "taste";
      DEAL_VIEW.sortDir = getDefaultSortDir("taste");
    }
  }
  DEAL_VIEW.page = 1;
  if (refresh) refreshDiscoveryBrowse();
}

function setTasteReference(
  requestedName,
  { syncUrl = true, refresh = true, defaultToMatch = true } = {},
) {
  const resolvedName = resolveCatalogProductName(requestedName);
  const nextName = resolvedName && getTasteReferenceProfile(resolvedName) ? resolvedName : "";
  const changed = tasteReferenceName !== nextName;

  if (!resolvedName) tasteReferenceError = "";
  if (resolvedName && !nextName) {
    tasteReferenceError = `“${resolvedName}” does not yet have enough tasting-note coverage for similarity search.`;
  }

  if (nextName) {
    discoveryMode = "similar";
    clearDiscoveryTasteProfile();
    if (!tasteReferenceName && !isProfileMatchSort(DEAL_VIEW.sortBy)) {
      tasteReferencePreviousSort = {
        sortBy: DEAL_VIEW.sortBy,
        sortDir: DEAL_VIEW.sortDir,
      };
    }
    tasteReferenceName = nextName;
    tasteReferenceError = "";
    if (defaultToMatch && (changed || !isProfileMatchSort(DEAL_VIEW.sortBy))) {
      DEAL_VIEW.sortBy = "match";
      DEAL_VIEW.sortDir = getDefaultSortDir("match");
    }
  } else {
    tasteReferenceName = "";
    if (isProfileMatchSort(DEAL_VIEW.sortBy)) {
      const fallbackSort = tasteReferencePreviousSort?.sortBy;
      DEAL_VIEW.sortBy = isValidDealSort(fallbackSort) && !isProfileMatchSort(fallbackSort)
        ? fallbackSort
        : "pct";
      DEAL_VIEW.sortDir = isValidSortDir(tasteReferencePreviousSort?.sortDir)
        ? tasteReferencePreviousSort.sortDir
        : getDefaultSortDir(DEAL_VIEW.sortBy);
    }
    tasteReferencePreviousSort = null;
  }

  DEAL_VIEW.page = 1;
  if (syncUrl) syncTasteReferenceUrl(tasteReferenceName);
  renderDiscoveryModeUi();
  renderTasteReferenceUi();
  renderDiscoveryFlavourSummary();
  if (refresh) window.__refreshTasteReferenceBrowse?.();
  persistBrowseState();
  return !!tasteReferenceName;
}

function initializeTasteReferenceFromUrl() {
  const { requested, name } = readRequestedTasteReference();
  if (!requested) {
    if (isProfileMatchSort(DEAL_VIEW.sortBy)) {
      DEAL_VIEW.sortBy = "pct";
      DEAL_VIEW.sortDir = getDefaultSortDir("pct");
    }
    return false;
  }
  if (!name) {
    tasteReferenceError = `The requested reference bottle “${requested}” is not available.`;
    syncTasteReferenceUrl("");
    return false;
  }
  const activated = setTasteReference(name, { syncUrl: false, refresh: false, defaultToMatch: true });
  if (!activated) syncTasteReferenceUrl("");
  return activated;
}

function shouldRestoreBrowseState() {
  if (!isBrowsePage()) return false;
  try {
    return new URLSearchParams(window.location.search).get("restore") === "1";
  } catch {
    return false;
  }
}

function clearBrowseRestoreFlag() {
  if (!isBrowsePage()) return;
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("restore")) return;
    url.searchParams.delete("restore");
    window.history.replaceState(window.history.state, "", url);
  } catch {}
}

function readBrowseState() {
  try {
    const raw = sessionStorage.getItem(BROWSE_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === "object") ? parsed : null;
  } catch {
    return null;
  }
}

function snapshotBrowseState(overrides = {}) {
  const searchInput = document.getElementById("product-search");
  const searchQuery = (typeof overrides.searchQuery === "string")
    ? overrides.searchQuery
    : String(catalogueSearchFilter || searchInput?.value || "").trim();
  const scrollY = Number.isFinite(overrides.scrollY)
    ? Math.max(0, Math.round(overrides.scrollY))
    : Math.max(0, Math.round(window.scrollY || 0));

  return {
    savedAt: Date.now(),
    searchQuery,
    filters: Object.fromEntries(
      FACET_KEYS.map((key) => [key, Array.from(FILTERS[key] || [])])
    ),
    priceMin: FILTERS.priceMin,
    priceMax: FILTERS.priceMax,
    sortBy: DEAL_VIEW.sortBy,
    sortDir: DEAL_VIEW.sortDir,
    tasteReferenceName,
    discoveryMode,
    discoveryDropMetric,
    discoveryTasteLevels: Object.fromEntries(discoveryTasteLevels),
    page: DEAL_VIEW.page,
    scrollY,
  };
}

function persistBrowseState(overrides = {}) {
  if (!isBrowsePage() || !hasBrowseSurface()) return;
  try {
    sessionStorage.setItem(BROWSE_STATE_KEY, JSON.stringify(snapshotBrowseState(overrides)));
  } catch {}
}

function applyBrowseStateToControls(state, { wrap, input } = {}) {
  if (!state || typeof state !== "object" || !wrap || !input) return false;

  const explicitReference = readRequestedTasteReference();
  const savedTasteLevels = state.discoveryTasteLevels && typeof state.discoveryTasteLevels === "object"
    ? Object.entries(state.discoveryTasteLevels)
      .filter(([key, level]) => TASTE_AXES.some((axis) => axis.key === key) && Number(level) > 0)
    : (Array.isArray(state.discoveryTasteAxes)
      ? state.discoveryTasteAxes.map((key) => [key, 0.6])
      : []);
  const savedMode = normaliseDiscoveryMode(state.discoveryMode);
  const savedDropMetric = state.discoveryDropMetric === "value" ? "value" : "pct";
  if (!explicitReference.requested && savedTasteLevels.length) {
    clearDiscoveryTasteProfile();
    savedTasteLevels.forEach(([key, level]) => {
      discoveryTasteAxes.add(key);
      discoveryTasteLevels.set(key, Math.max(0, Math.min(1, Number(level) || 0)));
    });
    discoveryMode = "flavour";
  } else if (!explicitReference.requested && !tasteReferenceName && !tasteReferenceError && state.tasteReferenceName) {
    setTasteReference(state.tasteReferenceName, {
      syncUrl: true,
      refresh: false,
      defaultToMatch: false,
    });
  } else if (!tasteReferenceName) {
    discoveryMode = savedMode;
    if (discoveryMode === "alltime" || discoveryMode === "recent") {
      discoveryDropMetric = savedDropMetric;
    }
  }

  const filterState = (state.filters && typeof state.filters === "object") ? state.filters : {};
  wrap.querySelectorAll("input[data-filter-checkbox]").forEach((control) => {
    const key = control.getAttribute("data-filter-checkbox");
    const values = Array.isArray(filterState?.[key]) ? filterState[key].map((v) => String(v)) : [];
    control.checked = values.includes(String(control.value));
  });

  const minInput = document.getElementById("filter-price-min");
  const maxInput = document.getElementById("filter-price-max");
  if (minInput) minInput.value = (state.priceMin == null || state.priceMin === "") ? "" : String(state.priceMin);
  if (maxInput) maxInput.value = (state.priceMax == null || state.priceMax === "") ? "" : String(state.priceMax);

  input.value = String(state.searchQuery || "");

  if (state.sortBy === "recent") {
    DEAL_VIEW.sortBy = "recent_value";
  } else if (isValidDealSort(state.sortBy)) {
    DEAL_VIEW.sortBy = state.sortBy;
  }
  if (!hasActiveTasteSearch() && isProfileMatchSort(DEAL_VIEW.sortBy)) {
    DEAL_VIEW.sortBy = "pct";
  }
  if (!hasActiveTasteSearch()) syncDiscoveryDropStateForSort(DEAL_VIEW.sortBy);
  if (isValidSortDir(state.sortDir)) {
    DEAL_VIEW.sortDir = state.sortDir;
  } else {
    DEAL_VIEW.sortDir = getDefaultSortDir(DEAL_VIEW.sortBy);
  }

  const page = Number(state.page);
  DEAL_VIEW.page = (Number.isFinite(page) && page > 0) ? Math.floor(page) : 1;
  renderDiscoveryModeUi();
  renderTasteReferenceUi();
  renderDiscoveryFlavourSummary();
  return true;
}

function restoreBrowseScroll(scrollY) {
  const top = Number(scrollY);
  if (!Number.isFinite(top) || top <= 0) return;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      window.scrollTo({ top: Math.max(0, Math.floor(top)), behavior: "auto" });
    });
  });
}

function formatFacetValueLabel(key, value) {
  return formatMetadataValueLabel(key, value, {
    blankLabel: FACET_EMPTY_LABELS[key] || "Unspecified",
  });
}

function getMetaFacetValue(meta, key) {
  const metaKey = FACET_META_KEYS[key];
  if (!metaKey) return "";
  const value = meta?.[metaKey];
  return key === "age" ? normalizeAgeFacetValue(value) : cleanFacetValue(value);
}

function splitFacetListValue(value) {
  if (Array.isArray(value)) {
    return value.flatMap((item) => splitFacetListValue(item));
  }
  if (value && typeof value === "object") {
    return splitFacetListValue(value.normalized_value ?? value.value ?? value.label ?? "");
  }
  return String(value ?? "")
    .split(/\s*(?:,|;|\+|\/|&|\band\b)\s*/i)
    .map(cleanFacetValue)
    .filter(Boolean)
    .filter((item) => !/^\+\d+\s+more$/i.test(item));
}

function uniqueFacetValues(values) {
  const out = [];
  const seen = new Set();
  for (const value of values || []) {
    const text = cleanFacetValue(value);
    if (!text) continue;
    const key = text.casefold ? text.casefold() : text.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(text);
  }
  return out;
}

function getCaskComponentValues(meta, componentType) {
  if (!Array.isArray(meta?.cask_components)) return [];
  return uniqueFacetValues(
    meta.cask_components
      .filter((component) => cleanFacetValue(component?.component_type).toLowerCase() === componentType)
      .map((component) => component?.normalized_value)
      .flatMap((value) => splitFacetListValue(value))
  );
}

function getMetaFacetValues(meta, key) {
  if (key === "cask") {
    const hasStructuredComponents = Array.isArray(meta?.cask_components) && meta.cask_components.length > 0;
    const seasoningValues = getCaskComponentValues(meta, "seasoning");
    return hasStructuredComponents ? seasoningValues : uniqueFacetValues(splitFacetListValue(meta?.cask));
  }
  if (key === "wood") {
    return getCaskComponentValues(meta, "wood");
  }
  const value = getMetaFacetValue(meta, key);
  return value ? [value] : [];
}

function getSortDirectionSummary(sortBy, sortDir) {
  if (isProfileMatchSort(sortBy)) return sortDir === "desc" ? "closest first" : "furthest first";
  if (sortBy === "price") return sortDir === "asc" ? "low to high" : "high to low";
  if (sortBy === "name") return sortDir === "asc" ? "A to Z" : "Z to A";
  return sortDir === "asc" ? "ascending" : "descending";
}

function getSortDirectionButtonLabel(sortBy, sortDir) {
  const arrow = sortDir === "asc" ? "\u2191" : "\u2193";
  if (isProfileMatchSort(sortBy)) return `${arrow} ${sortDir === "desc" ? "Closest" : "Furthest"}`;
  if (sortBy === "name") return `${arrow} ${sortDir === "asc" ? "A-Z" : "Z-A"}`;
  if (sortBy === "price") return `${arrow} ${sortDir === "asc" ? "Asc" : "Desc"}`;
  return `${arrow} ${sortDir === "asc" ? "Asc" : "Desc"}`;
}

function getProductRetailerValues(name) {
  const out = new Set();
  const rows = getCatalogRetailerRows(name);
  for (const r of rows) {
    const retailer = cleanFacetValue(r?.retailer);
    if (retailer && hasValidCurrentPrice(r?.current_price)) out.add(retailer);
  }
  return Array.from(out);
}

function getSelectedRetailerNormSet() {
  const selected = FILTERS?.retailer || new Set();
  const out = new Set();
  for (const value of selected) {
    if (value === BLANK_FILTER_VALUE) continue;
    const normalized = norm(cleanFacetValue(value));
    if (normalized) out.add(normalized);
  }
  return out;
}

function getBestRetailerRow(name, retailerNorms = null) {
  let rows = getCatalogRetailerRows(name).filter((r) => hasValidCurrentPrice(r?.current_price));
  if (retailerNorms && retailerNorms.size) {
    rows = rows.filter((r) => retailerNorms.has(norm(cleanFacetValue(r?.retailer))));
  }
  if (!rows.length) return null;
  rows.sort((a, b) => Number(a.current_price) - Number(b.current_price));
  return rows[0];
}

function getCurrentMinPrice(name, retailerNorms = null){
  if (retailerNorms && retailerNorms.size) {
    const best = getBestRetailerRow(name, retailerNorms);
    if (best) return Number(best.current_price);
    return null;
  }
  const summary = getCatalogSummary(name);
  return (summary && summary.current_min != null) ? Number(summary.current_min) : null;
}

function getFacets(){
  const facetState = Object.fromEntries(
    FACET_KEYS.map((key) => [key, { values: new Set(), hasBlank: false }])
  );
  const facetNames = (products && products.length)
    ? products
    : [];
  for (const name of facetNames){
    const m = getCatalogMeta(name);
    const retailerValues = getProductRetailerValues(name);
    if (retailerValues.length) {
      for (const retailerName of retailerValues) facetState.retailer.values.add(retailerName);
    } else {
      facetState.retailer.hasBlank = true;
    }
    for (const key of FACET_KEYS) {
      if (key === "retailer") continue;
      const values = getMetaFacetValues(m, key);
      if (values.length) {
        for (const value of values) facetState[key].values.add(value);
      } else {
        facetState[key].hasBlank = true;
      }
    }
  }
  const sortAZ = (a,b) => String(a).localeCompare(String(b));
  const sortAge = (a,b) => {
    const an = Number(a);
    const bn = Number(b);
    const aNum = Number.isFinite(an);
    const bNum = Number.isFinite(bn);
    if (aNum && bNum) return an - bn;
    if (aNum) return -1;
    if (bNum) return 1;
    return String(a).localeCompare(String(b));
  };
  return Object.fromEntries(
    FACET_KEYS.map((key) => [
      key,
      {
        values: Array.from(facetState[key].values).sort(key === "age" ? sortAge : sortAZ),
        hasBlank: facetState[key].hasBlank,
      },
    ])
  );
}

function facetPasses(selectedSet, rawValues) {
  if (!selectedSet || selectedSet.size === 0) return true;
  const values = Array.isArray(rawValues)
    ? rawValues.map(cleanFacetValue).filter(Boolean)
    : [cleanFacetValue(rawValues)].filter(Boolean);
  if (!values.length) return selectedSet.has(BLANK_FILTER_VALUE);
  return values.some((value) => selectedSet.has(value));
}

function retailerFacetPasses(name, selectedRetailerNorms = null) {
  const selectedSet = FILTERS.retailer;
  if (!selectedSet || selectedSet.size === 0) return true;

  const norms = selectedRetailerNorms || getSelectedRetailerNormSet();
  if (!norms.size) return selectedSet.has(BLANK_FILTER_VALUE);

  const rows = getCatalogRetailerRows(name);
  return rows.some((r) => {
    const retailerNorm = norm(cleanFacetValue(r?.retailer));
    return norms.has(retailerNorm) && hasValidCurrentPrice(r?.current_price);
  });
}

function matchesFilters(name){
  const selectedRetailerNorms = getSelectedRetailerNormSet();
  const m = getCatalogMeta(name);
  if (!retailerFacetPasses(name, selectedRetailerNorms)) return false;
  for (const key of FACET_KEYS) {
    if (key === "retailer") continue;
    if (!facetPasses(FILTERS[key], getMetaFacetValues(m, key))) return false;
  }

  const p = getCurrentMinPrice(name, selectedRetailerNorms);
  if (FILTERS.priceMin != null && (p == null || p < FILTERS.priceMin)) return false;
  if (FILTERS.priceMax != null && (p == null || p > FILTERS.priceMax)) return false;
  return true;
}

function dealSortValue(v, fallback = Number.NEGATIVE_INFINITY) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function compareMaybeNumeric(a, b, sortDir = "asc") {
  const aNum = Number(a);
  const bNum = Number(b);
  const aValid = Number.isFinite(aNum);
  const bValid = Number.isFinite(bNum);
  if (!aValid && !bValid) return 0;
  if (!aValid) return 1;
  if (!bValid) return -1;
  return sortDir === "asc" ? (aNum - bNum) : (bNum - aNum);
}

function compareMaybeText(a, b, sortDir = "asc") {
  const aa = String(a ?? "").trim();
  const bb = String(b ?? "").trim();
  const diff = aa.localeCompare(bb);
  return sortDir === "asc" ? diff : -diff;
}

function compareDealRows(a, b, sortBy = "pct", sortDir = null) {
  const dir = isValidSortDir(sortDir) ? sortDir : getDefaultSortDir(sortBy);
  const nameDiff = compareMaybeText(a?.product, b?.product, "asc");
  const priceDiff = compareMaybeNumeric(a?.current_lowest, b?.current_lowest, "asc");
  const valueDiff = compareMaybeNumeric(a?.drop_value, b?.drop_value, "desc");
  const pctDiff = compareMaybeNumeric(a?.drop_pct, b?.drop_pct, "desc");
  const recentValueDiff = compareMaybeNumeric(a?.recent_drop_value, b?.recent_drop_value, "desc");
  const recentPctDiff = compareMaybeNumeric(a?.recent_drop_pct, b?.recent_drop_pct, "desc");
  const matchDiff = compareMaybeNumeric(a?.match_score, b?.match_score, dir);
  const tasteDiff = compareMaybeNumeric(a?.taste_score, b?.taste_score, dir);

  if (sortBy === "match") {
    return matchDiff || compareMaybeNumeric(a?.taste_score, b?.taste_score, "desc") || priceDiff || nameDiff;
  }
  if (sortBy === "taste") {
    return tasteDiff || compareMaybeNumeric(a?.match_score, b?.match_score, "desc") || priceDiff || nameDiff;
  }

  if (sortBy === "price") {
    return compareMaybeNumeric(a?.current_lowest, b?.current_lowest, dir) || valueDiff || pctDiff || nameDiff;
  }
  if (sortBy === "pct") {
    return compareMaybeNumeric(a?.drop_pct, b?.drop_pct, dir) || valueDiff || priceDiff || nameDiff;
  }
  if (sortBy === "recent_pct") {
    return compareMaybeNumeric(a?.recent_drop_pct, b?.recent_drop_pct, dir) || recentValueDiff || priceDiff || nameDiff;
  }
  if (sortBy === "recent_value") {
    return compareMaybeNumeric(a?.recent_drop_value, b?.recent_drop_value, dir) || recentPctDiff || priceDiff || nameDiff;
  }
  if (sortBy === "name") {
    return compareMaybeText(a?.product, b?.product, dir) || priceDiff || valueDiff;
  }
  return compareMaybeNumeric(a?.drop_value, b?.drop_value, dir) || pctDiff || priceDiff || nameDiff;
}

function buildGlobalDealRow(name) {
  const summary = getCatalogSummary(name) || {};
  const d = getCatalogDeal(name);
  const baseline = Number(d.baseline_price);
  const current = Number(d.current_lowest);
  const hasBaseline = Number.isFinite(baseline) && baseline > 0;
  const hasCurrent = Number.isFinite(current) && current > 0;
  const fallbackCurrent = Number(summary.current_min);
  const currentPrice = hasCurrent ? current : (Number.isFinite(fallbackCurrent) ? fallbackCurrent : null);
  if (!Number.isFinite(currentPrice) || currentPrice <= 0) return null;

  const dropValue = Number.isFinite(Number(d.drop_value))
    ? Number(d.drop_value)
    : (hasBaseline ? (baseline - currentPrice) : null);
  const dropPct = Number.isFinite(Number(d.drop_pct))
    ? Number(d.drop_pct)
    : ((hasBaseline && dropValue != null) ? ((dropValue / baseline) * 100) : null);

  const low12m = Number(d.alltime_low);
  const distFromLowPct = Number.isFinite(Number(d.dist_from_low_pct))
    ? Number(d.dist_from_low_pct)
    : ((Number.isFinite(low12m) && low12m > 0) ? (((currentPrice - low12m) / low12m) * 100) : null);

  return {
    __mode: "filtered_discounts",
    product: name,
    baseline_price: hasBaseline ? baseline : null,
    alltime_low: Number.isFinite(low12m) ? low12m : null,
    current_lowest: currentPrice,
    retailer: d.retailer || summary.current_min_retailer || null,
    url: d.url || null,
    drop_value: dropValue,
    drop_pct: dropPct,
    is_new_low: !!d.is_new_low,
    dist_from_low_pct: distFromLowPct,
    is_strong_deal: !!(dropValue != null && dropValue > 0 && dropPct != null && dropPct >= STRONG_DEAL_MIN_DROP_PCT),
  };
}

function rankDealsForNames(names, limit = 5, sortBy = "pct", sortDir = null) {
  const rows = (names || [])
    .map((name) => buildGlobalDealRow(name))
    .filter(Boolean)
    .sort((a, b) => compareDealRows(a, b, sortBy, sortDir));
  return rows.slice(0, limit);
}

function buildAlltimePriceDropRow(name, retailerNorms = new Set()) {
  const alltime = alltimePriceDropsByProduct.get(name);
  if (!alltime) return null;

  const current = Number(alltime.current_lowest);
  const baseline = Number(alltime.baseline_price);
  const dropValue = Number(alltime.drop_value);
  const dropPct = Number(alltime.drop_pct);
  const retailer = String(alltime.retailer || "").trim();
  if (!Number.isFinite(current) || current <= 0 || !Number.isFinite(baseline) || baseline <= 0 || !Number.isFinite(dropValue) || dropValue <= 0) {
    return null;
  }
  if (retailerNorms.size && !retailerNorms.has(norm(retailer))) return null;

  return {
    __mode: "alltime_price_drops",
    product: name,
    baseline_price: baseline,
    current_lowest: current,
    retailer: retailer || null,
    url: alltime.url || null,
    drop_value: dropValue,
    drop_pct: Number.isFinite(dropPct) ? dropPct : ((dropValue / baseline) * 100),
    alltime_drop: true,
  };
}

function rankAlltimePriceDropsForNames(names, retailerNorms, limit = 5, sortBy = "value", sortDir = null) {
  const rows = (names || [])
    .map((name) => buildAlltimePriceDropRow(name, retailerNorms))
    .filter(Boolean)
    .sort((a, b) => compareDealRows(a, b, sortBy, sortDir));
  return rows.slice(0, limit);
}

function buildRecentPriceDropRow(name, retailerNorms = new Set()) {
  const recent = recentPriceDropsByProduct.get(name);
  if (!recent) return null;

  const current = Number(recent.current_lowest);
  const previous = Number(recent.previous_lowest);
  const dropValue = Number(recent.drop_value);
  const dropPct = Number(recent.drop_pct);
  const retailer = String(recent.retailer || "").trim();
  if (!Number.isFinite(current) || current <= 0 || !Number.isFinite(previous) || previous <= 0 || !Number.isFinite(dropValue) || dropValue <= 0) {
    return null;
  }
  if (retailerNorms.size && !retailerNorms.has(norm(retailer))) return null;

  return {
    __mode: "recent_price_drops",
    product: name,
    baseline_price: previous,
    current_lowest: current,
    retailer: retailer || null,
    url: recent.url || null,
    drop_value: dropValue,
    drop_pct: Number.isFinite(dropPct) ? dropPct : ((dropValue / previous) * 100),
    recent_drop_value: dropValue,
    recent_drop_pct: Number.isFinite(dropPct) ? dropPct : ((dropValue / previous) * 100),
    period_days: Number(recent.period_days) || 7,
  };
}

function rankRecentPriceDropsForNames(names, retailerNorms, limit = 5, sortBy = "recent_value", sortDir = null) {
  const rows = (names || [])
    .map((name) => buildRecentPriceDropRow(name, retailerNorms))
    .filter(Boolean)
    .sort((a, b) => compareDealRows(a, b, sortBy, sortDir));
  return rows.slice(0, limit);
}

function getVerifiedPriceMovement(movements, name, retailerNorms = new Set()) {
  const movement = movements.get(name);
  if (!movement) return null;

  const current = Number(movement.current_lowest);
  const previous = Number(movement.previous_highest ?? movement.previous_lowest);
  const dropValue = Number(movement.drop_value);
  const retailer = String(movement.retailer || "").trim();
  if (!Number.isFinite(current) || current <= 0 || !Number.isFinite(previous) || previous <= 0 || !Number.isFinite(dropValue)) {
    return null;
  }
  if (retailerNorms.size && !retailerNorms.has(norm(retailer))) return null;
  return movement;
}

function buildDiscoveryResultRow(name, retailerNorms = new Set(), sortBy = DEAL_VIEW.sortBy) {
  const base = retailerNorms.size
    ? buildRetailerScopedDeal(name, retailerNorms)
    : buildGlobalDealRow(name);
  if (!base && retailerNorms.size) return null;

  const deal = getCatalogDeal(name);
  const summary = getCatalogSummary(name) || {};
  const row = base ? { ...base } : {
    __mode: "catalogue",
    product: name,
    baseline_price: null,
    alltime_low: Number.isFinite(Number(deal.alltime_low)) ? Number(deal.alltime_low) : null,
    current_lowest: null,
    retailer: deal.retailer || summary.current_min_retailer || null,
    url: deal.url || null,
    drop_value: null,
    drop_pct: null,
    is_new_low: false,
    dist_from_low_pct: null,
    is_strong_deal: false,
  };

  const alltime = getVerifiedPriceMovement(alltimePriceDropsByProduct, name, retailerNorms);
  const recent = getVerifiedPriceMovement(recentPriceDropsByProduct, name, retailerNorms);
  if ((sortBy === "pct" || sortBy === "value") && alltime) {
    const baseline = Number(alltime.baseline_price);
    const dropValue = Number(alltime.drop_value);
    row.baseline_price = baseline;
    row.current_lowest = Number(alltime.current_lowest);
    row.retailer = alltime.retailer || row.retailer;
    row.url = alltime.url || row.url;
    row.drop_value = dropValue;
    row.drop_pct = Number.isFinite(Number(alltime.drop_pct))
      ? Number(alltime.drop_pct)
      : (dropValue / baseline) * 100;
    row.alltime_drop = dropValue > 0;
  }
  if ((sortBy === "recent_pct" || sortBy === "recent_value") && recent) {
    const previous = Number(recent.previous_lowest);
    const dropValue = Number(recent.drop_value);
    row.baseline_price = previous;
    row.current_lowest = Number(recent.current_lowest);
    row.retailer = recent.retailer || row.retailer;
    row.url = recent.url || row.url;
    row.drop_value = dropValue;
    row.drop_pct = Number.isFinite(Number(recent.drop_pct))
      ? Number(recent.drop_pct)
      : (dropValue / previous) * 100;
    row.recent_drop_value = dropValue;
    row.recent_drop_pct = row.drop_pct;
    row.period_days = Number(recent.period_days) || 7;
  } else if (recent) {
    const previous = Number(recent.previous_lowest);
    const dropValue = Number(recent.drop_value);
    row.recent_drop_value = dropValue;
    row.recent_drop_pct = Number.isFinite(Number(recent.drop_pct))
      ? Number(recent.drop_pct)
      : (dropValue / previous) * 100;
  }

  return row;
}

function rankDiscoveryRows(names, retailerNorms, limit = 5, sortBy = "pct", sortDir = null) {
  return (names || [])
    .map((name) => buildDiscoveryResultRow(name, retailerNorms, sortBy))
    .filter(Boolean)
    .sort((a, b) => compareDealRows(a, b, sortBy, sortDir))
    .slice(0, limit);
}

const STRONG_DEAL_MIN_DROP_PCT = 2;

function buildRetailerScopedDeal(name, retailerNorms) {
  const bestRow = getBestRetailerRow(name, retailerNorms);
  if (!bestRow) return null;

  const currentPrice = Number(bestRow.current_price);
  if (!Number.isFinite(currentPrice)) return null;

  const d = getCatalogDeal(name);
  const baseline = Number(d.baseline_price);
  const low12m = Number(d.alltime_low);
  const hasBaseline = Number.isFinite(baseline) && baseline > 0;
  const hasLow12m = Number.isFinite(low12m) && low12m > 0;

  const dropValue = hasBaseline ? (baseline - currentPrice) : null;
  const dropPct = (hasBaseline && dropValue != null) ? (dropValue / baseline) * 100 : null;
  const distFromLowPct = hasLow12m ? ((currentPrice - low12m) / low12m) * 100 : null;

  return {
    __mode: "retailer_focus",
    product: name,
    baseline_price: hasBaseline ? baseline : null,
    alltime_low: hasLow12m ? low12m : null,
    current_lowest: currentPrice,
    retailer: bestRow.retailer || null,
    url: bestRow.url || null,
    drop_value: (dropValue == null) ? null : dropValue,
    drop_pct: (dropPct == null) ? null : dropPct,
    is_new_low: false,
    dist_from_low_pct: (distFromLowPct == null) ? null : distFromLowPct,
    is_strong_deal: !!(dropValue != null && dropValue > 0 && dropPct != null && dropPct >= STRONG_DEAL_MIN_DROP_PCT),
  };
}

function searchProducts(q, { applyFilters = true, resultLimit = 200 } = {}) {
  const nq = norm(q);
  const pool = applyFilters ? SEARCH_ROWS.filter((r) => matchesFilters(r.name)) : SEARCH_ROWS;
  if (!nq) return pool.map((r) => r.name).sort((a, b) => a.localeCompare(b));

  const qTokens = nq.split(' ').filter(Boolean);
  const scored = pool.map((p) => {
    const exact = p.n === nq ? 12 : 0;
    const prefix = p.n.startsWith(nq) ? 8 : 0;
    const nameSubstr = p.n.includes(nq) ? 5 : 0;
    let tokenPrefixHits = 0;
    let tokenContainsHits = 0;
    let matchedTokens = 0;
    for (const t of qTokens) {
      if (p.nameTokens.some((pt) => pt.startsWith(t))) {
        tokenPrefixHits += 2;
        matchedTokens += 1;
      } else if (t.length >= 3 && p.nameTokens.some((pt) => pt.includes(t))) {
        tokenContainsHits += 0.75;
        matchedTokens += 1;
      }
    }
    const sim = (nq.length >= 4) ? dice(nq, p.n) : 0;
    const hasDirectMatch = !!(exact || prefix || nameSubstr || matchedTokens);
    const strongFuzzyMatch = !hasDirectMatch && sim >= 0.82;
    const hasFullTokenCoverage = qTokens.length <= 1 || matchedTokens === qTokens.length;
    if (!hasDirectMatch && !strongFuzzyMatch) return null;
    if (!strongFuzzyMatch && qTokens.length > 1 && !exact && !prefix && !nameSubstr && !hasFullTokenCoverage) return null;

    const score = exact + prefix + nameSubstr + tokenPrefixHits + tokenContainsHits + sim;
    return {
      name: p.name,
      score,
      exact,
      prefix,
      matchedTokens,
    };
  }).filter(Boolean);

  scored.sort((a, b) =>
    (b.score - a.score)
    || (b.exact - a.exact)
    || (b.prefix - a.prefix)
    || (b.matchedTokens - a.matchedTokens)
    || (a.name.length - b.name.length)
    || a.name.localeCompare(b.name)
  );
  return scored.slice(0, resultLimit).map((x) => x.name);
}

async function ensureSearchReady() {
  if (SEARCH_ROWS.length) return;
  await loadCatalog();
  rebuildSearchIndex();
}

function fmtZAR(v) {
    if (v == null) return '-';
    try { return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 2 }).format(v); }
    catch { return 'R' + Number(v).toFixed(2); }
}

function fmtDeltaZAR(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (Math.abs(n) < 0.005) return 'Best';
  return `+${fmtZAR(n).replace(/\s+/g, '')}`;
}

function formatMetricNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  try {
    return new Intl.NumberFormat("en-ZA", { maximumFractionDigits: 0 }).format(n);
  } catch {
    return String(Math.round(n));
  }
}

function formatDataVersionDate(version) {
  const text = String(version || "").trim();
  const match = text.match(/^(\d{4})(\d{2})(\d{2})/);
  if (!match) return "";
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return "";
  const date = new Date(Date.UTC(year, month - 1, day));
  try {
    return new Intl.DateTimeFormat("en-ZA", {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }).format(date);
  } catch {
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }
}

function setTextById(id, value) {
  const el = document.getElementById(id);
  if (el) el.textContent = value;
}

function updateLandingMetrics() {
  if (!document.getElementById("metric-bottles")) return;

  const items = Array.isArray(CATALOG_ITEMS) ? CATALOG_ITEMS : [];
  const totalBottles = Number(CATALOG?.count) || items.length || products.length;
  const retailers = new Set();
  let strongDeals = 0;

  for (const item of items) {
    if (item?.deal?.is_strong_deal) strongDeals += 1;
    if (Array.isArray(item?.retailers)) {
      item.retailers.forEach((retailer) => {
        const name = String(retailer || "").trim();
        if (name) retailers.add(name);
      });
    }
    if (Array.isArray(item?.retailer_rows)) {
      item.retailer_rows.forEach((row) => {
        const name = String(row?.retailer || "").trim();
        if (name) retailers.add(name);
      });
    }
  }

  setTextById("metric-bottles", formatMetricNumber(totalBottles));
  setTextById("metric-retailers", formatMetricNumber(retailers.size));
  setTextById("metric-deals", formatMetricNumber(strongDeals || top5_deals.length));
  setTextById("metric-updated", formatDataVersionDate(dataVersion) || "Daily");
}

function formatHeroTrendPct(value, digits = 1) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "--";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(digits)}%`;
}

function buildHeroTrendPath(points, width, height, padding) {
  if (!Array.isArray(points) || points.length < 2) return "";

  const values = points
    .map((point) => Number(point?.value))
    .filter((value) => Number.isFinite(value));
  if (values.length < 2) return "";

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(max - min, 0.01);
  const usableW = width - padding * 2;
  const usableH = height - padding * 2;

  const coords = points
    .map((point, index) => {
      const value = Number(point?.value);
      if (!Number.isFinite(value)) return null;
      const x = padding + (usableW * index) / Math.max(points.length - 1, 1);
      const y = padding + usableH - ((value - min) / range) * usableH;
      return { x, y };
    })
    .filter(Boolean);

  if (coords.length < 2) return "";

  let d = `M ${coords[0].x.toFixed(2)} ${coords[0].y.toFixed(2)}`;
  for (let i = 0; i < coords.length - 1; i += 1) {
    const p0 = coords[i - 1] || coords[i];
    const p1 = coords[i];
    const p2 = coords[i + 1];
    const p3 = coords[i + 2] || p2;
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x.toFixed(2)} ${c1y.toFixed(2)}, ${c2x.toFixed(2)} ${c2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }
  return d;
}

function renderLandingTrend() {
  const root = document.getElementById("hero-trend");
  if (!root) return;

  const points = Array.isArray(heroTrend?.points) ? heroTrend.points : [];
  const path = buildHeroTrendPath(points, 720, 220, 18);
  if (!path) {
    root.classList.add("is-hidden");
    return;
  }

  const valueEl = document.getElementById("hero-trend-value");
  const subEl = document.getElementById("hero-trend-sub");
  const svg = document.getElementById("hero-trend-svg");
  const periodDays = Number(heroTrend?.period_days);
  const avgDaily = heroTrend?.avg_daily_change_pct;

  if (valueEl) valueEl.textContent = formatHeroTrendPct(heroTrend?.change_pct, 1);
  if (subEl) {
    const period = Number.isFinite(periodDays) && periodDays > 1 ? `${Math.round(periodDays)} days` : "the latest period";
    subEl.textContent = `${formatHeroTrendPct(avgDaily, 2)} per day across tracked bottles, ${period}`;
  }
  if (svg) {
    svg.innerHTML = `
      <path class="hero-trend__glow" d="${path}"></path>
      <path class="hero-trend__line" d="${path}"></path>
    `;
  }

  const label = valueEl?.textContent || "average price change";
  root.setAttribute("aria-label", `Average whisky price movement ${label}`);
  root.classList.remove("is-hidden");
}

function formatIntelligencePct(value, digits = 1) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return `${Math.abs(n).toFixed(digits)}%`;
}

function getIntelligenceMetaLine(card) {
  const name = String(card?.product || "");
  const meta = getCatalogMeta(name) || {};
  const parts = [
    meta.production_style || meta.whisky_family || meta.brand,
    card?.retailer,
  ].map((value) => String(value || "").trim()).filter(Boolean);
  return parts.slice(0, 2).join(" | ");
}

function getIntelligenceMetric(card) {
  const id = String(card?.id || "");
  if (id === "biggest-rand-drop") {
    return `${fmtZAR(card?.drop_value)} drop`;
  }
  if (card?.kind === "recent") {
    return `-${formatIntelligencePct(card?.drop_pct, 1)} in ${Number(card?.period_days) || 7} days`;
  }
  return `${formatIntelligencePct(card?.drop_pct, 1)} below usual low`;
}

function getIntelligenceDetail(card) {
  const id = String(card?.id || "");
  if (card?.kind === "recent") {
    return card?.previous_price != null ? `Was ${fmtZAR(card.previous_price)}` : "Seven-day tracked movement";
  }
  if (id === "best-under-1000") {
    return `Under R1000${card?.baseline_price != null ? ` | usual low ${fmtZAR(card.baseline_price)}` : ""}`;
  }
  return card?.baseline_price != null ? `Usual low ${fmtZAR(card.baseline_price)}` : "Lowest relative to usual low";
}

function renderLandingIntelligence() {
  const section = document.getElementById("landing-intelligence");
  const host = document.getElementById("landing-intelligence-cards");
  if (!section || !host) return;

  const cards = (Array.isArray(landingIntelligenceCards) ? landingIntelligenceCards : [])
    .filter((card) => card && productNameSet.has(String(card.product || "")))
    .slice(0, 4);

  if (!cards.length) {
    section.hidden = true;
    return;
  }

  section.hidden = false;
  host.innerHTML = cards.map((card, index) => {
    const name = String(card.product || "");
    const img = getCatalogImage(name);
    const href = buildProductPageUrl(name);
    const title = String(card.title || "Bottle intelligence");
    const metaLine = getIntelligenceMetaLine(card);
    const metric = getIntelligenceMetric(card);
    const detail = getIntelligenceDetail(card);
    const price = fmtZAR(card.current_price);
    const rank = String(index + 1).padStart(2, "0");

    return `
      <a class="intelligence-card" href="${escHtml(href)}" data-product-name="${escHtml(name)}" aria-label="Open product page for ${escHtml(name)}">
        <div class="intelligence-card__label"><span>${rank}</span>${escHtml(title)}</div>
        <div class="intelligence-card__body">
          <div class="intelligence-card__media">
            ${img ? `<img src="${escHtml(img)}" alt="${escHtml(name)}" loading="lazy">` : `<span aria-hidden="true"></span>`}
          </div>
          <div class="intelligence-card__copy">
            <h3>${escHtml(name)}</h3>
            ${metaLine ? `<p class="intelligence-card__meta">${escHtml(metaLine)}</p>` : ""}
            <div class="intelligence-card__price">${escHtml(price)}</div>
            <div class="intelligence-card__metric">${escHtml(metric)}</div>
            <div class="intelligence-card__detail">${escHtml(detail)}</div>
          </div>
        </div>
      </a>
    `;
  }).join("");

  if (!window.__landingIntelligenceClickInit) {
    window.__landingIntelligenceClickInit = true;
    host.addEventListener("click", (e) => {
      const link = e.target.closest(".intelligence-card[href]");
      if (!link) return;
      persistBrowseState({ scrollY: window.scrollY });
    });
  }
}

// ---- Whisky shelf (browse panel) ----
function formatDealDelta(row) {
  const dropValue = Number(row?.drop_value);
  const dropPct = Number(row?.drop_pct);
  const hasDropValue = Number.isFinite(dropValue);
  const hasDropPct = Number.isFinite(dropPct);
  const periodDays = Number(row?.period_days);

  if (Number.isFinite(periodDays) && periodDays > 0 && hasDropValue && dropValue > 0) {
    const pctLabel = hasDropPct ? ` (${dropPct.toFixed(2)}%)` : "";
    return `Dropped ${fmtZAR(dropValue)}${pctLabel} in ${Math.round(periodDays)} days`;
  }

  if (row?.alltime_drop && hasDropValue && dropValue > 0) {
    const pctLabel = hasDropPct ? ` (${dropPct.toFixed(2)}%)` : "";
    return `Dropped ${fmtZAR(dropValue)}${pctLabel} below its usual low`;
  }

  if (hasDropValue && dropValue > 0) {
    const pctLabel = hasDropPct ? `${dropPct.toFixed(2)}%` : "";
    return `Save ${fmtZAR(dropValue)}${pctLabel ? ` (${pctLabel})` : ""}`;
  }
  if (hasDropValue && dropValue < 0) {
    const pctLabel = hasDropPct ? `${Math.abs(dropPct).toFixed(2)}%` : "";
    return `${fmtZAR(Math.abs(dropValue))}${pctLabel ? ` (${pctLabel})` : ""} above usual low`;
  }
  if (hasDropPct) {
    return `${dropPct.toFixed(2)}% vs usual low`;
  }
  return "No baseline deal signal yet";
}

function formatDiscoveryTagValue(key, value) {
  return formatMetadataValueLabel(key, value);
}

function getDiscoveryProductTags(name, row = null) {
  const meta = getCatalogMeta(name) || {};
  const tags = [];
  const add = (key, value) => {
    const text = formatDiscoveryTagValue(key, value);
    if (!text) return;
    if (tags.some((tag) => norm(tag.label) === norm(text))) return;
    tags.push({ key, label: text });
  };

  add("peat", meta.peated_state);
  add("cask", meta.cask);
  add("style", meta.production_style || meta.whisky_family);
  add("region", meta.region || meta.country);
  add("age", meta.age);

  const price = Number(row?.current_lowest ?? getCatalogSummary(name)?.current_min);
  if (Number.isFinite(price) && price > 0 && price <= 1000) add("price", "Under R1000");

  return tags.slice(0, 4);
}

function getDiscoveryWhy(row) {
  const name = String(row?.product || "");
  const tags = getDiscoveryProductTags(name, row).map((tag) => tag.label);
  const delta = formatDealDelta(row);
  if (tags.length >= 2) return `${tags[0]} profile with ${tags[1].toLowerCase()} character. ${delta}.`;
  if (tags.length === 1) return `${tags[0]} profile. ${delta}.`;
  return delta;
}

function formatOverallMatchStrength(score, { tasteTarget = false } = {}) {
  const value = Number(score);
  if (!Number.isFinite(value)) return tasteTarget ? "Flavour profile fit" : "Profile match";
  if (tasteTarget) {
    if (value >= 0.9) return "Exceptional flavour fit";
    if (value >= 0.82) return "Strong flavour fit";
    if (value >= 0.72) return "Good flavour fit";
    return "Related flavour fit";
  }
  if (value >= 0.9) return "Exceptional overall match";
  if (value >= 0.82) return "Strong overall match";
  if (value >= 0.72) return "Good overall match";
  return "Related overall profile";
}

function getFamiliarMatchReasons(match, limit = 3) {
  return (Array.isArray(match?.reasons) ? match.reasons : [])
    .map((reason) => String(reason?.label || "").trim())
    .filter(Boolean)
    .slice(0, limit);
}

function renderTasteReferenceUi() {
  const selected = document.getElementById("taste-reference-selected");
  const status = document.getElementById("taste-reference-status");
  const summary = document.getElementById("taste-reference-summary");
  const input = document.getElementById("taste-reference-input");
  const browseTitle = document.querySelector(".browse-dock__title");
  const referenceProfile = getTasteReferenceProfile(tasteReferenceName);
  syncDiscoveryOrderControl();

  if (status) {
    status.hidden = !tasteReferenceError;
    status.textContent = tasteReferenceError;
  }

  if (!tasteReferenceName || !referenceProfile) {
    if (selected) {
      selected.hidden = true;
      selected.innerHTML = "";
    }
    if (summary) {
      summary.hidden = true;
      summary.innerHTML = "";
    }
    if (input) input.placeholder = "Choose a reference bottle...";
    if (browseTitle && !hasDiscoveryTasteProfile()) {
      const mode = getDiscoveryModeCopy();
      browseTitle.textContent = mode.resultsTitle;
    }
    return;
  }

  const leaders = getTasteProfileLeaders(referenceProfile, 3).map((axis) => axis.label);
  const tags = getDiscoveryProductTags(tasteReferenceName).map((tag) => tag.label).slice(0, 3);
  const imageUrl = getCatalogImage(tasteReferenceName);
  const referenceUrl = buildProductPageUrl(tasteReferenceName);
  const chips = [...leaders, ...tags]
    .filter((label, index, values) => values.findIndex((value) => norm(value) === norm(label)) === index)
    .slice(0, 5);

  if (selected) {
    selected.hidden = false;
    selected.innerHTML = `
      <span><strong>${escHtml(tasteReferenceName)}</strong></span>
      <button type="button" data-taste-reference-clear aria-label="Clear ${escHtml(tasteReferenceName)} as the reference bottle">Clear</button>
    `;
  }
  if (input) input.placeholder = "Change reference bottle...";

  if (summary) {
    summary.hidden = false;
    summary.innerHTML = `
      ${imageUrl ? `<div class="taste-reference-summary__media"><img src="${escHtml(imageUrl)}" alt="" loading="lazy"></div>` : ""}
      <div class="taste-reference-summary__body">
        <h2>Finding whiskies like ${escHtml(tasteReferenceName)}</h2>
        ${chips.length ? `<div class="taste-reference-summary__chips">${chips.map((chip) => `<span>${escHtml(chip)}</span>`).join("")}</div>` : ""}
      </div>
      <div class="taste-reference-summary__actions">
        <a href="${escHtml(referenceUrl)}">View bottle</a>
        <button type="button" data-taste-reference-clear>Clear reference</button>
      </div>
    `;
  }
  if (browseTitle) browseTitle.textContent = `Closest matches to ${tasteReferenceName}`;
  if (browseSub) browseSub.textContent = "Use filters to narrow the comparable bottles, then rank by overall context or taste alone.";
}

function bindTasteReferencePickerOnce() {
  const input = document.getElementById("taste-reference-input");
  const menu = document.getElementById("taste-reference-menu");
  const toggle = document.getElementById("taste-reference-toggle");
  const combo = document.getElementById("taste-reference-combo");
  if (!input || !menu || !toggle || !combo || combo.dataset.bound === "true") return;
  combo.dataset.bound = "true";

  let activeIndex = -1;
  let matches = [];
  const closeMenu = () => {
    menu.classList.add("hidden");
    input.setAttribute("aria-expanded", "false");
  };
  const renderMenu = (query = "") => {
    matches = searchProducts(query, { applyFilters: false, resultLimit: 120 })
      .filter((name) => !!getTasteReferenceProfile(name))
      .slice(0, 60);
    if (activeIndex >= matches.length) activeIndex = matches.length - 1;
    menu.innerHTML = matches.map((name, index) => `
      <div class="item${index === activeIndex ? " active" : ""}" role="option" data-taste-reference-name="${escHtml(name)}" aria-selected="${index === activeIndex ? "true" : "false"}">${escHtml(name)}</div>
    `).join("") || `<div class="item" role="option">No bottles with comparable taste profiles</div>`;
    menu.classList.remove("hidden");
    input.setAttribute("aria-expanded", "true");
  };
  const selectReference = (name) => {
    if (!name) return;
    input.value = "";
    activeIndex = -1;
    closeMenu();
    setTasteReference(name, { syncUrl: true, refresh: true, defaultToMatch: true });
  };

  input.addEventListener("focus", () => renderMenu(input.value));
  input.addEventListener("input", () => {
    activeIndex = -1;
    renderMenu(input.value);
  });
  input.addEventListener("keydown", (event) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!matches.length) renderMenu(input.value);
      const direction = event.key === "ArrowDown" ? 1 : -1;
      activeIndex = Math.max(0, Math.min(matches.length - 1, activeIndex + direction));
      renderMenu(input.value);
      menu.querySelector(".item.active")?.scrollIntoView({ block: "nearest" });
      return;
    }
    if (event.key === "Enter") {
      const name = matches[Math.max(0, activeIndex)];
      if (name) {
        event.preventDefault();
        selectReference(name);
      }
      return;
    }
    if (event.key === "Escape") closeMenu();
  });
  menu.addEventListener("click", (event) => {
    const option = event.target.closest("[data-taste-reference-name]");
    if (option) selectReference(option.getAttribute("data-taste-reference-name"));
  });
  toggle.addEventListener("click", () => {
    if (menu.classList.contains("hidden")) renderMenu(input.value);
    else closeMenu();
  });
  document.addEventListener("click", (event) => {
    if (!combo.contains(event.target)) closeMenu();
    if (event.target.closest("[data-taste-reference-clear]")) {
      setTasteReference("", { syncUrl: true, refresh: true, defaultToMatch: false });
    }
  });
  renderTasteReferenceUi();
}

function getActiveDiscoveryFilterCount() {
  const filterbar = document.getElementById("filterbar");
  if (!filterbar) return 0;
  let count = filterbar.querySelectorAll('input[data-filter-checkbox]:checked').length;
  if (document.getElementById("filter-price-min")?.value) count += 1;
  if (document.getElementById("filter-price-max")?.value) count += 1;
  const search = String(catalogueSearchFilter || "").trim();
  if (search) count += 1;
  if (hasActiveTasteSearch()) count += 1;
  return count;
}

function getDiscoveryFacetShortcutInputs(key, value, root = document) {
  if (!key || !root) return [];
  const wanted = norm(value);
  if (!wanted) return [];
  return Array.from(root.querySelectorAll(`input[data-filter-checkbox="${key}"]`))
    .filter((input) => {
      const current = norm(input.value);
      if (key === "peat" && wanted === "peated") {
        return current.includes("peated") && !current.includes("unpeated");
      }
      if (key === "peat") return current === wanted;
      return current === wanted || current.includes(wanted);
    });
}

function discoveryFacetShortcutIsActive(key, value) {
  const targets = getDiscoveryFacetShortcutInputs(key, value);
  return targets.length > 0 && targets.every((input) => input.checked);
}

function discoveryPriceShortcutIsActive(minValue, maxValue) {
  const minInput = document.getElementById("filter-price-min");
  const maxInput = document.getElementById("filter-price-max");
  const currentMin = String(minInput?.value || "").trim();
  const currentMax = String(maxInput?.value || "").trim();
  const wantedMin = minValue == null ? "" : String(minValue);
  const wantedMax = maxValue == null ? "" : String(maxValue);
  return currentMin === wantedMin && currentMax === wantedMax;
}

function getDiscoverySmokeShift(meta = {}, style = "", age = null) {
  const peat = norm(`${meta.peated_state || ""} ${meta.peated || ""}`);
  const origin = norm(`${meta.region || ""} ${meta.country || ""}`);
  let smoke = 0;

  if (peat.includes("unpeated")) smoke = 0;
  else if (peat.includes("heavily peated")) smoke = 34;
  else if (peat.includes("lightly peated")) smoke = 14;
  else if (peat.includes("peated") || peat.includes("peat")) smoke = 24;
  else if (style.includes("islay") || origin.includes("islay")) smoke = 14;

  const ageNumber = Number(age);
  if (smoke > 0 && Number.isFinite(ageNumber) && ageNumber > 10) {
    const dilution = Math.max(0.55, 1 - (ageNumber - 10) * 0.035);
    smoke *= dilution;
  }

  return smoke;
}

function syncDiscoveryShortcutStates() {
  if (!isBrowsePage()) return;
  const searchText = norm(catalogueSearchFilter || "");
  document.querySelectorAll(".discovery-chip").forEach((chip) => {
    let active = false;
    if (chip.hasAttribute("data-discovery-query")) {
      active = !!searchText && searchText === norm(chip.getAttribute("data-discovery-query") || "");
    } else if (chip.hasAttribute("data-discovery-facet")) {
      active = discoveryFacetShortcutIsActive(
        chip.getAttribute("data-discovery-facet"),
        chip.getAttribute("data-discovery-value") || ""
      );
    } else if (chip.hasAttribute("data-discovery-price-min") || chip.hasAttribute("data-discovery-price-max")) {
      const minRaw = chip.getAttribute("data-discovery-price-min");
      const maxRaw = chip.getAttribute("data-discovery-price-max");
      active = discoveryPriceShortcutIsActive(minRaw === null ? null : Number(minRaw), maxRaw === null ? null : Number(maxRaw));
    }
    chip.classList.toggle("is-active", active);
    chip.setAttribute("aria-pressed", active ? "true" : "false");
  });
}

function renderDiscoveryTasteMap(rows = [], viewMeta = {}) {
  const host = document.getElementById("discovery-taste-map-plot");
  if (!host) return;
  const title = document.getElementById("discovery-map-title");
  const key = document.querySelector(".discovery-map-key");

  if (hasActiveTasteSearch()) {
    const neighbours = Array.isArray(viewMeta?.neighbourRows) ? viewMeta.neighbourRows : rows;
    const isFlavourTarget = !!viewMeta?.tasteProfileActive && !tasteReferenceName;
    if (title) title.textContent = isFlavourTarget ? "Closest flavour fits" : "Profile Neighbours";
    if (key) key.innerHTML = isFlavourTarget ? "<i></i> Taste profile" : "<i></i> Overall match";
    host.classList.add("taste-map--neighbours");
    host.innerHTML = neighbours.length ? `
      <div class="taste-neighbour-list">
        ${neighbours.map((row, index) => {
          const match = row?.profile_match;
          const reason = getFamiliarMatchReasons(match, 1)[0] || "Comparable flavour profile";
          return `
            <a class="taste-neighbour" href="${escHtml(buildProductPageUrl(row.product))}">
              <span class="taste-neighbour__rank">${String(index + 1).padStart(2, "0")}</span>
              <span class="taste-neighbour__body"><strong>${escHtml(row.product)}</strong><em>${escHtml(reason)}</em></span>
              <span class="taste-neighbour__score">${Math.round(Number(match?.similarityScore || 0) * 100)}</span>
            </a>
          `;
        }).join("")}
      </div>
      <p class="taste-neighbour-list__note">${isFlavourTarget ? "Closest fits from the selected flavour profile, before the visible result page is applied." : "Stable nearest neighbours from the full filtered result set—not the heuristic map coordinates."}</p>
    ` : `<div class="taste-map__empty muted">No comparable bottles match these filters.</div>`;
    return;
  }

  if (title) title.textContent = "Bottle Style Map";
  if (key) key.innerHTML = "<i></i> Metadata view";
  host.classList.remove("taste-map--neighbours");

  const candidates = rows.slice(0, 5).filter((row) => row?.product);
  if (!candidates.length) {
    host.innerHTML = `
      <div class="taste-map__axis taste-map__axis--x"></div>
      <div class="taste-map__axis taste-map__axis--y"></div>
      <div class="taste-map__label taste-map__label--left">Light</div>
      <div class="taste-map__label taste-map__label--right">Rich</div>
      <div class="taste-map__label taste-map__label--top">Smoky</div>
      <div class="taste-map__label taste-map__label--bottom">Sweet</div>
      <div class="taste-map__empty muted">No matched bottles yet.</div>
    `;
    return;
  }

  const pointFor = (name, index) => {
    const meta = getCatalogMeta(name) || {};
    const cask = norm(meta.cask || "");
    const style = norm(`${meta.production_style || ""} ${meta.whisky_family || ""}`);
    const age = Number(meta.age);
    const abv = Number(meta.abv);

    let x = 48;
    let y = 52;
    if (cask.includes("sherry") || cask.includes("port") || cask.includes("wine")) x += 20;
    if (cask.includes("bourbon") || cask.includes("american")) x -= 8;
    if (style.includes("bourbon")) x += 14;
    if (Number.isFinite(age)) x += Math.max(-8, Math.min(16, (age - 10) * 0.9));
    if (Number.isFinite(abv)) x += Math.max(-6, Math.min(12, (abv - 43) * 0.8));
    y -= getDiscoverySmokeShift(meta, style, age);
    if (cask.includes("sherry") || cask.includes("port")) y += 10;
    if (index % 2) y += 8;
    if (index % 3 === 2) x -= 12;

    return {
      x: Math.max(13, Math.min(87, x)),
      y: Math.max(14, Math.min(86, y)),
    };
  };

  const points = candidates.map((row, index) => {
    const name = String(row.product || "");
    const point = pointFor(name, index);
    const label = name.length > 24 ? `${name.slice(0, 23)}...` : name;
    return `
      <a class="taste-map__point" href="${escHtml(buildProductPageUrl(name))}" style="--x:${point.x}%; --y:${point.y}%;" aria-label="Open ${escHtml(name)}">
        <span></span>
        <em>${escHtml(label)}</em>
      </a>
    `;
  }).join("");

  host.innerHTML = `
    <div class="taste-map__axis taste-map__axis--x"></div>
    <div class="taste-map__axis taste-map__axis--y"></div>
    <div class="taste-map__rings" aria-hidden="true"></div>
    <div class="taste-map__label taste-map__label--left">Light</div>
    <div class="taste-map__label taste-map__label--right">Rich</div>
    <div class="taste-map__label taste-map__label--top">Smoky</div>
    <div class="taste-map__label taste-map__label--bottom">Sweet</div>
    ${points}
  `;
}

function renderDiscoveryValuePicks(rows = []) {
  const host = document.getElementById("discovery-value-picks");
  if (!host) return;

  const picks = rows
    .filter((row) => row?.product && Number.isFinite(Number(row.current_lowest)))
    .slice(0, 3);

  host.innerHTML = picks.map((row) => {
    const name = String(row.product || "");
    const img = getCatalogImage(name);
    const price = fmtZAR(row.current_lowest);
    const delta = Number(row.drop_pct);
    const deltaLabel = Number.isFinite(delta) ? `${delta >= 0 ? "-" : "+"}${Math.abs(delta).toFixed(0)}%` : "";
    return `
      <a class="discovery-value-pick" href="${escHtml(buildProductPageUrl(name))}" aria-label="Open ${escHtml(name)}">
        <span class="discovery-value-pick__media">
          ${img ? `<img src="${escHtml(img)}" alt="" loading="lazy">` : ""}
        </span>
        <span class="discovery-value-pick__body">
          <strong>${escHtml(name)}</strong>
          <em>${escHtml(row.retailer || "Best tracked retailer")}</em>
        </span>
        <span class="discovery-value-pick__price">
          <strong>${escHtml(price)}</strong>
          ${deltaLabel ? `<em>${escHtml(deltaLabel)} vs usual</em>` : ""}
        </span>
      </a>
    `;
  }).join("") || `<div class="muted discovery-value-picks__empty">No priced matches in this view.</div>`;
}

function updateDiscoveryCompanions() {
  if (!isBrowsePage()) return;
  syncDiscoveryShortcutStates();
}

function renderDealPager(meta = {}) {
  const pagers = Array.from(document.querySelectorAll('.deal-pager'));
  if (!pagers.length) return;

  const totalPages = Math.max(1, Number(meta?.totalPages) || 1);
  const currentPage = Math.max(1, Math.min(totalPages, Number(meta?.currentPage) || 1));
  const totalRanked = Number(meta?.totalRanked) || 0;

  pagers.forEach((pager) => {
    const prevBtn = pager.querySelector('[data-deal-page-action="prev"]');
    const nextBtn = pager.querySelector('[data-deal-page-action="next"]');
    const info = pager.querySelector('[data-deal-page-info]');
    const pageSelect = pager.querySelector('[data-deal-page-select]');

    if (prevBtn) prevBtn.disabled = currentPage <= 1;
    if (nextBtn) nextBtn.disabled = currentPage >= totalPages;
    if (info) info.textContent = `Page ${currentPage} of ${totalPages}`;
    if (pageSelect) {
      const knownPages = Number(pageSelect.dataset.totalPages || 0);
      if (knownPages !== totalPages) {
        pageSelect.innerHTML = Array.from({ length: totalPages }, (_, i) => {
          const n = i + 1;
          return `<option value="${n}">${n}</option>`;
        }).join("");
        pageSelect.dataset.totalPages = String(totalPages);
      }
      pageSelect.value = String(currentPage);
      pageSelect.disabled = totalPages <= 1;
    }
    pager.dataset.totalPages = String(totalPages);

    pager.classList.toggle('hidden', totalRanked <= DEALS_PER_PAGE);
  });
}

function renderWhiskyShelf(rowsOverride = null, meta = {}) {
  const list = document.getElementById('whisky-shelf-list');
  if (!list) return;
  const rows = Array.isArray(rowsOverride) ? rowsOverride : [];
  const totalMatches = Number(meta?.totalMatches);
  const totalRanked = Number(meta?.totalRanked);
  const browseMeta = document.getElementById('browse-meta');
  if (browseMeta) {
    const count = Number.isFinite(totalRanked)
      ? totalRanked
      : (Number.isFinite(totalMatches) ? totalMatches : rows.length);
    browseMeta.textContent = `${count} bottle${count === 1 ? "" : "s"}`;
  }

  const html = rows.map((row) => {
    const name = String(row?.product || "");
    const currentPrice = Number(row?.current_lowest);
    const price = Number.isFinite(currentPrice) && currentPrice > 0
      ? fmtZAR(currentPrice)
      : "No live price tracked";
    const img = getCatalogImage(name);
    const delta = formatDealDelta(row);
    const href = buildProductPageUrl(name);
    const match = row?.profile_match || null;
    const familiarReasons = getFamiliarMatchReasons(match, 2);
    const matchMarkup = match ? `
      <div class="shelf-match-panel">
        <strong>${escHtml(formatOverallMatchStrength(match.similarityScore, { tasteTarget: !!match.isTasteTarget }))}</strong>
        ${familiarReasons.length ? `<span>${familiarReasons.map(escHtml).join(" · ")}</span>` : ""}
      </div>
    ` : "";
    const bgStyle = img
      ? ` style="--shelf-bg-image: url(&quot;${escHtml(String(img).replace(/"/g, '\\"'))}&quot;);"`
      : "";

    return `
      <article class="shelf-item${img ? " has-bg-image" : ""}${match ? " has-profile-match" : ""}" data-name="${escHtml(name)}"${bgStyle}>
        ${img ? `
          <button class="shelf-bg-image-btn" type="button" data-image-zoom aria-haspopup="dialog" aria-controls="image-modal" aria-label="Expand image for ${escHtml(name)}" title="Expand image">
            <img class="shelf-bg-image-proxy" src="${escHtml(img)}" alt="${escHtml(name)}" loading="lazy">
          </button>
        ` : ""}
        <div class="shelf-card-content">
          <div class="shelf-name-banner">${escHtml(name)}</div>
          <a class="shelf-body" href="${escHtml(href)}" aria-label="Open product page for ${escHtml(name)}" title="Open product page">
            ${matchMarkup}
            <div class="shelf-sub muted">${price}</div>
            <div class="shelf-relative muted">${escHtml(delta)}</div>
          </a>
        </div>
      </article>
    `;
  }).join('');

  const emptyMessage = hasActiveTasteSearch()
    ? "No priced bottles with comparable taste profiles match the selected filters."
    : "No matching deals for the selected filters.";
  list.innerHTML = html || `<div class="muted" style="padding:12px;">${escHtml(emptyMessage)}</div>`;
  renderDealPager(meta);
  updateDiscoveryCompanions(rows, meta);

  if (!window.__whiskyShelfNavStateInit) {
    window.__whiskyShelfNavStateInit = true;
    list.addEventListener('click', (e) => {
      if (e.target.closest('[data-image-zoom]')) return;
      const link = e.target.closest('.shelf-body[href]');
      if (link) {
        persistBrowseState({ scrollY: window.scrollY });
        return;
      }
      const item = e.target.closest('.shelf-item');
      const itemLink = item?.querySelector?.('.shelf-body[href]');
      if (!itemLink) return;
      persistBrowseState({ scrollY: window.scrollY });
      window.location.href = itemLink.href;
    });
  }

  updateShelfActive(hasDetailSurface() ? currentProduct : "");
}

function scrollToProductDetail() {
  const target = document.getElementById('product-detail') || document.getElementById('focus-stage');
  if (!target) return;
  smoothScrollTo(target, { offset: -getJourneyNavCoverOffset(12), duration: 0.75 });
}

async function openProductDetailFromCard(name) {
  if (!name || !productNameSet.has(name)) return;
  await setProduct(name);
  scrollToProductDetail();
}

function rankDealsAtSelectedRetailers(names, limit = 5, sortBy = "pct", sortDir = null) {
  const retailerNorms = getSelectedRetailerNormSet();
  if (!retailerNorms.size) return [];
  const rows = (names || [])
    .map((name) => buildRetailerScopedDeal(name, retailerNorms))
    .filter(Boolean)
    .sort((a, b) => compareDealRows(a, b, sortBy, sortDir));
  return rows.slice(0, limit);
}

function getTasteMatchLookup(referenceName = tasteReferenceName) {
  const name = resolveCatalogProductName(referenceName);
  if (!name) return new Map();
  if (tasteMatchCache.has(name)) return tasteMatchCache.get(name);

  const sourceMeta = getCatalogMeta(name);
  const sourceProfile = getTasteReferenceProfile(name);
  if (!sourceProfile) return new Map();
  const matches = rankTasteMatches(name, sourceProfile, CATALOG_ITEMS, {
    limit: CATALOG_ITEMS.length,
    sourceMeta,
  });
  const lookup = new Map(matches.map((match) => [match.name, match]));
  tasteMatchCache.set(name, lookup);
  return lookup;
}

function getDiscoveryTasteMatchLookup() {
  const target = getDiscoveryTasteProfile();
  if (!target) return new Map();
  const cacheKey = `discovery:${TASTE_AXES.map((axis) => `${axis.key}:${getDiscoveryTasteLevel(axis.key).toFixed(2)}`).join(",")}`;
  if (tasteMatchCache.has(cacheKey)) return tasteMatchCache.get(cacheKey);

  const matches = [];
  for (const item of CATALOG_ITEMS) {
    const name = String(item?.name || "").trim();
    const rawVector = item?.meta?.taste_vector;
    const includedCount = Number(rawVector?.included_count);
    if (!name || !Number.isFinite(includedCount) || includedCount < 3) continue;
    const scored = scoreDiscoveryTasteTarget(target, rawVector);
    if (!scored) continue;
    matches.push({
      item,
      name,
      profile: scored.candidate,
      distance: scored.distance,
      evidenceScore: Number(rawVector?.scored_rate) || 0,
      tasteSimilarity: scored.tasteSimilarity,
      metadataSimilarity: null,
      metadataCoverage: null,
      similarityScore: scored.tasteSimilarity,
      metadataComparisons: {},
      reasons: scored.reasons,
      noveltyReasons: [],
      isTasteTarget: true,
    });
  }
  matches.sort((left, right) => (
    (right.tasteSimilarity - left.tasteSimilarity)
    || (left.distance - right.distance)
    || (right.evidenceScore - left.evidenceScore)
    || left.name.localeCompare(right.name)
  ));
  const lookup = new Map(matches.map((match) => [match.name, match]));
  tasteMatchCache.set(cacheKey, lookup);
  return lookup;
}

function scoreDiscoveryTasteTarget(target, rawVector) {
  const candidate = normalizeTasteVector(rawVector);
  if (!candidate) return null;
  const activeAxes = TASTE_AXES.filter((axis) => Number(target?.categories?.[axis.key]) > 0);
  if (!activeAxes.length) return null;
  const distance = Math.sqrt(activeAxes.reduce((total, axis) => {
    const desired = Number(target.categories[axis.key]) || 0;
    const actual = Number(candidate.categories[axis.key]) || 0;
    return total + ((desired - actual) ** 2);
  }, 0) / activeAxes.length);
  const tasteSimilarity = Math.max(0, Math.min(1, 1 - distance));
  const closestAxis = activeAxes
    .map((axis) => ({ axis, difference: Math.abs((Number(target.categories[axis.key]) || 0) - (Number(candidate.categories[axis.key]) || 0)) }))
    .sort((left, right) => left.difference - right.difference)[0];
  return {
    candidate,
    distance,
    tasteSimilarity,
    reasons: closestAxis ? [{
      group: "taste",
      label: `Matches your ${closestAxis.axis.label.toLowerCase()} target`,
      contribution: tasteSimilarity,
    }] : [],
  };
}

function attachTasteMatchToRow(row, lookup) {
  const match = lookup.get(String(row?.product || ""));
  if (!match) return null;
  return {
    ...row,
    profile_match: match,
    match_score: match.similarityScore,
    taste_score: match.tasteSimilarity,
  };
}

function getDealShelfRows(query) {
  const names = searchProducts(query, { resultLimit: Number.MAX_SAFE_INTEGER });
  const retailerNorms = getSelectedRetailerNormSet();
  let rankedRows = rankDiscoveryRows(
    names,
    retailerNorms,
    names.length,
    DEAL_VIEW.sortBy,
    DEAL_VIEW.sortDir,
  );
  if (hasActiveTasteSearch()) {
    const lookup = tasteReferenceName
      ? getTasteMatchLookup(tasteReferenceName)
      : getDiscoveryTasteMatchLookup();
    rankedRows = rankedRows
      .map((row) => attachTasteMatchToRow(row, lookup))
      .filter(Boolean)
      .sort((left, right) => compareDealRows(left, right, DEAL_VIEW.sortBy, DEAL_VIEW.sortDir));
  }
  const pageSize = DEALS_PER_PAGE;
  const totalPages = Math.max(1, Math.ceil(rankedRows.length / pageSize));
  if (!Number.isFinite(Number(DEAL_VIEW.page)) || DEAL_VIEW.page < 1) DEAL_VIEW.page = 1;
  if (DEAL_VIEW.page > totalPages) DEAL_VIEW.page = totalPages;
  const start = (DEAL_VIEW.page - 1) * pageSize;
  const rows = rankedRows.slice(start, start + pageSize);
  const neighbourRows = hasActiveTasteSearch()
    ? rankedRows.slice().sort((left, right) => (
        compareMaybeNumeric(left?.match_score, right?.match_score, "desc")
        || compareMaybeNumeric(left?.taste_score, right?.taste_score, "desc")
        || compareMaybeText(left?.product, right?.product, "asc")
      )).slice(0, 5)
    : [];
  return {
    rows,
    totalMatches: hasActiveTasteSearch() ? rankedRows.length : names.length,
    totalRanked: rankedRows.length,
    pageSize,
    totalPages,
    currentPage: DEAL_VIEW.page,
    neighbourRows,
    tasteReferenceName,
    tasteProfileActive: hasDiscoveryTasteProfile(),
  };
}

function initJourneyNav() {
  const nav = document.getElementById('journey-nav');
  if (!nav) return;

  nav.addEventListener('click', (e) => {
    const btn = e.target.closest('button[data-target]');
    if (!btn) return;
    const targetSel = btn.getAttribute('data-target');
    if (!targetSel) return;
    const target = document.querySelector(targetSel);
    if (!target) return;
    if (target.tagName === "DETAILS") target.open = true;
    smoothScrollTo(target, { offset: -getJourneyNavCoverOffset(), duration: 0.7 });
  });

  const sections = Array.from(nav.querySelectorAll('button[data-target]')).map((btn) => ({
    btn,
    target: document.querySelector(btn.getAttribute('data-target')),
  }));
  const updateActive = () => {
    const y = window.scrollY + getJourneyNavCoverOffset(24);
    let active = sections[0] || null;
    for (const section of sections) {
      if (!section.target) continue;
      if (section.target.getBoundingClientRect().top + window.scrollY <= y) active = section;
    }
    sections.forEach((section) => section.btn.classList.toggle('is-active', section === active));
  };
  onScrollUnified(updateActive);
  updateActive();
}

function updateShelfActive(name) {
  const list = document.getElementById('whisky-shelf-list');
  if (!list) return;
  const items = list.querySelectorAll('.shelf-item');
  items.forEach((el) => {
    const isActive = !!name && el.getAttribute('data-name') === name;
    el.classList.toggle('is-active', isActive);
    if (el.tagName === 'A') {
      if (isActive) el.setAttribute('aria-current', 'page');
      else el.removeAttribute('aria-current');
      return;
    }
    el.setAttribute('aria-selected', isActive ? 'true' : 'false');
  });
}

function updateDensityToggleLabel() {
    const btn = document.getElementById('density-toggle');
    const tbl = document.getElementById('retailer-table');
    if (!btn || !tbl) return;
    // If the table is currently compact, the button should offer to switch to "Cozy"
    btn.textContent = tbl.classList.contains('table-compact') ? 'Cozy' : 'Compact';
}

// --- Container-aware resizing ---
function computePlotHeight() {
    const h = window.innerHeight || 800;
    const isMobile = (window.innerWidth || 1200) <= 680;
    if (isMobile) {
      return Math.max(320, Math.min(430, Math.round(h * 0.44)));
    }
    return Math.max(390, Math.min(560, Math.round(h * (h > 900 ? 0.52 : (h > 700 ? 0.50 : 0.48)))));
}
function computeMarkerSize() {
    const w = window.innerWidth || 1200;
    if (w <= 420) return 7;
    if (w <= 680) return 8;
    if (w <= 1024) return 9;
    return 10;
}
function measureAndResize() {
    const gd = document.getElementById('graph'); if (!gd || !window.Plotly || !plotInitialized) return;
    const parent = gd.parentElement || gd;
    const w = Math.floor(parent.getBoundingClientRect().width || window.innerWidth || 360);
    const h = computePlotHeight();
    const isMobile = w <= 680;
    const legendOrientation = isMobile ? "v" : "h";
    const legendY = isMobile ? -0.18 : -0.24;
    const legendFontSize = isMobile ? 10 : 11;

    let leftMargin;
    if (w <= 360) leftMargin = 76;
    else if (w <= 420) leftMargin = 68;
    else if (w <= 520) leftMargin = 56;
    else if (w <= 680) leftMargin = 44;
    else leftMargin = 28;

    // responsive title size + top margin
    const titleSize = (w <= 360) ? 14 : (w <= 420) ? 16 : (w <= 680) ? 18 : 20;
    const topMargin = isMobile ? 34 : 82;
    const bottomMargin = isMobile ? 58 : 62;
    const xTickFontSize = (w <= 360) ? 9 : (w <= 520 ? 10 : 11);

    Plotly.relayout(gd, {
        width: w,
        height: h,
        margin: { l: leftMargin + 8, r: isMobile ? 18 : 28, t: topMargin, b: bottomMargin },
        'xaxis.automargin': true,
        'yaxis.automargin': true,
        legend: {
          orientation: legendOrientation,
          y: legendY,
          x: 0,
          xanchor: "left",
          yanchor: "top",
          font: { size: legendFontSize }
        },
        'xaxis.tickfont.size': xTickFontSize,
        'xaxis.nticks': isMobile ? 4 : 7,
        'xaxis.tickangle': isMobile ? -30 : 0,
        'yaxis.tickfont.size': (w <= 360 ? 10 : w <= 420 ? 11 : 12),
        'title.font.size': titleSize
    });

    Plotly.restyle(gd, { 'marker.size': computeMarkerSize() });
    Plotly.Plots.resize(gd);
}

measureAndResize();
['resize', 'orientationchange'].forEach(ev => {
  window.addEventListener(ev, () => { requestAnimationFrame(measureAndResize); });
});
(function () {
    const gd = document.getElementById('graph'); if (!gd || !('ResizeObserver' in window)) return;
    const target = gd.parentElement || gd; let ticking = false;
    const ro = new ResizeObserver(() => {
        if (ticking) return; ticking = true;
        requestAnimationFrame(() => { try { measureAndResize(); } finally { ticking = false; } });
    });
    ro.observe(target);
})();

// ---- Theme handling ----
const THEME_DARK = { template: 'plotly_dark', paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)', font_color: '#f3ede2', grid: 'rgba(243,237,226,0.055)' };
const THEME_SHELL = {
  dark: { bg: '#070504', bg2: '#0e0705', colorScheme: 'dark' },
};

function normalizeThemeMode(mode) {
  return 'dark';
}

function applyThemeShell(mode) {
  const normalized = normalizeThemeMode(mode);
  const shell = THEME_SHELL[normalized];
  const root = document.documentElement;
  root.style.backgroundColor = shell.bg;
  root.style.colorScheme = shell.colorScheme;
  if (!document.body) return;
  document.body.style.backgroundColor = shell.bg;
  document.body.style.backgroundImage = `linear-gradient(180deg, ${shell.bg}, ${shell.bg2})`;
}

function applyThemeToPlot(mode) {
    const gd = document.getElementById('graph'); if (!gd || !window.Plotly || !plotInitialized) return;
    const t = THEME_DARK;
    Plotly.relayout(gd, {
        template: t.template,
        paper_bgcolor: t.paper_bgcolor,
        plot_bgcolor: t.plot_bgcolor,
        font: { color: t.font_color, family: getComputedStyle(document.documentElement).getPropertyValue('--font-sans').trim() || 'IBM Plex Sans' },
        'xaxis.gridcolor': t.grid,
        'xaxis.zerolinecolor': t.grid,
        'yaxis.gridcolor': t.grid,
        'yaxis.zerolinecolor': t.grid
    });
    measureAndResize();
}

// ---- Best Deals Today: Bottle Shelf + unfold drawer ----
function renderTop5Shelf(dataOverride = null) {
    const data = (dataOverride || top5_deals || []).slice(0, 5);
    const shelf = document.getElementById('top5-shelf');
    if (!shelf) return;

    if (!data.length) {
        shelf.innerHTML = `<div class="muted" style="padding:12px;">No standout deals right now.</div>`;
        return;
    }

    function retailerLink(r) {
        if (r.url) return `<a href="${escHtml(r.url)}" target="_blank" rel="noopener">${escHtml(r.retailer || 'Retailer')}</a>`;
        return escHtml(r.retailer || '-');
    }

    shelf.innerHTML = data.map((r, i) => {
        const name = escHtml(r.product || '-');
        const safeName = escHtml(r.product || '');
        const dropPct = (r.drop_pct != null) ? Number(r.drop_pct).toFixed(2) : null;
        const saveR = (r.drop_value != null) ? fmtZAR(r.drop_value) : '-';
        const below = (dropPct != null) ? `${dropPct}% below usual low` : '-';

        let badge = '';
        if (r.__mode === 'retailer_focus') {
            badge = `<span class="pill pill-amber">Best deal at selected retailer</span>`;
        } else if (r.__mode === 'price_range') {
            badge = `<span class="pill pill-amber">In your price range</span>`;
        } else if (r.is_new_low) {
            badge = `<span class="pill pill-good">New all-time low</span>`;
        } else if (r.dist_from_low_pct != null) {
            const d = Number(r.dist_from_low_pct);
            badge = (d <= 1)
                ? `<span class="pill pill-good">At 12-month low</span>`
                : `<span class="pill pill-amber">Within ${d.toFixed(2)}% of 12-month low</span>`;
        } else {
            badge = `<span class="pill pill-amber">Deal today</span>`;
        }

    const sideCls = (i % 2 === 0) ? 'float-left' : 'float-right';

    return `
      <article class="deal-card ${sideCls}" role="group" aria-expanded="false" data-name="${safeName}">
        <div class="deal-content">
          <button class="deal-btn" type="button" aria-label="Open deal details">
            <div class="deal-top">
              <span class="bottleIcon" aria-hidden="true">
                <svg viewBox="-63 65 128 128" role="img" focusable="false" preserveAspectRatio="xMidYMid meet">
                  <path class="bottleStroke" d="M8.4,108.4c7.7,1.8,13.5,8.4,13.5,16.3v60.6c0,1.9-1.6,3.4-3.5,3.4h-34.8c-1.9,0-3.5-1.5-3.5-3.4v-60.6c0-7.9,5.7-14.6,13.5-16.3V81.3H8.3v27.1H8.4z"/>
                  <rect class="bottleStroke bottleSubtle" x="-6.4" y="69.3" width="14.7" height="5.8"/>
                  <rect class="bottleStroke bottleSubtle" x="-10" y="138" width="20" height="18" rx="3"/>
                </svg>
              </span>
              <div class="deal-head">
                <div class="deal-name">${name}</div>
                <div class="deal-price">${fmtZAR(r.current_lowest)}</div>
                <div class="deal-sub">
                  ${badge}
                  ${dropPct != null ? `<span class="pill pill-good">&#9660; ${dropPct}%</span>` : ''}
                </div>
              </div>
            </div>
          </button>

          <div class="deal-drawer" aria-hidden="true">
            <div class="deal-drawer-inner">
              <div class="muted">
                ${r.__mode === 'retailer_focus'
                  ? `Best deal price in your selected retailer filter: ${retailerLink(r)}${Number(r.drop_value) > 0 ? ` You save <b>${saveR}</b>` : ''}`
                  : (r.__mode === 'price_range'
                    ? `Current best price at ${retailerLink(r)}`
                    : `Current best price at ${retailerLink(r)} You save <b>${saveR}</b>`)}
              </div>

              <div class="deal-grid">
                <div class="kv">
                  <div class="k">Usual low</div>
                  <div class="v">${fmtZAR(r.baseline_price)}</div>
                  <div class="muted" style="margin-top:4px;">Most common low level (12-month)</div>
                </div>

                <div class="kv">
                  <div class="k">12-month low</div>
                  <div class="v">${fmtZAR(r.alltime_low)}</div>
                  <div class="muted" style="margin-top:4px;">How close today is to the floor</div>
                </div>
              </div>

              <div class="kv">
                <div class="k">What this means</div>
                <div class="muted">${r.__mode === 'retailer_focus'
                  ? `${below}. Ranked by discount vs this bottle's usual low, scoped to your selected retailer.`
                  : `${below}. This is the type of price you only see occasionally.`}</div>
              </div>

              <div class="deal-actions">
                <button class="js-open-story btn-link" type="button" data-name="${safeName}">Open price story</button>
                ${r.url ? `<a class="btn-link" href="${escHtml(r.url)}" target="_blank" rel="noopener">Buy Cheapest</a>` : ''}
              </div>
            </div>
          </div>
        </div>
      </article>
    `;
    }).join('');

    // Keep deal motion subtle and deterministic.
    if (typeof window.__dealMotionCleanup === "function") {
      window.__dealMotionCleanup();
    }
    window.__dealMotionCleanup = setupDealScrollMotion(shelf);
       
    // Click handling (unfold one at a time) - only register once
    if (!window.__top5DealsClickInit){
      window.__top5DealsClickInit = true;
      shelf.addEventListener('click', (e)=>{
        const openBtn = e.target.closest('.js-open-story');
        if (openBtn) {
            const name = openBtn.getAttribute('data-name');
            if (name && products && products.includes(name)) {
                setProduct(name);
                const graph = document.getElementById('graph');
                smoothScrollTo(graph);
            }
            return;
        }

        const card = e.target.closest('.deal-card');
        if (!card) return;

        const isOpen = card.getAttribute('aria-expanded') === 'true';
        document.querySelectorAll('#top5-shelf .deal-card[aria-expanded="true"]').forEach(c=>{
            c.setAttribute('aria-expanded', 'false');
            const dr = c.querySelector('.deal-drawer');
            if (dr) dr.setAttribute('aria-hidden', 'true');
        });
        if (!isOpen) {
            card.setAttribute('aria-expanded', 'true');
            const dr = card.querySelector('.deal-drawer');
            if (dr) dr.setAttribute('aria-hidden', 'false');
        }
    }, { passive: true });
}
}

function setupDealScrollMotion(stageEl){
  if (!stageEl) return () => {};
  const cards = Array.from(stageEl.querySelectorAll('article.deal-card'));
  if (!cards.length) return () => {};

  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  if (reduce) {
    cards.forEach((el) => el.classList.add('is-inview'));
    return () => {};
  }

  cards.forEach((el, i) => {
    el.classList.remove('is-inview');
    const fromLeft = el.classList.contains('float-left') || i % 2 === 0;
    el.classList.add(fromLeft ? 'enter-left' : 'enter-right');
  });

  const io = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) entry.target.classList.add('is-inview');
    }
  }, {
    root: null,
    rootMargin: '0px 0px -8% 0px',
    threshold: 0.16,
  });

  cards.forEach((el) => io.observe(el));

  // Reveal top cards immediately so initial paint feels intentional.
  requestAnimationFrame(() => {
    cards.slice(0, 2).forEach((el) => el.classList.add('is-inview'));
  });

  return () => io.disconnect();
}

function quietHeaderUtilities(){
  const insights = document.querySelector('a.btn-link[href="insights.html"]');
  if (insights){
    insights.textContent = 'Insights';
    insights.title = 'Retailer insights';
  }
}

// ---- Retailer table sorting (optional) ----
let retailerSort = { key: 'current_price', dir: 'asc' };
function sortRetailerRows(rows) {
    const { key, dir } = retailerSort;
    const mul = dir === 'asc' ? 1 : -1;
    return rows.slice().sort((a, b) => {
        const va = a[key] ?? '';
        const vb = b[key] ?? '';
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mul;
        return String(va).localeCompare(String(vb)) * mul;
    });
}
const retailerTableHead = document.querySelector('#retailer-table thead');
if (retailerTableHead) {
  retailerTableHead.addEventListener('click', (e) => {
      const th = e.target.closest('th');
      if (!th) return;
      const map = {
          'Retailer': 'retailer',
          'Latest Price': 'current_price',
          'Compare-at': 'compare_at_latest',
          'Discount': 'discount_pct'
      };
      const key = map[th.textContent.trim()];
      if (!key) return;
      retailerSort = { key, dir: (retailerSort.key === key && retailerSort.dir === 'asc') ? 'desc' : 'asc' };
      if (currentProduct) setProduct(currentProduct);
  });
}

// ---- Per-product JSON for detail + chart payload ----
function slugifyProductName(name) {
    return encodeURIComponent(
      String(name || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
    );
}

async function fetchProductPayload(name) {
    if (!name) return null;
    if (productPayloadCache.has(name)) return productPayloadCache.get(name);

    const promise = (async () => {
        const slug = slugifyProductName(name);
        const fileName = getCatalogDataFile(name) || `${slug}.json`;
        const attempts = [
          { url: buildDataUrl(fileName), init: {} },
          { url: buildDataUrl(fileName, { versioned: false }), init: { cache: "reload" } },
        ];

        for (const attempt of attempts) {
          try {
            const res = await fetch(attempt.url, attempt.init);
            if (!res.ok) {
              console.warn(`Could not load product payload for "${name}" from ${attempt.url} (${res.status}).`);
              continue;
            }
            const raw = await res.text();
            const text = String(raw || "").replace(/^\uFEFF/, "");
            const trimmed = text.trimStart();
            if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
              const contentType = res.headers.get("content-type") || "unknown";
              console.warn(
                `Product payload for "${name}" from ${attempt.url} was not JSON.`,
                { contentType, snippet: trimmed.slice(0, 140) }
              );
              continue;
            }
            try {
              return JSON.parse(text);
            } catch (err) {
              console.warn(
                `Product payload parse failed for "${name}" from ${attempt.url}.`,
                { snippet: trimmed.slice(0, 140) },
                err
              );
            }
          } catch (err) {
            console.warn(`Product payload request failed for "${name}" from ${attempt.url}.`, err);
          }
        }

        console.warn(`All product payload attempts failed for "${name}".`);
        productPayloadCache.delete(name);
        return null;
    })();

    productPayloadCache.set(name, promise);
    return promise;
}
function renderBadges() {
    const cont = document.getElementById('badge-row');
    if (!cont) return;
    cont.innerHTML = '';
}
function renderDealMeta(payload) {
    const el = document.getElementById('deal-meta');
    if (!el) return;
    el.textContent = '';
}

function renderDealMetaFromRows(rows = []) {
    const el = document.getElementById('deal-meta');
    if (!el) return;
    el.textContent = '';
}

// ---- Product switching & table render ----
function shortTimestamp(ts) {
    if (!ts) return '-';
    // Accepts "YYYY-MM-DDTHH:MM[:SS]" or "YYYY-MM-DD HH:MM[:SS]"
    const tsBaseline = ts.replace('T', ' ').slice(0, 16); // keep YYYY-MM-DD HH:MM baseline
    // Try to format as "DD Mon HH:MM" (shorter, fits narrow cells)
    const d = new Date(tsBaseline.replace(' ', 'T'));
    if (!isNaN(d.getTime())) {
        const dd = String(d.getDate()).padStart(2, '0');
        const mon = d.toLocaleString('en-GB', { month: 'short' }); // e.g., "Oct"
        const hh = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${dd} ${mon} ${hh}:${min}`; // e.g., "06 Oct 14:35"
    }
    return tsBaseline; // fallback
}


function syncProductControls(name) {
  const sel = document.getElementById('product-select');
  if (sel) sel.value = name;
}

function getActivePlotTheme() {
  return THEME_DARK;
}

function getPlotViewportFlags() {
  const width = window.innerWidth || 1200;
  return {
    isMobile: width <= 680,
    isNarrow: width <= 420,
  };
}

function getPlotRangeSelectorConfig() {
  const { isMobile } = getPlotViewportFlags();
  if (isMobile) return null;
  return {
    buttons: [
      { count: 30, label: '30D', step: 'day', stepmode: 'backward' },
      { count: 90, label: '90D', step: 'day', stepmode: 'backward' },
      { label: 'All', step: 'all' },
    ],
    active: 1,
    x: 0,
    y: 1.12,
    xanchor: 'left',
    yanchor: 'top',
    bgcolor: 'rgba(255,255,255,0.045)',
    activecolor: 'rgba(197,138,58,0.32)',
    bordercolor: 'rgba(247,229,198,0.12)',
    borderwidth: 1,
    font: { color: '#f3ede2', size: 11 },
  };
}

function getPlotDateAxisConfig(defaultRange = null) {
  const { isMobile, isNarrow } = getPlotViewportFlags();
  const axis = {
    automargin: true,
    ticklabelmode: 'period',
    tickformat: isMobile ? '%d %b' : '%d %b %Y',
    tickfont: { size: isNarrow ? 9 : (isMobile ? 10 : 11) },
    nticks: isMobile ? 4 : 7,
    tickangle: isMobile ? -30 : 0,
    zeroline: false,
    showgrid: false,
  };
  if (defaultRange) axis.range = defaultRange;
  const rangeSelector = getPlotRangeSelectorConfig();
  if (rangeSelector) axis.rangeselector = rangeSelector;
  return axis;
}

function getTraceStyle(seriesLength = 0) {
  const { isMobile } = getPlotViewportFlags();
  if (seriesLength >= 90) {
    return {
      mode: 'lines',
      lineWidth: isMobile ? 2.35 : 2.65,
      markerSize: isMobile ? 4 : 5,
    };
  }
  if (seriesLength >= 40) {
    return {
      mode: 'lines+markers',
      lineWidth: isMobile ? 2.6 : 2.9,
      markerSize: isMobile ? 5 : 6,
    };
  }
  return {
    mode: 'lines+markers',
    lineWidth: isMobile ? 2.85 : 3.35,
    markerSize: isMobile ? 6 : 8,
  };
}

function collectPlotPriceEvents(payload) {
  const events = [];
  for (const [retailer, series] of Object.entries(payload?.retailers || {})) {
    const ts = Array.isArray(series?.ts) ? series.ts : [];
    const price = Array.isArray(series?.price) ? series.price : [];
    for (let i = 0; i < ts.length; i += 1) {
      const y = Number(price[i]);
      const time = Date.parse(ts[i]);
      if (!Number.isFinite(y) || !Number.isFinite(time)) continue;
      events.push({ retailer, ts: ts[i], price: y, time });
    }
  }
  return events.sort((a, b) => (a.time - b.time) || (a.price - b.price) || a.retailer.localeCompare(b.retailer));
}

function buildMarketSnapshots(payload) {
  const events = collectPlotPriceEvents(payload);
  if (!events.length) return [];

  const groups = [];
  for (const event of events) {
    const last = groups[groups.length - 1];
    if (last && last.time === event.time) {
      last.events.push(event);
    } else {
      groups.push({ time: event.time, events: [event] });
    }
  }

  const currentPrices = new Map();
  const snapshots = [];
  for (const group of groups) {
    for (const event of group.events) {
      currentPrices.set(event.retailer, event.price);
    }
    const priced = Array.from(currentPrices.entries())
      .filter(([, price]) => Number.isFinite(Number(price)));
    if (!priced.length) continue;

    let min = Infinity;
    let max = -Infinity;
    let minRetailer = '';
    for (const [retailer, price] of priced) {
      if (price < min) {
        min = price;
        minRetailer = retailer;
      }
      if (price > max) max = price;
    }

    snapshots.push({
      ts: new Date(group.time).toISOString(),
      time: group.time,
      min,
      max,
      minRetailer,
      retailerCount: priced.length,
    });
  }

  return snapshots;
}

function getDefaultPlotDateRange(payload, days = PLOT_DEFAULT_RANGE_DAYS) {
  const snapshots = buildMarketSnapshots(payload);
  if (!snapshots.length) return null;
  const minTime = snapshots[0].time;
  const maxTime = snapshots[snapshots.length - 1].time;
  if (!Number.isFinite(minTime) || !Number.isFinite(maxTime)) return null;
  const span = maxTime - minTime;
  const rangeMs = days * MS_PER_DAY;
  if (span <= rangeMs) return null;
  const rightPad = Math.max(6 * 60 * 60 * 1000, Math.min(3 * MS_PER_DAY, rangeMs * 0.02));
  return [
    new Date(maxTime - rangeMs).toISOString(),
    new Date(maxTime + rightPad).toISOString(),
  ];
}

function samePlotSignal(a, b) {
  return !!a && !!b
    && a.time === b.time
    && Math.abs(Number(a.min) - Number(b.min)) < 0.005;
}

function getPlotSignalPoints(payload) {
  const snapshots = buildMarketSnapshots(payload);
  if (!snapshots.length) return [];
  const points = [];

  function addLabel(snapshot, label, detail = '') {
    if (!snapshot) return;
    let point = points.find((candidate) => samePlotSignal(candidate.event, snapshot));
    if (!point) {
      point = { event: snapshot, labels: [], details: [] };
      points.push(point);
    }
    if (!point.labels.includes(label)) point.labels.push(label);
    if (detail && !point.details.includes(detail)) point.details.push(detail);
  }

  const lowest = snapshots.reduce((best, snapshot) => {
    if (!best || snapshot.min < best.min) return snapshot;
    if (snapshot.min === best.min && snapshot.time > best.time) return snapshot;
    return best;
  }, null);
  addLabel(lowest, 'Lowest tracked', fmtZAR(lowest?.min));

  let runningLow = Infinity;
  let latestNewLow = null;
  for (const snapshot of snapshots) {
    if (snapshot.min < runningLow - 0.005) {
      if (Number.isFinite(runningLow)) {
        latestNewLow = { ...snapshot, drop: runningLow - snapshot.min };
      }
      runningLow = snapshot.min;
    }
  }
  addLabel(latestNewLow, 'New low', latestNewLow?.drop ? `-${fmtZAR(latestNewLow.drop)}` : fmtZAR(latestNewLow?.min));

  let biggestDrop = null;
  for (let i = 1; i < snapshots.length; i += 1) {
    const previous = snapshots[i - 1];
    const current = snapshots[i];
    const drop = previous.min - current.min;
    if (drop > 0.005 && (!biggestDrop || drop > biggestDrop.drop)) {
      biggestDrop = { ...current, drop };
    }
  }
  addLabel(biggestDrop, 'Biggest drop', biggestDrop?.drop ? `-${fmtZAR(biggestDrop.drop)}` : '');

  return points.sort((a, b) => a.event.time - b.event.time);
}

function renderPlotSignalRow(payload) {
  document.getElementById('plot-signal-row')?.remove();
}

function buildPlotAnnotations(payload, hasData = true) {
  return [];
}

function buildPlotConfig() {
  return {
    responsive: true,
    displaylogo: false,
    displayModeBar: false,
  };
}

function buildPlotLayout(title, hasData = true, payload = null) {
  const theme = getActivePlotTheme();
  const { isMobile } = getPlotViewportFlags();
  const defaultRange = getDefaultPlotDateRange(payload);
  return {
    template: theme.template,
    paper_bgcolor: theme.paper_bgcolor,
    plot_bgcolor: theme.plot_bgcolor,
    colorway: PLOT_PALETTE,
    title: { text: '' },
    xaxis_title: isMobile ? '' : 'Date',
    yaxis_title: 'Price (ZAR)',
    hovermode: 'closest',
    font: {
      family: getComputedStyle(document.documentElement).getPropertyValue('--font-sans').trim() || 'IBM Plex Sans',
      size: 14,
      color: theme.font_color
    },
    margin: { l: isMobile ? 58 : 58, r: isMobile ? 16 : 30, t: isMobile ? 30 : 82, b: isMobile ? 54 : 62 },
    xaxis: { ...getPlotDateAxisConfig(defaultRange), gridcolor: theme.grid, zerolinecolor: theme.grid },
    yaxis: { automargin: true, gridcolor: theme.grid, zerolinecolor: theme.grid, zeroline: false, showgrid: true },
    autosize: true,
    showlegend: false,
    legend: {
      orientation: isMobile ? 'v' : 'h',
      y: isMobile ? -0.18 : -0.25,
      x: 0,
      xanchor: 'left',
      yanchor: 'top',
      font: { size: isMobile ? 10 : 11 },
      bgcolor: 'rgba(0,0,0,0)',
      borderwidth: 0,
      itemsizing: 'trace'
    },
    hoverlabel: { font_size: 13 },
    annotations: hasData ? buildPlotAnnotations(payload, hasData) : [{
      text: 'No chart data yet for this bottle.',
      xref: 'paper',
      yref: 'paper',
      x: 0.5,
      y: 0.5,
      showarrow: false,
      font: { size: 16, color: theme.font_color }
    }]
  };
}

async function ensurePlotInitialized() {
  if (!window.Plotly) {
    console.error('Plotly not loaded. Ensure plotly script loads before app.js (or both use defer).');
    return null;
  }
  const gd = document.getElementById('graph');
  if (!gd) return null;
  if (!plotInitialized) {
    await Plotly.newPlot(gd, [], buildPlotLayout('Price history', false), buildPlotConfig());
    plotInitialized = true;
  }
  return gd;
}

function buildPlotTraces(payload) {
  const traces = [];
  const retailers = Object.entries(payload?.retailers || {});
  const { isMobile } = getPlotViewportFlags();
  const snapshots = buildMarketSnapshots(payload);

  if (snapshots.length) {
    const x = snapshots.map((point) => point.ts);
    const low = snapshots.map((point) => point.min);
    const high = snapshots.map((point) => point.max);
    const rangeCustomData = snapshots.map((point) => [point.min, point.minRetailer, point.retailerCount]);
    const primaryWidth = isMobile ? 3 : 3.45;

    traces.push({
      type: 'scatter',
      x,
      y: low,
      mode: 'lines',
      name: 'Range floor',
      hoverinfo: 'skip',
      line: { width: 0, color: 'rgba(0,0,0,0)', shape: 'hv' },
      showlegend: false,
      opacity: 0,
      meta: { aggregateRole: 'range-floor', baseOpacity: 0, baseLineWidth: 0 },
    });

    if (retailers.length > 1) {
      traces.push({
        type: 'scatter',
        x,
        y: high,
        customdata: rangeCustomData,
        mode: 'lines',
        name: 'Retailer range',
        hovertemplate: '%{x|%d %b}<br>Range R%{customdata[0]:.2f}-R%{y:.2f}<extra></extra>',
        line: { width: 0, color: 'rgba(224,177,92,0)', shape: 'hv' },
        fill: 'tonexty',
        fillcolor: 'rgba(224,177,92,0.105)',
        showlegend: false,
        opacity: 0.88,
        meta: { aggregateRole: 'range', baseOpacity: 0.88, baseLineWidth: 0 },
      });
    }

    traces.push({
      type: 'scatter',
      x,
      y: low,
      customdata: rangeCustomData,
      mode: 'lines',
      name: 'Lowest available',
      hovertemplate: '%{x|%d %b}<br>Low R%{y:.2f}<extra></extra>',
      line: { width: primaryWidth, shape: 'hv', color: '#e0b15c' },
      showlegend: false,
      opacity: 1,
      meta: { aggregateRole: 'market-low', baseOpacity: 1, baseLineWidth: primaryWidth },
    });

    const signalPoints = getPlotSignalPoints(payload);
    if (signalPoints.length) {
      traces.push({
        type: 'scatter',
        x: signalPoints.map((point) => point.event.ts),
        y: signalPoints.map((point) => point.event.min),
        customdata: signalPoints.map((point) => point.labels[0] || 'Price signal'),
        mode: 'markers',
        name: 'Price signals',
        hovertemplate: '%{customdata}<br>R%{y:.2f}<extra></extra>',
        marker: {
          size: isMobile ? 7 : 8,
          color: '#fff2bf',
          line: { width: 1.2, color: 'rgba(8,5,3,0.86)' },
          symbol: 'circle',
        },
        showlegend: false,
        opacity: 0.95,
        meta: { aggregateRole: 'signals', baseOpacity: 0.95, baseLineWidth: 0 },
      });
    }
  }

  let colorIx = 0;

  for (const [retailer, series] of retailers) {
    const ts = Array.isArray(series?.ts) ? series.ts : [];
    const price = Array.isArray(series?.price) ? series.price : [];
    const compare = Array.isArray(series?.compare) ? series.compare : [];
    if (!ts.length || !price.some((v) => v != null)) continue;

    const color = PLOT_PALETTE[colorIx % PLOT_PALETTE.length];
    const traceStyle = getTraceStyle(ts.length);
    colorIx += 1;

    traces.push({
      type: 'scatter',
      x: ts,
      y: price,
      mode: traceStyle.mode,
      name: retailer,
      legendgroup: retailer,
      meta: { retailer, isCompare: false, isRetailerDetail: true, baseLineWidth: traceStyle.lineWidth },
      hovertemplate: `${retailer}<br>%{x|%d %b}<br>R%{y:.2f}<extra></extra>`,
      hoverinfo: 'skip',
      line: { width: traceStyle.lineWidth, shape: 'hv', color },
      marker: { size: traceStyle.markerSize, color, line: { width: 0.7, color: 'rgba(8,5,3,0.78)' } },
      showlegend: false,
      opacity: 0,
    });

    if (compare.some((v) => v != null)) {
      const compareWidth = isMobile ? 1.35 : 1.55;
      traces.push({
        type: 'scatter',
        x: ts,
        y: compare,
        mode: 'lines',
        name: `${retailer} (compare-at)`,
        legendgroup: retailer,
        meta: { retailer, isCompare: true, isRetailerDetail: true, baseLineWidth: compareWidth },
        hovertemplate: `${retailer}<br>Compare R%{y:.2f}<extra></extra>`,
        hoverinfo: 'skip',
        line: { dash: 'dash', width: compareWidth, shape: 'hv', color },
        opacity: 0,
        showlegend: false,
      });
    }
  }

  return traces;
}

async function renderPlotForProduct(name, payload) {
  const gd = await ensurePlotInitialized();
  if (!gd) return;

  renderPlotSignalRow(payload);
  const traces = buildPlotTraces(payload);
  await Plotly.react(gd, traces, buildPlotLayout(name, traces.length > 0, payload), buildPlotConfig());
  applyRetailerHighlight(getEffectiveRetailerHighlight());
}

function getEffectiveRetailerHighlight() {
  return previewRetailerHighlight || lockedRetailerHighlight || "";
}

function getTraceRetailer(trace) {
  return String(trace?.meta?.retailer || trace?.legendgroup || trace?.name || '')
    .replace(/\s+\(compare-at\)$/i, '')
    .trim();
}

function syncRetailerHighlightDom(retailer = getEffectiveRetailerHighlight()) {
  const active = String(retailer || '');
  document.querySelectorAll('[data-retailer-highlight]').forEach((el) => {
    const match = active && el.getAttribute('data-retailer-highlight') === active;
    el.classList.toggle('is-highlighted', !!match);
    el.classList.toggle('is-dimmed', !!active && !match);
    if (el.matches('tr.ret-row')) {
      if (match) el.setAttribute('aria-current', 'true');
      else el.removeAttribute('aria-current');
    }
  });
}

function applyRetailerHighlight(retailer = getEffectiveRetailerHighlight()) {
  const active = String(retailer || '');
  syncRetailerHighlightDom(active);

  const gd = document.getElementById('graph');
  if (!gd || !window.Plotly || !plotInitialized || !Array.isArray(gd.data) || !gd.data.length) return;

  const opacities = [];
  const widths = [];
  const hoverInfos = [];
  for (const trace of gd.data) {
    const traceRetailer = getTraceRetailer(trace);
    const isCompare = !!trace?.meta?.isCompare;
    const isDetail = !!trace?.meta?.isRetailerDetail;
    const aggregateRole = trace?.meta?.aggregateRole || '';
    const baseOpacity = Number(trace?.meta?.baseOpacity ?? (isCompare ? 0 : 1));
    const baseWidth = Number(trace?.meta?.baseLineWidth || trace?.line?.width || (isCompare ? 1.5 : 2.5));
    const isMatch = active && traceRetailer === active;

    if (aggregateRole) {
      if (aggregateRole === 'range-floor') {
        opacities.push(0);
      } else if (aggregateRole === 'range') {
        opacities.push(active ? 0.24 : baseOpacity);
      } else if (aggregateRole === 'market-low') {
        opacities.push(active ? 0.38 : baseOpacity);
      } else {
        opacities.push(active ? 0.52 : baseOpacity);
      }
      widths.push(baseWidth);
      hoverInfos.push(active && aggregateRole !== 'signals' ? 'skip' : 'all');
      continue;
    }

    if (isDetail) {
      opacities.push(isMatch ? (isCompare ? 0.24 : 1) : 0);
      widths.push(isMatch ? baseWidth + (isCompare ? 0.25 : 0.75) : baseWidth);
      hoverInfos.push(isMatch ? 'all' : 'skip');
      continue;
    }

    opacities.push(baseOpacity);
    widths.push(baseWidth);
    hoverInfos.push('all');
  }

  Plotly.restyle(gd, {
    opacity: opacities,
    'line.width': widths,
    hoverinfo: hoverInfos,
  });
}

function setPreviewRetailerHighlight(retailer) {
  previewRetailerHighlight = String(retailer || '');
  applyRetailerHighlight(getEffectiveRetailerHighlight());
}

function toggleLockedRetailerHighlight(retailer) {
  const value = String(retailer || '');
  lockedRetailerHighlight = lockedRetailerHighlight === value ? '' : value;
  previewRetailerHighlight = '';
  applyRetailerHighlight(getEffectiveRetailerHighlight());
}

function clearRetailerHighlight() {
  lockedRetailerHighlight = '';
  previewRetailerHighlight = '';
  applyRetailerHighlight('');
}

function renderProductImage(name) {
  const img = document.getElementById('product-image');
  if (!img) return;
  const candidates = getCatalogImageCandidates(name);
  img.onerror = null;
  if (candidates.length) {
    const loadCandidate = (index) => {
      if (index >= candidates.length) {
        img.removeAttribute('src');
        img.style.display = 'none';
        img.classList.remove('is-zoomable');
        img.removeAttribute('aria-label');
        return;
      }
      img.dataset.imageIndex = String(index);
      img.src = candidates[index];
    };
    img.onerror = () => {
      const index = Number.parseInt(img.dataset.imageIndex || '0', 10);
      loadCandidate(index + 1);
    };
    loadCandidate(0);
    img.style.display = 'block';
    img.classList.add('is-zoomable');
    img.setAttribute('aria-label', 'Open larger product image');
  } else {
    img.removeAttribute('src');
    img.style.display = 'none';
    img.classList.remove('is-zoomable');
    img.removeAttribute('aria-label');
  }
}

function renderRetailerLinks(rows) {
  const linksDiv = document.getElementById('retailer-links');
  if (!linksDiv) return;
  const linkRows = rows
    .filter((x) => x?.url)
    .slice()
    .sort((a, b) => {
      const aPrice = hasValidCurrentPrice(a.current_price) ? Number(a.current_price) : Infinity;
      const bPrice = hasValidCurrentPrice(b.current_price) ? Number(b.current_price) : Infinity;
      return (aPrice - bPrice) || String(a.retailer || '').localeCompare(String(b.retailer || ''));
    });
  const bestRow = linkRows[0];
  if (!bestRow) {
    linksDiv.innerHTML = '';
    return;
  }

  const moreCount = Math.max(0, linkRows.length - 1);
  const retailer = bestRow.retailer || "Retailer";
  const price = hasValidCurrentPrice(bestRow.current_price) ? fmtZAR(bestRow.current_price) : '';
  linksDiv.innerHTML = `
    <div class="retailer-links__list">
        <a class="retailer-link-card is-best" href="${escHtml(bestRow.url)}" target="_blank" rel="noopener" data-retailer-highlight="${escHtml(retailer)}" title="Open ${escHtml(retailer)} product page">
          <span class="retailer-link-card__rank">Cheapest retailer</span>
          <strong class="retailer-link-card__name">${escHtml(retailer)}</strong>
          <span class="retailer-link-card__price">${price || '-'}</span>
          <span class="retailer-link-card__action">Open retailer</span>
        </a>
      <button type="button" class="retailer-links__all" data-scroll-target="#retailer-details">${moreCount ? `Compare ${linkRows.length} retailers` : 'Retailer details'}</button>
    </div>
  `;
  syncRetailerHighlightDom();
}

function renderSummaryCards(summary) {
  const minEl = document.getElementById('stat-current-min');
  const minRetEl = document.getElementById('stat-current-min-ret');
  const maxEl = document.getElementById('stat-current-max');
  const spreadEl = document.getElementById('stat-spread');
  if (minEl) minEl.textContent = (summary && summary.current_min != null) ? fmtZAR(summary.current_min) : '-';
  if (minRetEl) minRetEl.textContent = (summary && summary.current_min_retailer) ? summary.current_min_retailer : '-';
  if (maxEl) maxEl.textContent = (summary && summary.current_max != null) ? fmtZAR(summary.current_max) : '-';
  if (spreadEl) {
    const min = Number(summary?.current_min);
    const max = Number(summary?.current_max);
    spreadEl.textContent = Number.isFinite(min) && Number.isFinite(max) ? `Spread ${fmtZAR(Math.max(0, max - min))}` : 'Spread -';
  }
  updateRetailerSummaryMetrics(summary, []);
}

function updateRetailerSummaryMetrics(summary, rows = []) {
  const countEl = document.getElementById('stat-retailer-count');
  const nearLowEl = document.getElementById('stat-near-low-count');
  const pricedRows = Array.isArray(rows)
    ? rows.filter((row) => hasValidCurrentPrice(row?.current_price))
    : [];
  const min = Number(summary?.current_min);
  const nearLowCount = Number.isFinite(min)
    ? pricedRows.filter((row) => Number(row.current_price) <= min * 1.02).length
    : 0;
  if (countEl) countEl.textContent = pricedRows.length ? String(pricedRows.length) : '-';
  if (nearLowEl) nearLowEl.textContent = pricedRows.length ? `Near low ${nearLowCount}` : 'Near low -';
}

function renderSpotlightInsight(summary, rows) {
  const el = document.getElementById('spotlight-insight');
  if (!el) return;
  el.textContent = '';
}

function resolveBrandFacetValue(label) {
  const key = norm(label);
  if (!key) return null;

  const fromDom = Array.from(
    document.querySelectorAll('#filterbar input[data-filter-checkbox="brand"]')
  ).map((el) => String(el.value || '').trim()).filter(Boolean);
  const pool = fromDom.length ? fromDom : ((getFacets().brand?.values) || []);
  const exact = pool.find((v) => norm(v) === key);
  if (exact) return exact;
  return pool.find((v) => norm(v).startsWith(key) || key.startsWith(norm(v))) || null;
}

function applyBrandFacetFilter(brandLabel) {
  const canonical = resolveBrandFacetValue(brandLabel);
  if (!canonical) return false;
  const wrap = document.getElementById('filterbar');
  if (!wrap) return false;
  const checks = Array.from(wrap.querySelectorAll('input[data-filter-checkbox="brand"]'));
  if (!checks.length) return false;

  let found = false;
  checks.forEach((c) => {
    const value = String(c.value || '');
    const isMatch = norm(value) === norm(canonical);
    if (isMatch) found = true;
    if (value === BLANK_FILTER_VALUE) {
      c.checked = false;
      return;
    }
    c.checked = c.checked || isMatch;
  });
  if (!found) return false;

  const apply = window.__applyAndRefreshFilters;
  if (typeof apply === 'function') {
    apply();
  } else {
    const changed = checks.find((c) => c.checked);
    if (changed) changed.dispatchEvent(new Event('change', { bubbles: true }));
  }
  return true;
}

function getTasteProfileLeaders(profile, limit = 3) {
  const categories = profile?.compositionCategories || profile?.categories || {};
  return TASTE_AXES
    .map((axis, index) => ({
      ...axis,
      index,
      value: Number(categories[axis.key]) || 0,
    }))
    .filter((axis) => axis.value > 0.001)
    .sort((left, right) => (right.value - left.value) || (left.index - right.index))
    .slice(0, limit);
}

function formatTasteLeaderList(labels) {
  if (labels.length < 2) return labels[0] || "";
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")} and ${labels.at(-1)}`;
}

function buildTasteWheelSvg(profile, productName) {
  const size = 300;
  const center = size / 2;
  const radius = 92;
  const categories = profile.compositionCategories || profile.categories;
  let offset = 0;
  const segments = TASTE_AXES.map((axis) => {
    const share = Math.max(0, Number(categories[axis.key]) || 0) * 100;
    const segmentOffset = offset;
    offset += share;
    if (share <= 0.001) return "";

    const gap = share >= 1.5 ? 0.75 : 0;
    const visibleShare = Math.max(0, share - gap);
    const label = `${axis.label}: ${Math.round(share)}% of the scored flavour profile`;
    return `
      <circle class="taste-wheel__segment" style="--taste-tone:${axis.tone}" cx="${center}" cy="${center}" r="${radius}" pathLength="100" stroke-dasharray="${visibleShare.toFixed(3)} ${(100 - visibleShare).toFixed(3)}" stroke-dashoffset="${(-segmentOffset).toFixed(3)}" transform="rotate(-90 ${center} ${center})">
        <title>${escHtml(label)}</title>
      </circle>
    `;
  }).join("");
  const description = TASTE_AXES
    .map((axis) => `${axis.label} ${Math.round(Number(categories[axis.key]) * 100)} percent of the scored flavour profile`)
    .join(", ");
  const activeFamilyCount = Number(profile.activeFamilyCount) || 0;

  return `
    <svg class="taste-wheel" viewBox="0 0 ${size} ${size}" role="img" aria-labelledby="taste-wheel-title taste-wheel-description">
      <title id="taste-wheel-title">Taste profile for ${escHtml(productName)}</title>
      <desc id="taste-wheel-description">${escHtml(description)}</desc>
      <circle class="taste-wheel__track" cx="${center}" cy="${center}" r="${radius}"></circle>
      ${segments}
      <circle class="taste-wheel__inner" cx="${center}" cy="${center}" r="63"></circle>
      <text class="taste-wheel__caption" x="${center}" y="132" text-anchor="middle">FLAVOUR</text>
      <text class="taste-wheel__count" x="${center}" y="163" text-anchor="middle">${activeFamilyCount}</text>
      <text class="taste-wheel__caption" x="${center}" y="181" text-anchor="middle">FAMILIES</text>
    </svg>
  `;
}

function formatTasteCloseness(distance) {
  const value = Number(distance);
  if (!Number.isFinite(value)) return "Similar profile";
  if (value <= 0.025) return "Exceptionally close";
  if (value <= 0.05) return "Very close profile";
  if (value <= 0.09) return "Close profile";
  return "Related profile";
}

function getTasteMatchInitials(name) {
  return String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "W";
}

function renderTasteProfile(name, rawVector = null) {
  const wrap = document.getElementById("taste-profile-wrap");
  const content = document.getElementById("taste-profile");
  const nav = document.getElementById("taste-profile-nav");
  if (!wrap || !content) return;

  const profile = normalizeTasteVector(rawVector || getCatalogMeta(name)?.taste_vector);
  if (!profile) {
    wrap.hidden = true;
    content.innerHTML = "";
    if (nav) nav.hidden = true;
    return;
  }

  const leaders = getTasteProfileLeaders(profile);
  const leaderNames = leaders.map((axis) => axis.label.toLowerCase());
  const profileHeadline = leaderNames.length
    ? `Led by ${formatTasteLeaderList(leaderNames)}`
    : "A subtle taste profile";
  const axisRows = TASTE_AXES.map((axis) => {
    const value = Number((profile.compositionCategories || profile.categories)[axis.key]);
    const score = Math.round(value * 100);
    const isLeader = leaders.some((leader) => leader.key === axis.key);
    return `
      <div class="taste-axis${isLeader ? " is-leading" : ""}" role="listitem">
        <span class="taste-axis__label"><i class="taste-axis__swatch" style="--taste-tone:${axis.tone}" aria-hidden="true"></i>${escHtml(axis.label)}</span>
        <span class="taste-axis__track" aria-hidden="true"><span style="--taste-score:${(value * 100).toFixed(2)}%;--taste-tone:${axis.tone}"></span></span>
        <strong class="taste-axis__value" aria-label="${escHtml(axis.label)}: ${score} percent of the scored profile">${score}%</strong>
      </div>
    `;
  }).join("");

  const pricedCatalogItems = CATALOG_ITEMS.filter((item) => hasValidCurrentPrice(item?.stats?.current_min));
  const sourceMeta = getCatalogMeta(name);
  const matches = rankTasteMatches(name, profile, pricedCatalogItems, {
    limit: 4,
    sourceMeta,
    diversify: true,
  });
  const matchesMarkup = matches.length
    ? `<div class="taste-matches__grid">${matches.map((match) => {
        const imageUrl = getCatalogImage(match.name);
        const price = Number(match.item?.stats?.current_min);
        const matchLeaders = getTasteProfileLeaders(match.profile, 2).map((axis) => axis.label).join(" · ");
        const familiarReasons = getFamiliarMatchReasons(match, 2);
        const initials = getTasteMatchInitials(match.name);
        const media = imageUrl
          ? `<div class="taste-match__media" data-taste-initials="${escHtml(initials)}"><img src="${escHtml(imageUrl)}" alt="" loading="lazy" decoding="async"></div>`
          : `<div class="taste-match__media taste-match__media--empty" aria-hidden="true"><span>${escHtml(initials)}</span></div>`;
        return `
          <a class="taste-match" href="${escHtml(buildProductPageUrl(match.name))}" aria-label="View ${escHtml(match.name)}, ${escHtml(formatOverallMatchStrength(match.similarityScore))}">
            ${media}
            <span class="taste-match__body">
              <span class="taste-match__rank">${escHtml(formatOverallMatchStrength(match.similarityScore))}</span>
              <strong class="taste-match__name">${escHtml(match.name)}</strong>
              ${familiarReasons.length ? `<span class="taste-match__notes">${escHtml(familiarReasons.join(" · "))}</span>` : (matchLeaders ? `<span class="taste-match__notes">${escHtml(matchLeaders)}</span>` : "")}
              <span class="taste-match__price">${Number.isFinite(price) ? `From ${fmtZAR(price)}` : "View product"}</span>
            </span>
            <span class="taste-match__arrow" aria-hidden="true">→</span>
          </a>
        `;
      }).join("")}</div>`
    : `<p class="taste-matches__empty muted">More tasting-note coverage is needed before reliable taste matches can be suggested.</p>`;

  content.innerHTML = `
    <div class="taste-profile__overview">
      <div class="taste-profile__wheel-card">
        ${buildTasteWheelSvg(profile, name)}
      </div>
      <div class="taste-profile__detail">
        <h3>${escHtml(profileHeadline)}</h3>
        <div class="taste-axis-list" role="list" aria-label="Taste vector values">${axisRows}</div>
      </div>
    </div>
    <section class="taste-matches" aria-labelledby="taste-matches-title">
      <div class="taste-matches__head">
        <div>
          <h3 id="taste-matches-title">Similar bottles</h3>
        </div>
        <div class="taste-matches__aside">
          <a class="taste-matches__explore" href="${escHtml(buildTasteBrowseUrl(name))}">See all similar bottles</a>
        </div>
      </div>
      ${matchesMarkup}
    </section>
  `;
  content.querySelectorAll(".taste-match__media img").forEach((img) => {
    const showFallback = () => {
      const media = img.closest(".taste-match__media");
      if (!media || !img.isConnected) return;
      const fallback = document.createElement("span");
      fallback.textContent = media.dataset.tasteInitials || "W";
      media.replaceChildren(fallback);
      media.classList.add("taste-match__media--empty");
      media.setAttribute("aria-hidden", "true");
    };
    img.addEventListener("error", showFallback, { once: true });
    if (img.complete && !img.naturalWidth) showFallback();
  });
  wrap.hidden = false;
  if (nav) nav.hidden = false;
}

function renderIdentityPanel(name, payload) {
  const factsEl = document.getElementById('identity-facts');
  const maturationEl = document.getElementById('identity-maturation');
  const tastingWrapEl = document.getElementById('identity-tasting-wrap');
  const tastingEl = document.getElementById('identity-tasting');
  const linksEl = document.getElementById('identity-links');
  const titleEl = document.getElementById('focus-title');
  if (titleEl) titleEl.textContent = name;

  if (!factsEl || !maturationEl || !linksEl) return;
  const meta = payload?.meta || {};
  const fallback = getCatalogMeta(name);
  renderTasteProfile(name, meta.taste_vector || fallback.taste_vector);
  const toStringArray = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => String(item ?? '').trim()).filter(Boolean);
    }
    const text = String(value ?? '').trim();
    return text ? [text] : [];
  };
  const toStringValue = (value) => {
    const text = String(value ?? '').replace(/\s+/g, ' ').trim();
    return text || null;
  };
  const formatAbvValue = (value) => {
    const text = String(value ?? '').replace(/\s+/g, ' ').trim();
    if (!text) return null;
    const cleaned = text
      .replace(/\bABV\b/ig, '')
      .replace(/\bAlc(?:ohol)?\.?\b/ig, '')
      .replace(/%/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!cleaned) return null;
    const numeric = cleaned.match(/\d+(?:[.,]\d+)?/);
    if (numeric) return numeric[0].replace(',', '.');
    return cleaned;
  };
  const merged = {
    brand: toStringValue(meta.brand || fallback.brand),
    region: toStringValue(meta.region || fallback.region),
    country: toStringValue(meta.country || fallback.country),
    ageStatement: toStringValue(meta.age_statement),
    age: toStringValue(meta.age || fallback.age),
    whiskyFamily: toStringValue(meta.whisky_family || fallback.whisky_family),
    productionStyle: toStringValue(meta.production_style || fallback.production_style || meta.whisky_type || fallback.whisky_type),
    stillTerms: toStringArray(meta.still_terms || fallback.still_terms),
    strengthLabel: toStringValue(meta.strength_label || fallback.strength_label),
    peated: toStringValue(meta.peated_state || meta.peated || fallback.peated_state || fallback.peated),
    abv: formatAbvValue(meta.abv || fallback.abv),
    cask: toStringValue(meta.cask || fallback.cask),
    colour: toStringValue(meta.colour_text || fallback.colour_text),
    nose: toStringValue(meta.nose_text || fallback.nose_text),
    palate: toStringValue(meta.palate_text || fallback.palate_text),
    finish: toStringValue(meta.finish_text || fallback.finish_text),
    description: toStringValue(meta.description || fallback.description),
    website: toStringValue(meta.website || fallback.website),
    related: toStringValue(meta.related || fallback.related),
  };
  const ageDisplay = formatAgeDisplay(merged.age, { ageStatement: merged.ageStatement });

  const headlineParts = [merged.productionStyle, merged.whiskyFamily].filter(Boolean);
  const originParts = [merged.region, merged.country].filter(Boolean);
  const portraitMeta = [];
  if (merged.brand) portraitMeta.push(merged.brand);
  if (originParts.length) portraitMeta.push(originParts.join(', '));
  if (ageDisplay) portraitMeta.push(ageDisplay);
  const descriptionText = merged.description || "No brand profile is available.";

  const ribbonItems = [
    merged.strengthLabel ? { label: 'Strength', value: merged.strengthLabel } : null,
    merged.peated ? { label: 'Peat', value: merged.peated } : null,
  ].filter(Boolean);

  const ledgerRows = [
    merged.abv ? { label: 'ABV', value: `${merged.abv}%` } : null,
    merged.stillTerms.length ? { label: 'Stills', value: merged.stillTerms.join(', ') } : null,
    originParts.length ? { label: 'Origin', value: originParts.join(', ') } : null,
    merged.brand ? { label: 'House', value: merged.brand } : null,
  ].filter(Boolean);

  factsEl.innerHTML = `
    <div class="identity-profile">
      <div class="identity-portrait">
        <div class="identity-portrait__headline">${escHtml(headlineParts.join(' · ') || name)}</div>
        ${portraitMeta.length ? `<div class="identity-portrait__sub">${escHtml(portraitMeta.join(' · '))}</div>` : ''}
        <p class="identity-portrait__description muted">${escHtml(descriptionText)}</p>
        ${ribbonItems.length ? `<div class="identity-ribbon">${ribbonItems.map((item) => `<span class="identity-ribbon__item"><span class="identity-ribbon__label">${escHtml(item.label)}</span><span class="identity-ribbon__value">${escHtml(item.value)}</span></span>`).join('')}</div>` : ''}
      </div>
      ${ledgerRows.length ? `<div class="identity-ledger">${ledgerRows.map((row) => `<div class="identity-ledger__row"><div class="identity-ledger__label">${escHtml(row.label)}</div><div class="identity-ledger__value">${escHtml(row.value)}</div></div>`).join('')}</div>` : ''}
    </div>
  `;

  maturationEl.textContent = merged.cask || 'No cask or maturation details available.';

  if (tastingWrapEl && tastingEl) {
    const tastingNotes = [
      merged.colour ? { label: 'Colour', value: merged.colour, tone: 'colour' } : null,
      merged.nose ? { label: 'Nose', value: merged.nose, tone: 'nose' } : null,
      merged.palate ? { label: 'Palate', value: merged.palate, tone: 'palate' } : null,
      merged.finish ? { label: 'Finish', value: merged.finish, tone: 'finish' } : null,
    ].filter(Boolean);

    if (tastingNotes.length) {
      tastingWrapEl.hidden = false;
      tastingEl.innerHTML = `
        <div class="identity-flight">
          ${tastingNotes.map((note) => `
            <article class="identity-flight__stop identity-flight__stop--${escHtml(note.tone)}">
              <div class="identity-flight__label">${escHtml(note.label)}</div>
              <p class="identity-flight__text">${escHtml(note.value)}</p>
            </article>
          `).join('')}
        </div>
      `;
    } else {
      tastingWrapEl.hidden = true;
      tastingEl.innerHTML = '';
    }
  }

  const linkParts = [];
  if (merged.website) {
    linkParts.push(`<a class="btn-link identity-link" href="${escHtml(merged.website)}" target="_blank" rel="noopener">Official site</a>`);
  }
  const related = String(merged.related || '')
    .split('|')
    .map((x) => x.trim())
    .filter(Boolean)
    .slice(0, 6);
  const canApplyBrandFilter = !!document.getElementById('filterbar');
  for (const relatedName of related) {
    const brandMatch = resolveBrandFacetValue(relatedName);
    if (brandMatch && canApplyBrandFilter) {
      linkParts.push(`<button type="button" class="identity-link identity-link--related" data-related-brand="${escHtml(brandMatch)}" title="Add brand filter">${escHtml(relatedName)}</button>`);
    } else {
      linkParts.push(`<span class="identity-link identity-link--muted">${escHtml(relatedName)}</span>`);
    }
  }
  linksEl.innerHTML = linkParts.join('');
}

const PRODUCT_MARKET_LOADER_DELAY_MS = 140;
let productMarketLoaderTimer = 0;

function setProductMarketLoading(isLoading) {
  const loader = document.getElementById("product-market-loader");
  const chapter = loader?.closest(".product-market-chapter");
  if (!loader || !chapter) return;
  window.clearTimeout(productMarketLoaderTimer);
  if (!isLoading) {
    loader.hidden = true;
    chapter.classList.remove("is-loading");
    return;
  }
  loader.hidden = true;
  productMarketLoaderTimer = window.setTimeout(() => {
    loader.hidden = false;
    chapter.classList.add("is-loading");
  }, PRODUCT_MARKET_LOADER_DELAY_MS);
}

function renderRetailerTable(name, rows, variantsByRetailer = {}) {
  const sorted = sortRetailerRows(rows);
  const tbody = document.querySelector('#retailer-table tbody');
  if (!tbody) return;
  const table = tbody.closest('#retailer-table');
  const grid = table?.closest('.grid');
  table?.classList.add('retailer-rail-table');
  grid?.classList.add('retailer-rail-grid');

  if (!sorted.length) {
    tbody.innerHTML = `
      <tr class="retailer-empty">
        <td colspan="4">No retailer prices captured yet.</td>
      </tr>
    `;
    syncRetailerHighlightDom();
    return;
  }

  const prices = sorted
    .map((r) => Number(r.current_price))
    .filter((price) => Number.isFinite(price));
  const bestPrice = prices.length ? Math.min(...prices) : Infinity;
  const highestPrice = prices.length ? Math.max(...prices) : bestPrice;
  const priceSpan = Math.max(0, highestPrice - bestPrice);

  tbody.innerHTML = sorted.map((r) => {
    const currentPrice = Number(r.current_price);
    const hasPrice = Number.isFinite(currentPrice);
    const delta = hasPrice && Number.isFinite(bestPrice) ? currentPrice - bestPrice : null;
    const railPct = hasPrice && priceSpan > 0 ? Math.max(0, Math.min(100, ((currentPrice - bestPrice) / priceSpan) * 100)) : 0;
    const discountPct = Number(r.discount_pct);
    const hasDiscount = Number.isFinite(discountPct) && Math.abs(discountPct) >= 0.05;
    const discountLabel = hasDiscount
      ? (discountPct >= 0 ? `-${discountPct.toFixed(0)}%` : `+${Math.abs(discountPct).toFixed(0)}%`)
      : '';
    const discountCls = discountPct > 0 ? 'retailer-offer__badge--down' : 'retailer-offer__badge--up';
    const isNearLow = hasPrice && Number.isFinite(bestPrice) && currentPrice <= bestPrice * 1.02;
    const isBest = hasPrice && Math.abs(currentPrice - bestPrice) < 0.005;
    const retailerKey = r.retailer || '';
    const retailerLabel = retailerKey || 'Retailer';
    const positionLabel = isBest ? 'Best' : (isNearLow ? 'Near low' : '');
    const visitLink = r.url
      ? `<a class="retailer-offer__visit" href="${escHtml(r.url)}" target="_blank" rel="noopener" data-retailer-highlight="${escHtml(retailerKey)}" aria-label="Open ${escHtml(retailerLabel)} product page">Visit</a>`
      : `<span class="retailer-offer__visit retailer-offer__visit--empty" aria-hidden="true"></span>`;
    const rowClasses = ['ret-row'];
    if (isBest) rowClasses.push('is-best');
    if (isNearLow) rowClasses.push('is-near-low');
    const detailsBlock = buildRetailerOfferDetails(r, variantsByRetailer, retailerKey);
    const title = [
      retailerKey || 'Retailer',
      r.variant || '',
    ].filter(Boolean).join(' | ');
    return `
    <tr class="${rowClasses.join(' ')}" data-retailer="${escHtml(retailerKey)}" data-retailer-highlight="${escHtml(retailerKey)}" tabindex="0" aria-expanded="false" aria-label="Show details for ${escHtml(retailerLabel)} and highlight it on the chart" title="${escHtml(title)}">
      <td colspan="4">
        <div class="retailer-offer" style="--price-pos:${railPct.toFixed(2)}%;">
          <div class="retailer-offer__name">
            <span>${escHtml(r.retailer || "-")}</span>
            ${positionLabel ? `<span class="retailer-offer__badge retailer-offer__badge--position">${escHtml(positionLabel)}</span>` : ''}
            ${discountLabel ? `<span class="retailer-offer__badge ${discountCls}">${escHtml(discountLabel)}</span>` : ''}
          </div>
          <div class="retailer-offer__rail" aria-hidden="true"><span></span></div>
          <div class="retailer-offer__price">
            <strong>${fmtZAR(r.current_price)}</strong>
            <span>${delta == null ? '-' : fmtDeltaZAR(delta)}</span>
          </div>
          ${visitLink}
        </div>
      </td>
    </tr>
    <tr class="var-row" data-retailer="${escHtml(retailerKey)}" style="display:none;">
      <td colspan="4">${detailsBlock}</td>
    </tr>
    `;
  }).join('');
  syncRetailerHighlightDom();
}

async function setProduct(name) {
  if (!name || !productNameSet.has(name)) return;
  const productChanged = currentProduct !== name;
  currentProduct = name;
  if (productChanged) clearRetailerHighlight();
  syncPageChrome(name);
  const requestedProduct = name;
  const catalogSummary = getCatalogSummary(name);

  const titleEl = document.getElementById('plot-product-title');
  if (titleEl) titleEl.textContent = 'Price history';
  renderPlotSignalRow(null);

  syncProductControls(name);
  renderProductImage(name);
  renderTasteProfile(name, getCatalogMeta(name)?.taste_vector);
  renderSummaryCards(catalogSummary);
  renderRetailerLinks([]);
  renderRetailerTable(name, [], {});
  const spotlightEl = document.getElementById('spotlight-insight');
  if (spotlightEl) spotlightEl.textContent = 'Loading retailer detail...';
  const dealMetaEl = document.getElementById('deal-meta');
  if (dealMetaEl) dealMetaEl.textContent = '';
  setProductMarketLoading(true);

  try {
    const payload = await fetchProductPayload(name);
    if (currentProduct !== requestedProduct) return;
    const rows = Array.isArray(payload?.retailer_rows) && payload.retailer_rows.length
      ? payload.retailer_rows
      : getCatalogRetailerRows(name);
    const summary = payload?.summary || catalogSummary;
    const variantsByRetailer = payload?.variants_by_retailer || {};

    renderRetailerLinks(rows);
    renderSummaryCards(summary);
    updateRetailerSummaryMetrics(summary, rows);
    renderRetailerTable(name, rows, variantsByRetailer);
    renderSpotlightInsight(summary, rows);
    await renderPlotForProduct(name, payload);
    if (currentProduct !== requestedProduct) return;
    renderBadges();
    if (payload) renderDealMeta(payload);
    else renderDealMetaFromRows(rows);
    renderIdentityPanel(name, payload);

    updateShelfActive(name);
    measureAndResize();
  } finally {
    if (currentProduct === requestedProduct) setProductMarketLoading(false);
  }
}

function bindPlotExpandOnce(){
  if (window.__plotExpandInit) return;
  window.__plotExpandInit = true;

  const btn = document.getElementById('plot-expand');
  const graphEl = document.getElementById('graph');
  if (!btn || !graphEl) return;

  // Ensure modal exists (either id or class)
  const modal = document.getElementById('plot-modal') || document.querySelector('.plot-modal');
  if (!modal) return;

  // Optional ids; we'll fall back safely
  const closeBtn = document.getElementById('plot-close') || modal.querySelector('[data-plot-close]');
  const modalBody = document.getElementById('plot-modal-body') || modal.querySelector('.plot-modal__body') || modal.querySelector('.plot-modal__inner') || modal;

  let lastFocusEl = null;
  let originalParent = null;
  let originalNext = null;

  function ensureModalAtBody(){
    if (modal.parentElement !== document.body) document.body.appendChild(modal);
  }

  function setBackgroundInert(on){
    // Inert the app container, but never the modal (we move modal to body)
    const root =
      document.querySelector('#app')
      || document.querySelector('main')
      || document.querySelector('.container')
      || document.body;

    if (root === document.body){
      [...document.body.children].forEach(el => {
        if (el === modal) return;
        if (on) el.setAttribute('inert','');
        else el.removeAttribute('inert');
      });
      return;
    }
    // modal is on body now; safe to inert root
    if (on) root.setAttribute('inert','');
    else root.removeAttribute('inert');
  }

  function open(){
    ensureModalAtBody();
    lastFocusEl = document.activeElement;

    // Remember original position once
    if (!originalParent){
      originalParent = graphEl.parentElement;
      originalNext = graphEl.nextSibling;
    }
    // Move plot into modal
    modalBody.appendChild(graphEl);

    document.body.classList.add('plot-open');
    modal.classList.add('is-open');
    setBackgroundInert(true);

    // Focus close button (prevents focused element being inside inert background)
    const focusTarget = closeBtn || modal.querySelector('button') || modal;
    if (focusTarget && focusTarget.focus) focusTarget.focus();

    // Resize Plotly after it is visible/in new container
    requestAnimationFrame(() => {
      try { if (window.Plotly) Plotly.Plots.resize(graphEl); } catch {}
      try { measureAndResize(); } catch {}
    });
  }

  function close(){
    modal.classList.remove('is-open');
    document.body.classList.remove('plot-open');
    setBackgroundInert(false);

    // Restore plot to original location
    if (originalParent){
      if (originalNext && originalNext.parentNode === originalParent) originalParent.insertBefore(graphEl, originalNext);
      else originalParent.appendChild(graphEl);
    }

    requestAnimationFrame(() => {
      try { if (window.Plotly) Plotly.Plots.resize(graphEl); } catch {}
      try { measureAndResize(); } catch {}
    });

    if (lastFocusEl && lastFocusEl.focus) lastFocusEl.focus();
    lastFocusEl = null;
  }

  btn.addEventListener('click', (e) => { e.preventDefault(); open(); });
  if (closeBtn) closeBtn.addEventListener('click', (e) => { e.preventDefault(); close(); });
  modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
  window.addEventListener('keydown', (e) => { if (e.key === 'Escape' && modal.classList.contains('is-open')) close(); });
 
}

function bindImageZoomOnce() {
  if (window.__imageZoomInit) return;
  window.__imageZoomInit = true;

  let modal = document.getElementById('image-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'image-modal';
    modal.className = 'image-modal';
    modal.setAttribute('aria-hidden', 'true');
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.innerHTML = `
      <div class="image-modal__inner">
        <button id="image-modal-close" class="btn image-modal__close" type="button" aria-label="Close image viewer">Close</button>
        <img id="image-modal-img" class="image-modal__img" alt="Expanded product image" />
      </div>
    `;
    document.body.appendChild(modal);
  }

  const modalImg = document.getElementById('image-modal-img');
  const closeBtn = document.getElementById('image-modal-close');
  if (!modalImg || !closeBtn) return;

  let lastFocusEl = null;

  const isImageReady = (img) => {
    if (!img || !img.src) return false;
    if (img.style.display === 'none') return false;
    return true;
  };

  const openFromImage = (img, focusEl = img) => {
    if (!isImageReady(img)) return;
    lastFocusEl = focusEl || document.activeElement;
    modalImg.src = img.currentSrc || img.src;
    modalImg.alt = img.alt || 'Expanded product image';
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('image-modal-open');
    closeBtn.focus();
  };

  const close = () => {
    if (!modal.classList.contains('is-open')) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('image-modal-open');
    if (lastFocusEl && lastFocusEl.focus) lastFocusEl.focus();
    lastFocusEl = null;
  };

  const triggerImg = document.getElementById('product-image');
  if (triggerImg) {
    triggerImg.setAttribute('role', 'button');
    triggerImg.setAttribute('tabindex', '0');
    triggerImg.setAttribute('aria-haspopup', 'dialog');
    triggerImg.setAttribute('aria-controls', 'image-modal');

    triggerImg.addEventListener('click', (e) => {
      e.preventDefault();
      openFromImage(triggerImg);
    });
    triggerImg.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openFromImage(triggerImg);
      }
    });
  }

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-image-zoom]');
    if (!trigger) return;
    const img = trigger.matches('img') ? trigger : trigger.querySelector('img');
    if (!img) return;
    e.preventDefault();
    e.stopPropagation();
    openFromImage(img, trigger);
  });

  closeBtn.addEventListener('click', (e) => {
    e.preventDefault();
    close();
  });
  modal.addEventListener('click', (e) => {
    if (e.target === modal) close();
  });
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('is-open')) close();
  });
}

// Expand/collapse variants when clicking a retailer row (register once; delegated)
(function initRetailerVariantTogglesOnce() {
    if (window.__retailerVariantTogglesInit) return;
    window.__retailerVariantTogglesInit = true;

    const tbody = document.querySelector('#retailer-table tbody');
    if (!tbody) return;

    function findVariantRow(retRow) {
        const retailer = retRow.getAttribute('data-retailer');
        const next = retRow.nextElementSibling;
        if (!next) return null;
        if (!next.classList.contains('var-row')) return null;
        if (next.getAttribute('data-retailer') !== retailer) return null;
        return next;
    }

    function closeAllExcept(exceptRetailer) {
        tbody.querySelectorAll('tr.var-row').forEach(vr => {
            const r = vr.getAttribute('data-retailer');
            if (!exceptRetailer || r !== exceptRetailer) {
                vr.style.display = 'none';
                const rr = vr.previousElementSibling;
                if (rr && rr.classList.contains('ret-row')) rr.setAttribute('aria-expanded', 'false');
            }
        });
    }

    tbody.addEventListener('click', (e) => {
        if (e.target.closest('a, button')) return;
        const retRow = e.target.closest('tr.ret-row');
        if (!retRow) return;
        const variantRow = findVariantRow(retRow);
        if (!variantRow) return;

        const retailer = retRow.getAttribute('data-retailer');
        const isOpen = variantRow.style.display !== 'none';
        closeAllExcept(retailer);
        variantRow.style.display = isOpen ? 'none' : '';
        retRow.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    });
})();

(function initRetailerHighlightInteractionsOnce() {
    if (window.__retailerHighlightInit) return;
    window.__retailerHighlightInit = true;

    const selector = '[data-retailer-highlight]';
    const getTarget = (target) => target?.closest?.(selector) || null;
    const getRetailer = (el) => String(el?.getAttribute?.('data-retailer-highlight') || '');

    document.addEventListener('mouseover', (e) => {
        const target = getTarget(e.target);
        if (!target) return;
        const from = getTarget(e.relatedTarget);
        if (from === target) return;
        setPreviewRetailerHighlight(getRetailer(target));
    });

    document.addEventListener('mouseout', (e) => {
        const target = getTarget(e.target);
        if (!target) return;
        const to = getTarget(e.relatedTarget);
        if (to === target) return;
        setPreviewRetailerHighlight('');
    });

    document.addEventListener('focusin', (e) => {
        const target = getTarget(e.target);
        if (target) setPreviewRetailerHighlight(getRetailer(target));
    });

    document.addEventListener('focusout', (e) => {
        const target = getTarget(e.target);
        if (!target) return;
        const to = getTarget(e.relatedTarget);
        if (to === target) return;
        setPreviewRetailerHighlight('');
    });

    document.addEventListener('click', (e) => {
        const row = e.target.closest?.('tr.ret-row[data-retailer-highlight]');
        if (!row) return;
        toggleLockedRetailerHighlight(getRetailer(row));
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && (lockedRetailerHighlight || previewRetailerHighlight)) {
            clearRetailerHighlight();
            return;
        }
        const row = e.target.closest?.('tr.ret-row[data-retailer-highlight]');
        if (!row || (e.key !== 'Enter' && e.key !== ' ')) return;
        e.preventDefault();
        row.click();
    });
})();

// Clicking Top 5 product name selects the product
document.addEventListener('click', (e) => {
    const link = e.target.closest('a.top5-product');
    if (!link) return;
    e.preventDefault();
    const name = link.getAttribute('data-name');
    if (name && products && products.includes(name)) {
        if (!hasDetailSurface()) {
            window.location.href = buildProductPageUrl(name);
            return;
        }
        setProduct(name);
        const details = document.getElementById('retailer-details');
        if (details) details.open = true;
        if (details && details.scrollIntoView) {
            details.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }
});

// ---- Custom dropdown ----
function populateProductControls() {
    const sel = document.getElementById('product-select');
    const input = document.getElementById('product-search');
    const menu = document.getElementById('product-menu');
    if (!sel || !input || !menu) return;
    const getEffectiveFilterQuery = () => {
        return String(input?.value || '').trim();
    };
    let activeIndex = -1;
    let lastItems = [];
    let renderTimer = null;
    let refreshQueryViews = (query = getEffectiveFilterQuery()) => {
      const q = String(query || '').trim();
      renderMenu(q);
      const shelf = getDealShelfRows(catalogueSearchFilter);
      renderWhiskyShelf(shelf.rows, shelf);
      persistBrowseState({ searchQuery: catalogueSearchFilter });
    };
    const qRefresh = () => refreshQueryViews();
    const applyCatalogueSearchFilter = (query, { close = true } = {}) => {
        const next = String(query || '').trim();
        input.value = next;
        catalogueSearchFilter = next;
        DEAL_VIEW.page = 1;
        activeIndex = -1;
        if (typeof window.__applyAndRefreshFilters === 'function') {
          window.__applyAndRefreshFilters();
        } else {
          refreshQueryViews(next);
        }
        if (close) closeMenu();
    };
    const selectSearchResult = (name, { close = true } = {}) => {
        const next = String(name || '').trim();
        if (!next) return;
        applyCatalogueSearchFilter(next, { close });
    };
    window.__selectProductFromSearch = (name) => {
        selectSearchResult(name);
    };
    window.__applyCatalogueSearchFilter = (query) => applyCatalogueSearchFilter(query);

    function highlight(label, q) {
        if (!q) return label;
        const lo = (label || '').toLowerCase();
        const qq = (q || '').toLowerCase();
        const i = lo.indexOf(qq);
        if (i < 0) return label;
        return label.slice(0, i) + '<mark>' + label.slice(i, i + qq.length) + '</mark>' + label.slice(i + qq.length);
    }

    function renderMenu(filterText = '') {
        const q = (filterText || '').trim();
        lastItems = q
          ? searchProducts(q, { applyFilters: false, resultLimit: 60 })
          : searchProducts('', { applyFilters: false }).slice(0, 60);
        if (activeIndex >= lastItems.length) activeIndex = lastItems.length - 1;
        if (activeIndex < 0 && lastItems.length) activeIndex = 0;
        menu.innerHTML = lastItems.map((p, i) => {
            const cls = (i === activeIndex) ? 'item active' : 'item';
            return `<div class="${cls}" role="option" data-name="${p}">${highlight(p, q)}</div>`;
        }).join('') || `<div class="item" role="option" data-name="">No matches</div>`;
    }

    sel.innerHTML = '';
    const frag = document.createDocumentFragment();
    (products || []).forEach(p => {
        const opt = document.createElement('option'); opt.value = p; opt.textContent = p; frag.appendChild(opt);
    });
    sel.appendChild(frag);

    // --- Insert filter bar UI once (under the search box) ---
    const combo = document.getElementById('product-combo');
    if (combo && !document.getElementById('filterbar')) {
      const controlsHost = combo.closest('.selector-controls') || combo.parentElement;
      const facets = getFacets();
      const facetField = (key) => {
        const facet = facets[key] || { values: [], hasBlank: false };
        const values = Array.isArray(facet.values) ? facet.values : [];
        const includeBlank = !!facet.hasBlank;
        const label = FACET_LABELS[key];
        const blankLabel = FACET_EMPTY_LABELS[key];
        const searchEnabled = values.length > 10 || key === "brand" || key === "cask" || key === "wood" || key === "retailer";
        const searchPlaceholder = `Filter ${String(label || key).toLowerCase()}...`;
        return `
        <div class="filterfield filtermulti" data-filter="${key}">
          <span class="filterfield__label">${label}</span>
          <button type="button" class="filterctl filtermulti__trigger" data-filter-trigger="${key}">${label} (all)</button>
          <div class="filtermulti__panel" data-filter-panel="${key}">
            ${searchEnabled ? `
              <div class="filtermulti__search">
                <input
                  type="search"
                  class="filterctl filtermulti__search-input"
                  data-filter-search="${key}"
                  placeholder="${escHtml(searchPlaceholder)}"
                  autocomplete="off"
                  spellcheck="false"
                />
              </div>
            ` : ""}
            <div class="filtermulti__controls">
              <button type="button" class="filtermini" data-filter-selectall="${key}">All</button>
              <button type="button" class="filtermini" data-filter-clearset="${key}">None</button>
            </div>
            <div class="filtermulti__options">
              ${includeBlank ? `
                <label class="filteropt">
                  <input type="checkbox" value="${BLANK_FILTER_VALUE}" data-filter-checkbox="${key}" />
                  <span>${blankLabel}</span>
                </label>
              ` : ''}
              ${values.map(v => `
                <label class="filteropt">
                  <input type="checkbox" value="${escHtml(v)}" data-filter-checkbox="${key}" />
                  <span>${escHtml(formatFacetValueLabel(key, v))}</span>
                </label>
              `).join('')}
            </div>
            ${searchEnabled ? `<div class="filtermulti__empty" data-filter-search-empty="${key}" hidden>No matching options.</div>` : ""}
          </div>
        </div>
      `;
      };
      const filterGroup = (group) => {
        const fields = group.keys.map((key) => facetField(key)).join('');
        const extras = group.key === "shopping" ? `
          <div class="filterfield filterfield--priceblock">
            <span class="filterfield__label">Price range</span>
            <div class="filterrange">
              <input id="filter-price-min" class="filterctl" type="number" inputmode="numeric" placeholder="Min price (R)" />
              <input id="filter-price-max" class="filterctl" type="number" inputmode="numeric" placeholder="Max price (R)" />
            </div>
          </div>
        ` : '';
        return `
          <details class="filtergroup" open>
            <summary class="filtergroup__summary">
              <span class="filtergroup__title">${group.title}</span>
              <span class="filtergroup__desc muted">${group.description}</span>
            </summary>
            <div class="filtergroup__body filtergroup__body--${group.key}">
              ${fields}
              ${extras}
            </div>
          </details>
        `;
      };
      const wrap = document.createElement('div');
      wrap.id = 'filterbar';
      wrap.className = 'filterbar';
      wrap.innerHTML = `
        <div class="filterbar__toolbar">
          <button id="filter-toggle" class="filterctl filterctl--toggle filterctl--filters" type="button" aria-expanded="false" aria-controls="filter-drawer">
            <span>Filters</span>
            <span id="filter-active-count" class="filtertoggle__count" hidden>0</span>
          </button>
          <label class="filterfield filterfield--inline filterfield--action filterfield--clear-desktop">
            <span class="filterfield__label filterfield__label--ghost">Actions</span>
            <button id="filter-clear" class="btn-link filterbtn" type="button">Clear</button>
          </label>
        </div>
        <div id="filter-chips" class="filterchips hidden"></div>
        <div id="filter-drawer" class="filterdrawer hidden">
          <div class="filterdrawer__grid">
            ${FACET_GROUPS.map((group) => filterGroup(group)).join("")}
          </div>
          <div class="filterdrawer__actions">
            <button id="filter-clear-mobile" class="btn-link filterbtn filterbtn--drawer" type="button">Clear filters</button>
          </div>
        </div>
      `;
      controlsHost.appendChild(wrap);
      if (!document.getElementById('browse-meta')) {
        const meta = document.createElement('div');
        meta.id = 'browse-meta';
        meta.className = 'muted browse-meta';
        meta.textContent = `${(products || []).length} products`;
        controlsHost.appendChild(meta);
      }
      const browseSummary = document.querySelector('#selector-browse > summary');
      if (browseSummary) browseSummary.textContent = 'Browse matching deals';
      const browseSection = document.getElementById('selector-browse');
      const browseHost = browseSection?.parentElement || document.getElementById('browse-dock') || document.getElementById('selector-dock');
      const createPager = (id, suffix = "") => {
        const pager = document.createElement('div');
        pager.id = id;
        pager.className = `deal-pager${suffix ? ' deal-pager--bottom' : ''} hidden`;
        pager.innerHTML = `
          <button id="deal-page-prev${suffix}" class="btn-link deal-pager__btn" type="button" data-deal-page-action="prev">Previous</button>
          <span id="deal-page-info${suffix}" class="deal-pager__info muted" data-deal-page-info>Page 1 of 1</span>
          <label class="deal-pager__pick" for="deal-page-select${suffix}">
            <span class="muted">Go to page</span>
            <select id="deal-page-select${suffix}" class="filterctl" data-deal-page-select></select>
          </label>
          <button id="deal-page-next${suffix}" class="btn-link deal-pager__btn" type="button" data-deal-page-action="next">Next</button>
        `;
        return pager;
      };
      if (!document.getElementById('deal-pager') && browseHost) {
        const pager = createPager('deal-pager');
        if (browseSection) {
          browseHost.insertBefore(pager, browseSection);
        } else {
          browseHost.appendChild(pager);
        }
      }
      if (!document.getElementById('deal-pager-bottom') && browseSection) {
        browseSection.insertAdjacentElement('afterend', createPager('deal-pager-bottom', '-bottom'));
      }

      const drawer = wrap.querySelector('#filter-drawer');
      const savedBrowseState = shouldRestoreBrowseState() ? readBrowseState() : null;
      const filterGroups = Array.from(wrap.querySelectorAll('.filtergroup'));
      const toggleBtn = wrap.querySelector('#filter-toggle');
      const activeCountEl = wrap.querySelector('#filter-active-count');
      const orderSelect = document.getElementById('discovery-order');
      const facetSetFromChecks = (key) => {
        const checks = wrap.querySelectorAll(`input[data-filter-checkbox="${key}"]:checked`);
        return new Set(Array.from(checks).map((c) => c.value));
      };
      const countActiveFilters = () => {
        let count = 0;
        for (const key of FACET_KEYS) count += facetSetFromChecks(key).size;
        if (document.getElementById('filter-price-min')?.value) count += 1;
        if (document.getElementById('filter-price-max')?.value) count += 1;
        if (catalogueSearchFilter) count += 1;
        if (hasActiveTasteSearch()) count += 1;
        return count;
      };
      const applyFacetSearch = (key, rawQuery = '') => {
        const panel = wrap.querySelector(`[data-filter-panel="${key}"]`);
        if (!panel) return;
        const query = norm(String(rawQuery || '').trim());
        const options = Array.from(panel.querySelectorAll(`input[data-filter-checkbox="${key}"]`))
          .map((input) => input.closest('.filteropt'))
          .filter(Boolean);
        let visibleCount = 0;
        for (const option of options) {
          const input = option.querySelector(`input[data-filter-checkbox="${key}"]`);
          const label = cleanFacetValue(option.querySelector('span')?.textContent);
          const checked = !!input?.checked;
          const matches = !query || checked || norm(label).includes(query);
          option.classList.toggle('is-hidden', !matches);
          if (matches) visibleCount += 1;
        }
        const empty = panel.querySelector(`[data-filter-search-empty="${key}"]`);
        if (empty) empty.hidden = visibleCount > 0;
      };
      const resetFacetSearches = () => {
        wrap.querySelectorAll('[data-filter-search]').forEach((input) => {
          const key = input.getAttribute('data-filter-search');
          input.value = '';
          if (key) applyFacetSearch(key, '');
        });
      };
      const closeFacetPanels = () => {
        resetFacetSearches();
        wrap.querySelectorAll('.filtermulti.is-open').forEach((el) => el.classList.remove('is-open'));
      };
      const setDrawerOpen = (open) => {
        if (!drawer || !toggleBtn) return;
        drawer.classList.toggle('hidden', !open);
        toggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
        toggleBtn.classList.toggle('is-open', open);
        if (!open) closeFacetPanels();
      };
      const syncMobileFilterGroups = () => {
        const isMobile = window.matchMedia('(max-width: 980px)').matches;
        filterGroups.forEach((group, idx) => {
          if (isMobile) {
            if (!group.dataset.mobileStateInit) {
              group.open = idx === 0;
              group.dataset.mobileStateInit = 'true';
            }
            return;
          }
          group.open = true;
        });
      };
      const updateOrderControl = () => {
        syncDiscoveryOrderControl();
      };
      const updateToolbarState = () => {
        const activeCount = countActiveFilters();
        if (activeCountEl) {
          activeCountEl.hidden = activeCount <= 0;
          activeCountEl.textContent = String(activeCount);
        }
        if (toggleBtn) toggleBtn.classList.toggle('has-filters', activeCount > 0);
        updateOrderControl();
      };
      const updateFacetTriggerLabels = () => {
        for (const key of FACET_KEYS) {
          const btn = wrap.querySelector(`[data-filter-trigger="${key}"]`);
          if (!btn) continue;
          const count = facetSetFromChecks(key).size;
          btn.textContent = count ? `${FACET_LABELS[key]} (${count})` : `${FACET_LABELS[key]} (all)`;
        }
      };
      const renderFilterChips = () => {
        const chipHost = document.getElementById('filter-chips');
        if (!chipHost) return;
        const chips = [];
        for (const key of FACET_KEYS) {
          const set = facetSetFromChecks(key);
          for (const val of set) {
            const label = formatFacetValueLabel(key, val);
            chips.push(
              `<button type="button" class="filterchip" data-chip-type="facet" data-chip-key="${key}" data-chip-val="${escHtml(val)}">${FACET_LABELS[key]}: ${escHtml(label)} <span aria-hidden="true">×</span></button>`
            );
          }
        }
        const minRaw = document.getElementById('filter-price-min')?.value;
        const maxRaw = document.getElementById('filter-price-max')?.value;
        if (minRaw !== '' && minRaw != null) {
          chips.push(`<button type="button" class="filterchip" data-chip-type="price" data-chip-key="priceMin">Min: R${escHtml(minRaw)} <span aria-hidden="true">×</span></button>`);
        }
        if (maxRaw !== '' && maxRaw != null) {
          chips.push(`<button type="button" class="filterchip" data-chip-type="price" data-chip-key="priceMax">Max: R${escHtml(maxRaw)} <span aria-hidden="true">×</span></button>`);
        }
        chipHost.innerHTML = chips.join('');
        chipHost.classList.toggle('hidden', chips.length === 0);
        updateToolbarState();
        syncDiscoveryShortcutStates();
      };
      const applyAndRefresh = ({ resetPage = true } = {}) => {
        for (const key of FACET_KEYS) {
          FILTERS[key] = facetSetFromChecks(key);
        }
        const minRaw = document.getElementById('filter-price-min')?.value;
        const maxRaw = document.getElementById('filter-price-max')?.value;
        FILTERS.priceMin = (minRaw !== '' && minRaw != null) ? Number(minRaw) : null;
        FILTERS.priceMax = (maxRaw !== '' && maxRaw != null) ? Number(maxRaw) : null;
        updateFacetTriggerLabels();
        renderFilterChips();
        if (resetPage) DEAL_VIEW.page = 1;

        // Rebuild browse menu + shelf list using same filtered pool
        qRefresh();
      };
      window.__applyAndRefreshFilters = applyAndRefresh;
      window.__refreshTasteReferenceBrowse = () => {
        renderTasteReferenceUi();
        updateToolbarState();
        qRefresh();
      };

      wrap.addEventListener('click', (e) => {
        const chip = e.target.closest('.filterchip');
        if (chip) {
          const type = chip.getAttribute('data-chip-type');
          const key = chip.getAttribute('data-chip-key');
          if (type === 'facet' && key) {
            const val = chip.getAttribute('data-chip-val');
            wrap.querySelectorAll(`input[data-filter-checkbox="${key}"]`).forEach((c) => {
              if (String(c.value) === String(val)) c.checked = false;
            });
            applyAndRefresh();
          } else if (type === 'price' && key) {
            if (key === 'priceMin') document.getElementById('filter-price-min').value = '';
            if (key === 'priceMax') document.getElementById('filter-price-max').value = '';
            applyAndRefresh();
          }
          return;
        }

        const drawerToggle = e.target.closest('#filter-toggle');
        if (drawerToggle) {
          const isOpen = !drawer.classList.contains('hidden');
          setDrawerOpen(!isOpen);
          return;
        }

        const trigger = e.target.closest('[data-filter-trigger]');
        if (trigger) {
          const key = trigger.getAttribute('data-filter-trigger');
          const parent = wrap.querySelector(`.filtermulti[data-filter="${key}"]`);
          if (!parent) return;
          const open = parent.classList.contains('is-open');
          closeFacetPanels();
          if (!open) {
            parent.classList.add('is-open');
            const searchInput = parent.querySelector(`[data-filter-search="${key}"]`);
            if (searchInput) {
              requestAnimationFrame(() => searchInput.focus());
            }
          }
          return;
        }

        const allBtn = e.target.closest('[data-filter-selectall]');
        if (allBtn) {
          const key = allBtn.getAttribute('data-filter-selectall');
          wrap.querySelectorAll(`input[data-filter-checkbox="${key}"]`).forEach((c) => { c.checked = true; });
          applyAndRefresh();
          return;
        }

        const noneBtn = e.target.closest('[data-filter-clearset]');
        if (noneBtn) {
          const key = noneBtn.getAttribute('data-filter-clearset');
          wrap.querySelectorAll(`input[data-filter-checkbox="${key}"]`).forEach((c) => { c.checked = false; });
          applyAndRefresh();
        }
      });

      wrap.addEventListener('change', (e) => {
        if (e.target?.matches?.('input[data-filter-checkbox]')) {
          applyAndRefresh();
          return;
        }
      });
      wrap.addEventListener('input', (e) => {
        if (e.target?.matches?.('[data-filter-search]')) {
          const key = e.target.getAttribute('data-filter-search');
          if (key) applyFacetSearch(key, e.target.value);
          return;
        }
        if (e.target && (e.target.id === 'filter-price-min' || e.target.id === 'filter-price-max')) {
          // don't thrash on every keystroke; small debounce
          if (window.__priceFilterTimer) clearTimeout(window.__priceFilterTimer);
          window.__priceFilterTimer = setTimeout(applyAndRefresh, 120);
        }
      });

      document.getElementById('filter-clear')?.addEventListener('click', () => {
        wrap.querySelectorAll('input[data-filter-checkbox]').forEach((c) => { c.checked = false; });
        document.getElementById('filter-price-min').value = '';
        document.getElementById('filter-price-max').value = '';
        catalogueSearchFilter = '';
        input.value = '';
        for (const key of FACET_KEYS) FILTERS[key] = new Set();
        FILTERS.priceMin = FILTERS.priceMax = null;
        closeFacetPanels();
        applyAndRefresh();
      });
      document.getElementById('filter-clear-mobile')?.addEventListener('click', () => {
        document.getElementById('filter-clear')?.click();
      });

      document.addEventListener('click', (e) => {
        if (!wrap.contains(e.target)) {
          closeFacetPanels();
        }
      });

      const applySortChange = (rawValue) => {
        const next = parseDealOrderValue(rawValue);
        if (!next || (isProfileMatchSort(next.sortBy) && !hasActiveTasteSearch())) return;
        DEAL_VIEW.sortBy = next.sortBy;
        DEAL_VIEW.sortDir = next.sortDir;
        if (!hasActiveTasteSearch()) syncDiscoveryDropStateForSort(next.sortBy);
        DEAL_VIEW.page = 1;
        persistDealViewPrefs();
        renderDiscoveryModeUi();
        updateToolbarState();
        qRefresh();
      };
      orderSelect?.addEventListener('change', (e) => applySortChange(e.target?.value));
      document.querySelectorAll('[data-deal-page-action]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const action = btn.getAttribute('data-deal-page-action');
          const pager = btn.closest('.deal-pager');
          const max = Number(pager?.dataset?.totalPages || 1);
          const current = Number(DEAL_VIEW.page || 1);
          DEAL_VIEW.page = action === 'next' ? Math.min(max, current + 1) : Math.max(1, current - 1);
          qRefresh();
        });
      });
      document.querySelectorAll('[data-deal-page-select]').forEach((select) => {
        select.addEventListener('change', (e) => {
          const pager = select.closest('.deal-pager');
          const max = Number(pager?.dataset?.totalPages || 1);
          const next = Number(e.target?.value);
          if (!Number.isFinite(next)) return;
          DEAL_VIEW.page = Math.max(1, Math.min(max, Math.floor(next)));
          qRefresh();
        });
      });
      syncMobileFilterGroups();
      const mobileFilterMq = window.matchMedia('(max-width: 980px)');
      const handleMobileFilterLayoutChange = () => syncMobileFilterGroups();
      if (typeof mobileFilterMq.addEventListener === 'function') {
        mobileFilterMq.addEventListener('change', handleMobileFilterLayoutChange);
      } else if (typeof mobileFilterMq.addListener === 'function') {
        mobileFilterMq.addListener(handleMobileFilterLayoutChange);
      }
      updateToolbarState();
      const restored = applyBrowseStateToControls(savedBrowseState, { wrap, input });
      catalogueSearchFilter = restored ? getEffectiveFilterQuery() : '';
      renderTasteReferenceUi();
      applyAndRefresh({ resetPage: !restored });
      if (shouldRestoreBrowseState()) {
        restoreBrowseScroll(savedBrowseState?.scrollY);
        clearBrowseRestoreFlag();
      }
    }

    bindTasteReferencePickerOnce();

    function openMenu() { menu.classList.remove('hidden'); }
    function closeMenu() { menu.classList.add('hidden'); }
    function toggleMenu() { menu.classList.toggle('hidden'); }

    document.getElementById('product-toggle')?.addEventListener('click', (e) => { e.preventDefault(); toggleMenu(); });
    input.addEventListener('focus', openMenu);
    input.addEventListener('input', (e) => {
        const v = e.target.value;
        activeIndex = -1;
        if (renderTimer) clearTimeout(renderTimer);
        renderTimer = setTimeout(() => renderMenu(v), 60);
    });

    menu.addEventListener('click', (e) => {
        const item = e.target.closest('.item');
        if (!item) return;
        const name = item.getAttribute('data-name');
        if (!name) return;
        selectSearchResult(name);
    });

    input.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!lastItems.length) return;
            activeIndex = Math.min(lastItems.length - 1, activeIndex + 1);
            renderMenu(input.value);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (!lastItems.length) return;
            activeIndex = Math.max(0, activeIndex - 1);
            renderMenu(input.value);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            applyCatalogueSearchFilter(getEffectiveFilterQuery());
        } else if (e.key === 'Escape') {
            closeMenu();
        }
    });

    document.addEventListener('click', (e) => {
        const combo = document.getElementById('product-combo');
        if (!combo) return;
        if (!combo.contains(e.target)) closeMenu();
    });

    sel.addEventListener('change', (e) => {
        selectSearchResult(e.target.value, { close: false });
    });

    if (currentProduct && products.includes(currentProduct)) {
        sel.value = currentProduct;
    }
}

function setTheme(mode) {
  const normalized = normalizeThemeMode(mode);
  document.documentElement.setAttribute('data-theme', normalized);
  applyThemeShell(normalized);
  applyThemeToPlot(normalized);
}

function applyDiscoverySearch(query) {
  const input = document.getElementById("product-search");
  if (!input) return;
  if (typeof window.__applyCatalogueSearchFilter === "function") {
    window.__applyCatalogueSearchFilter(query);
  } else {
    input.value = String(query || "");
    input.dispatchEvent(new Event("input", { bubbles: true }));
  }
  try {
    input.focus({ preventScroll: true });
  } catch {
    input.focus();
  }
}

function applyDiscoveryFacet(key, value) {
  const filterbar = document.getElementById("filterbar");
  if (!filterbar || !key) {
    applyDiscoverySearch(value);
    return false;
  }
  const targets = getDiscoveryFacetShortcutInputs(key, value, filterbar);
  if (!targets.length) {
    applyDiscoverySearch(value);
    return false;
  }
  const shouldSelect = !targets.every((input) => input.checked);
  targets.forEach((input) => {
    input.checked = shouldSelect;
  });
  window.__applyAndRefreshFilters?.();
  return true;
}

function applyDiscoveryPriceRange(minValue, maxValue) {
  const minInput = document.getElementById("filter-price-min");
  const maxInput = document.getElementById("filter-price-max");
  if (minInput) minInput.value = minValue == null ? "" : String(minValue);
  if (maxInput) maxInput.value = maxValue == null ? "" : String(maxValue);
  window.__applyAndRefreshFilters?.();
}

function bindDiscoveryActionsOnce() {
  if (window.__discoveryActionsInit) return;
  window.__discoveryActionsInit = true;

  document.addEventListener("mouseover", (e) => {
    const guideTarget = e.target.closest("[data-flavour-guide-axis], [data-flavour-axis]");
    const axis = guideTarget?.getAttribute("data-flavour-guide-axis") || guideTarget?.getAttribute("data-flavour-axis");
    if (!axis || axis === discoveryTasteGuideAxis || !TASTE_AXES.some((item) => item.key === axis)) return;
    discoveryTasteGuideAxis = axis;
    renderFlavourCompass();
  });

  document.addEventListener("keydown", (e) => {
    const target = e.target.closest?.("[data-flavour-axis][role=button]");
    if (!target || (e.key !== "Enter" && e.key !== " ")) return;
    e.preventDefault();
    setDiscoveryTasteAxis(target.getAttribute("data-flavour-axis"));
  });

  const updateFlavourSlider = (slider, rawLevel) => {
    const level = Math.max(0, Math.min(1, Math.round((Number(rawLevel) || 0) * 20) / 20));
    setDiscoveryTasteLevel(slider.getAttribute("data-flavour-level"), level, { refresh: false });
    const output = slider.closest(".flavour-intensity-control")?.querySelector("output");
    const intensity = formatTasteIntensity(level);
    const value = Math.round(level * 100);
    slider.dataset.flavourValue = String(level);
    slider.setAttribute("aria-valuenow", String(value));
    slider.setAttribute("aria-valuetext", intensity);
    if (output) output.textContent = intensity;
    const fill = slider.querySelector(".flavour-intensity-slider__fill");
    const thumb = slider.querySelector(".flavour-intensity-slider__thumb");
    if (fill) fill.style.width = `${value}%`;
    if (thumb) thumb.style.left = `${value}%`;
    const wheelHost = document.getElementById("flavour-target-wheel-host");
    if (wheelHost) wheelHost.innerHTML = renderDiscoveryTasteTargetWheel();
  };

  const commitFlavourSlider = (target) => {
    const slider = target?.closest?.("[data-flavour-level]");
    if (!slider) return;
    setDiscoveryTasteLevel(slider.getAttribute("data-flavour-level"), Number(slider.dataset.flavourValue) || 0);
  };

  const updateFlavourSliderFromPointer = (slider, clientX) => {
    const rect = slider.getBoundingClientRect();
    if (!rect.width) return;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    updateFlavourSlider(slider, ratio);
  };

  document.addEventListener("pointerdown", (e) => {
    const slider = e.target.closest?.("[data-flavour-level]");
    if (!slider) return;
    activeFlavourSlider = { slider, pointerId: e.pointerId };
    slider.setPointerCapture?.(e.pointerId);
    updateFlavourSliderFromPointer(slider, e.clientX);
    e.preventDefault();
  });

  document.addEventListener("pointermove", (e) => {
    if (!activeFlavourSlider || e.pointerId !== activeFlavourSlider.pointerId) return;
    updateFlavourSliderFromPointer(activeFlavourSlider.slider, e.clientX);
    e.preventDefault();
  }, { passive: false });

  document.addEventListener("pointerup", (e) => {
    const active = activeFlavourSlider;
    if (active && e.pointerId === active.pointerId) {
      updateFlavourSliderFromPointer(active.slider, e.clientX);
      active.slider.releasePointerCapture?.(e.pointerId);
      activeFlavourSlider = null;
      commitFlavourSlider(active.slider);
      return;
    }
    commitFlavourSlider(e.target);
  });

  document.addEventListener("pointercancel", () => {
    activeFlavourSlider = null;
  });

  document.addEventListener("keyup", (e) => {
    if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"].includes(e.key)) {
      commitFlavourSlider(e.target);
    }
  });

  document.addEventListener("keydown", (e) => {
    const slider = e.target.closest?.("[data-flavour-level]");
    if (!slider) return;
    const level = Number(slider.dataset.flavourValue) || 0;
    const movement = { ArrowLeft: -0.05, ArrowDown: -0.05, ArrowRight: 0.05, ArrowUp: 0.05 };
    if (Object.hasOwn(movement, e.key)) {
      e.preventDefault();
      updateFlavourSlider(slider, level + movement[e.key]);
    } else if (e.key === "Home") {
      e.preventDefault();
      updateFlavourSlider(slider, 0);
    } else if (e.key === "End") {
      e.preventDefault();
      updateFlavourSlider(slider, 1);
    }
  });

  document.addEventListener("click", (e) => {
    const modeButton = e.target.closest("[data-discovery-mode]");
    if (modeButton) {
      setDiscoveryMode(modeButton.getAttribute("data-discovery-mode"));
      return;
    }

    const flavourGuide = e.target.closest("[data-flavour-guide-axis]");
    if (flavourGuide) {
      const axis = flavourGuide.getAttribute("data-flavour-guide-axis");
      if (TASTE_AXES.some((item) => item.key === axis)) {
        discoveryTasteGuideAxis = axis;
        renderFlavourCompass();
      }
      return;
    }

    const flavourAxis = e.target.closest("[data-flavour-axis]");
    if (flavourAxis) {
      setDiscoveryTasteAxis(flavourAxis.getAttribute("data-flavour-axis"));
      return;
    }

    const sectionToggle = e.target.closest("[data-discovery-section-toggle]");
    if (sectionToggle) {
      const panelId = sectionToggle.getAttribute("aria-controls");
      const panel = panelId ? document.getElementById(panelId) : null;
      const shouldOpen = sectionToggle.getAttribute("aria-expanded") !== "true";
      sectionToggle.setAttribute("aria-expanded", shouldOpen ? "true" : "false");
      sectionToggle.closest(".discovery-step--toggle")?.classList.toggle("is-open", shouldOpen);
      if (panel) panel.hidden = !shouldOpen;
      return;
    }

    const queryChip = e.target.closest("[data-discovery-query]");
    if (queryChip) {
      applyDiscoverySearch(queryChip.getAttribute("data-discovery-query") || "");
      return;
    }

    const facetChip = e.target.closest("[data-discovery-facet][data-discovery-value]");
    if (facetChip) {
      applyDiscoveryFacet(
        facetChip.getAttribute("data-discovery-facet"),
        facetChip.getAttribute("data-discovery-value")
      );
      return;
    }

    const priceChip = e.target.closest("[data-discovery-price-min], [data-discovery-price-max]");
    if (priceChip) {
      const minRaw = priceChip.getAttribute("data-discovery-price-min");
      const maxRaw = priceChip.getAttribute("data-discovery-price-max");
      const minValue = minRaw === null ? null : Number(minRaw);
      const maxValue = maxRaw === null ? null : Number(maxRaw);
      const alreadyActive = discoveryPriceShortcutIsActive(minValue, maxValue);
      applyDiscoveryPriceRange(alreadyActive ? null : minValue, alreadyActive ? null : maxValue);
      return;
    }

    const reset = e.target.closest("#discovery-reset-all");
    if (reset) {
      const input = document.getElementById("product-search");
      catalogueSearchFilter = "";
      if (input) input.value = "";
      clearDiscoveryTasteProfile();
      discoveryTasteGuideAxis = "fruity";
      discoveryMode = "alltime";
      discoveryDropMetric = "pct";
      setTasteReference("", { syncUrl: true, refresh: false, defaultToMatch: false });
      document.getElementById("filter-clear")?.click();
      DEAL_VIEW.sortBy = "pct";
      DEAL_VIEW.sortDir = getDefaultSortDir("pct");
      renderDiscoveryModeUi();
      if (input) input.dispatchEvent(new Event("input", { bubbles: true }));
      return;
    }

    const findMatches = e.target.closest("#discovery-find-matches, #discovery-value-view-all");
    if (findMatches) {
      const target = document.getElementById("browse-dock");
      if (target) smoothScrollTo(target, { offset: -getLandingHeaderOffset(16), duration: 0.7 });
    }
  });
}

function bindUiOnce() {
  // Density toggle (delegated)
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('#density-toggle');
    if (!btn) return;
    e.preventDefault();
    const tbl = document.getElementById('retailer-table');
    if (!tbl) return;
    tbl.classList.toggle('table-compact');
    updateDensityToggleLabel();
  });

  // Related brand pills in identity panel
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.identity-link--related[data-related-brand]');
    if (!btn) return;
    const brand = btn.getAttribute('data-related-brand');
    if (!brand) return;
    const applied = applyBrandFacetFilter(brand);
    if (!applied) return;

    const names = searchProducts('');
    if (!names.includes(currentProduct) && names.length) {
      setProduct(names[0]);
    }
    const dock = document.getElementById('selector-dock');
    if (dock) smoothScrollTo(dock, { offset: -getJourneyNavCoverOffset(), duration: 0.6 });
  });

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-scroll-target]');
    if (!trigger) return;
    const targetSel = trigger.getAttribute('data-scroll-target');
    if (!targetSel) return;
    const target = document.querySelector(targetSel);
    if (!target) return;
    e.preventDefault();
    if (target.tagName === "DETAILS") target.open = true;
    smoothScrollTo(target, { offset: -getJourneyNavCoverOffset(), duration: 0.65 });
  });

}

async function initApp() {
  beginInitialLoad();
  try {
    await loadBootstrap();
    await ensureSearchReady();
    initializeTasteReferenceFromUrl();
    updateLandingMetrics();
    renderLandingTrend();
    renderLandingIntelligence();
    const requestedProduct = resolveRequestedProductName();
    if (requestedProduct) currentProduct = requestedProduct;
    const legacyDealsSection = document.getElementById('best-deals');
    if (legacyDealsSection) legacyDealsSection.classList.add('story-deals--merged');
    const legacyDealsNavBtn = document.querySelector('#journey-nav button[data-target="#best-deals"]');
    if (legacyDealsNavBtn) legacyDealsNavBtn.remove();

    initLenis();
    initLandingParallax();
    initLandingNav();
    initLandingIntelligenceMotion();
    initScrollRevealSections();
    initScrollChapters();
    initJourneyNav();
    bindDiscoveryActionsOnce();
    bindUiOnce();
    bindPlotExpandOnce();
    bindImageZoomOnce();
    setTheme('dark');
    quietHeaderUtilities();

    renderDiscoveryModeUi();
    renderDiscoveryFlavourSummary();
    populateProductControls();
    initDiscoverySidebarScrollMode();
    initFlavourCompassResponsiveMode();
    updateDensityToggleLabel();

    if (hasDetailSurface() && currentProduct) {
      await setProduct(currentProduct);
    } else if (hasDetailSurface() && products && products.length) {
      await setProduct(products[0]);
    } else {
      syncPageChrome("");
      measureAndResize();
    }
  } finally {
    finishInitialLoad();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp, { once: true });
} else {
  initApp();
}

